import { Injectable } from '@angular/core';
import type {
    ICoverData,
    ICustomerData,
    IEquityMovementScheduleData,
    IEquityMovementScheduleFinancialTableData,
    IEquityMovementSchedulePeriodTable,
    IEquityMovementSchedulePrintPreviewTableRow,
    IFinancialTableData,
    IItem,
    IItemForCalculatingRatioOption,
    INotesDetails,
    INotesMovementScheduleData,
    INotesTableData,
    IOptions,
    IOrganizationData,
    IPdfTronData,
    IProfitCalculationTableData,
    ISectionData,
    ISignatureData,
    ISubsection,
    IText,
} from '@modeso/aumico-lib-annualreport-fe';
import {
    CashFlowStatementPrintPreviewHelper,
    NotesItemsTypes,
    PDFSectionIds,
    RowTypes,
    SectionIds,
} from '@modeso/aumico-lib-annualreport-fe';
import { IBalanceSheetData } from '@modeso/aumico-lib-annualreport-fe/interfaces/pdf-tron/balance-sheet-data.interface';
import {
    AumFinancialNumberPipe,
    DateLocalePipe,
    EMPTY_ARRAY_LENGTH,
    FIRST_ELEMENT_INDEX,
    getCaptionString,
    NumberUtils,
} from '@modeso/aumico-lib-core-fe';
import { IFLIForNotes, IMovementScheduleFliForNotes, NotesEmptyLinesRemovalHelper, effectiveFxCurrencyDisplay, fliFxStampKeyOf } from '@modeso/aumico-lib-notes-fe';
import { isFli } from '@modeso/aumico-lib-templates-fe';
import { ICalculation } from '@modeso/aumico-lib-templates-fe/interfaces/ICalculation.interface';
import { GetOrganizationDto } from '@modeso/aumico-lib-users-fe';
import {
    AccountMappingAccountItem,
    AccountType,
    AnnualReportStep,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    IAopCcr,
    IAopProfitCalculation,
    IAppropriationOfProfit,
    IAppropriationOfProfitEntry,
    ICashFlowFinancingActivities,
    ICashFlowInvestingActivities,
    ICashFlowOperatingActivities,
    ICashFlowStatement,
    ICashFlowTotal,
    IComplementaryReport,
    IFinStatements,
    IManualAddedOrUpdatedAccount,
    IMovementScheduleFli,
    INotes,
    INotesComponent,
    INotesInputComponent,
    IStyleOptions,
    ItemType,
    PrintLocation,
    SortedMappingItem,
    type AccountMappingTemplateGroupItem,
    type IAccountMappingTemplate,
    type IAccountMappingTemplateGroup,
    type IAnnualReport,
    type IDisplayOptions,
    type IEquity,
    type IFinancialLineItemComponent,
    type IFreeTable,
    type IGeneralInformation,
    type IMovementScheduleComponent,
    type INotesSelectComponent,
    type IPrintOptions,
    type ISignatureOptions,
    type ITemplateAndAccountsMapping,
} from '@modeso/types__aumico-lib-annualreport-be';
import type { ICompanyDTO } from '@modeso/types__aumico-lib-companies-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IInputDataType } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/input-type.enum';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import * as countries from 'i18n-iso-countries';

import { EMPTY_STRING } from '@modeso/aumico-lib-settings-fe';
import { IGroupingMasking } from '@modeso/types__aumico-lib-annualreport-be/dist/models/interfaces/IGroupingMasking';
import { ComponentType } from '@modeso/types__aumico-lib-notes-be';
import { cloneDeep } from 'lodash-es';
import { NotesNumberingHelper } from '../../../../../../aumico-lib-notes-fe/src/lib/helpers/notes-numbering.helper';
import { FlisMapper } from './flis.mapper';
import { NotesVisibilityHelper } from '../../../../../../aumico-lib-notes-fe/src/lib/helpers/notesVisibility.helper';
import type { IMappingAndReclassesAccounts } from '../../../interfaces/IMappingAndReclassesAccounts.interface';
import type { IPrintPreviewMappingValue } from '../../../interfaces/IPrintPreviewMappingValue.interface';
import type { IReferenceValueForRatioCalculation } from '../../../interfaces/IReferenceValueForRatioCalculation.interface';
import { IPdfTronMapper } from '../../../interfaces/pdf-tron-mapper.interface';
import { CountriesMapper } from '../../../shared/mappers/countries.mapper';
import { CountriesService } from '../../../shared/services/countries.service';
import { templateConstants } from '../../../shared/template-constants';
import { isAccount, isAccountWithRatio } from '../../../shared/util/accountTypeGuard';
import { localizationKeys } from '../../../utils/localizationKeys';
import { AccountMappingDisplayHelper } from '../helpers/annual-report-account-mapping-group-display-component.helper';
import { AopAdjustmentsAdnReclassesHelper } from '../helpers/aopAdjustmentsAdnReclasses.helper';
import { AttachmentReferencesHelper } from '../helpers/attachmentReferences.helper';
import { CashFlowAdjustmentsAndReclassesHelper } from '../helpers/cashFlowAdjustmentsAndReClasses.helper';
import { EquityMovementScheduleHelper, IEmsPrintPeriodRows } from '../helpers/equity-movement-schedule.helper';
import { NotesAdjustmentsAndReclassesHelper } from '../helpers/notesAdjustmentsAndReClasses.helper';
import { PrintPageHelper } from '../helpers/printPage.helper';
import { ReportSectionsHelper } from '../helpers/report-sections/report-sections-helper';
import { SectionFactoryService } from '../services/pdf-tron-SectionFactoryService.service';
import { FxTranslationHelper, IFxNodeStampValues } from '../utils/FxTranslationHelper';
import { GroupingHelper } from '../utils/GroupingHelper';
import { GroupingPathHelper } from '../utils/GroupingPathHelper';
import { MaskingHelper } from '../utils/MaskingHelper';
import { SplitAccountHelper } from '../utils/SplitAccountHelper';
import { FixedAssetsScheduleGermanMapper } from './fixed-assets-schedule-german-mapper';
import { HtmlContentMapper } from './htmlContent.mapper';
import { AccountProofMapper } from './pdf.accountProof.mapper';
import { PreviousPeriodHelper } from '../helpers/previous-period.helper';
import { NO_VALUE_DIVISOR, PdfTronMapperHelper } from '../helpers/pdf-tron-mapper.helper';
const SEPERATOR = '-';
const LINE_BREAK = '\n';
const ZERO = '0';
const PERCENTAGE = '%';
const SPACE = ' ';
const TWO_DECIMAL_PLACES = 2;
const ZERO_DECIMAL_PLACES = 0;
const ZERO_NUMBER = 0;
const ONE = 1;

@Injectable()
export class PdfTronGermanMapper implements IPdfTronMapper {
    private totalWord: string | undefined;
    private fxReserveWord: string | undefined;
    private wordBeforeCurrency: string | undefined;
    private notesColumnHeader: string | undefined;
    private tocTitle: string | undefined;
    private balanceSectionTitle: string | undefined;
    private incomeSectionTitle: string | undefined;
    private notesSectionTitle: string | undefined;
    private aopSectionTitle: string | undefined;
    private cashFlowSectionTitle: string | undefined;
    private emsSectionTitle: string | undefined;
    private fasSectionTitle: string | undefined;
    private fcpSectionTitle: string | undefined;
    private appendixSectionTitle: string | undefined;
    private cashFlowTotalTableHeader: string | undefined;
    private cashAndCashAtBeginningOfThePeriod: string | undefined;
    private cashAndCashAtEndOfThePeriod: string | undefined;
    private emsRowTitleLabel: string | undefined;
    private emsBusinessYearLabel: string | undefined;
    private emsTableTotalEquityHeader: string | undefined;
    private aopAnnualProfitName: string | undefined;
    private printFromText: string;
    private printToText: string;
    private printOfText: string;
    private pyText: string;
    private cyText: string;
    private ratioText: string;
    private taxDomText: string;
    private taxNrText: string;
    private assetsText: string;
    private liabText: string;
    private pageText: string;
    private internalLabel: string;
    private roundingDisclaimer: string;
    private draftLabel: string;

    constructor(
        private readonly attachmentReferencesHelper: AttachmentReferencesHelper,
        private readonly accountMappingDisplayHelper: AccountMappingDisplayHelper,
        private readonly aopAdjustmentsAdnReclassesHelper: AopAdjustmentsAdnReclassesHelper,
        private readonly cashFlowAdjustmentsAndReclassesHelper: CashFlowAdjustmentsAndReclassesHelper,
        private readonly notesAdjustmentsAndreclassesHelper: NotesAdjustmentsAndReclassesHelper,
        private fixedAssetsScheduleMapper: FixedAssetsScheduleGermanMapper,
        private readonly dateLocalPipe: DateLocalePipe,
        private readonly aumFinancialNumberPipe: AumFinancialNumberPipe,
        private readonly printPageHelper: PrintPageHelper,
        private readonly notesEmptyLinesRemovalHelper: NotesEmptyLinesRemovalHelper,
        private readonly countriesMapper: CountriesMapper,
        private readonly countriesService: CountriesService,
        private readonly accountProofMapper: AccountProofMapper,
        private readonly sectionFactoryService: SectionFactoryService,
        private readonly notesNumberingHelper: NotesNumberingHelper,
        private readonly notesVisibilityHelper: NotesVisibilityHelper,
        private readonly flisMapper: FlisMapper,
    ) { }

    private applyLocalizations(predifinedLables: Map<string, string>): void {
        this.totalWord = predifinedLables.get(
            localizationKeys.aumico_reports_annualreport_page_print_account_mapping_label_total,
        );
        this.fxReserveWord = predifinedLables.get(localizationKeys.aumico_annual_report_fx_notes_reserve_label);

        this.wordBeforeCurrency = predifinedLables.get(
            localizationKeys.aumico_reports_annualreport_page_print_account_mapping_label_word_before_currency,
        );
        this.balanceSectionTitle = predifinedLables.get(localizationKeys.aumico_annual_report_print_preview_balance);
        this.incomeSectionTitle = predifinedLables.get(localizationKeys.aumico_annual_report_print_preview_income);

        this.notesSectionTitle = predifinedLables.get(localizationKeys.aumico_annual_report_print_preview_attachement);
        this.aopSectionTitle = predifinedLables.get(localizationKeys.aumico_annual_report_print_preview_aop);
        this.cashFlowSectionTitle = predifinedLables.get(localizationKeys.aumico_annual_report_print_preview_cashflow);
        this.emsSectionTitle = predifinedLables.get(localizationKeys.aumico_annual_report_print_preview_ems);
        this.fasSectionTitle = predifinedLables.get(localizationKeys.aumico_annual_report_print_preview_fas);
        this.fcpSectionTitle = predifinedLables.get(localizationKeys.aumico_annual_report_print_preview_fcp);

        this.appendixSectionTitle = predifinedLables.get(
            localizationKeys.aumico_annual_report_print_preview_appendix_section_title,
        );

        this.notesColumnHeader = predifinedLables.get(
            localizationKeys.aumico_reports_annualreport_page_print_account_mapping_label_notes,
        );
        this.cashFlowTotalTableHeader = predifinedLables.get(
            localizationKeys.aumico_annual_report_cash_flow_total_recapitulation,
        );
        this.cashAndCashAtBeginningOfThePeriod = predifinedLables.get(
            localizationKeys.aumico_annual_report_cash_flow_total_cashAndCashStart,
        );
        this.cashAndCashAtEndOfThePeriod = predifinedLables.get(
            localizationKeys.aumico_annual_report_cash_flow_total_cashAndCashEnd,
        );
        this.emsRowTitleLabel = predifinedLables.get(localizationKeys.aumico_annual_report_ems_row_title);
        this.emsBusinessYearLabel = predifinedLables.get(localizationKeys.aumico_annual_report_ems_business_year);
        this.emsTableTotalEquityHeader = predifinedLables.get(
            localizationKeys.aumico_annual_report_ems_total_equity_header_text,
        );
        this.tocTitle = predifinedLables.get(localizationKeys.aumico_reports_annualreport_toc_label_table_of_contents);
        this.printFromText = predifinedLables.get(localizationKeys.aumico_reports_annualreport_print_date_from);
        this.printToText = predifinedLables.get(localizationKeys.aumico_reports_annualreport_print_date_to);
        this.printOfText = predifinedLables.get(localizationKeys.aumico_reports_annualreport_print_date_of);
        this.pyText = predifinedLables.get(localizationKeys.aumico_reports_annualreport_print_label_previous_year);
        this.cyText = predifinedLables.get(localizationKeys.aumico_reports_annualreport_print_label_current_year);
        this.ratioText = predifinedLables.get(localizationKeys.aumico_reports_annualreport_print_label_ratio);
        this.taxDomText = predifinedLables.get(localizationKeys.aumico_reports_annualreport_print_label_taxdomicile);
        this.taxNrText = predifinedLables.get(localizationKeys.aumico_reports_annualreport_print_label_taxid);
        this.assetsText = predifinedLables.get(localizationKeys.aumico_reports_annualreport_print_label_assets);
        this.liabText = predifinedLables.get(localizationKeys.aumico_reports_annualreport_print_label_liabilities);
        this.pageText = predifinedLables.get(localizationKeys.aumico_reports_annualreport_print_label_page);
        this.aopAnnualProfitName = predifinedLables.get(
            localizationKeys.aumico_annual_report_profit_calculation_annual_profit,
        );
        this.internalLabel = predifinedLables.get(localizationKeys.aumico_cover_page_internal_label);
        this.roundingDisclaimer = predifinedLables.get(
            localizationKeys.aumico_annual_report_print_page_rounding_disclaimer,
        );
        this.draftLabel = predifinedLables.get(localizationKeys.aumico_cover_page_draft_label);
    }

    /** FX (REV-21512): print-side FX bundle for the BS/ER sections (stamping + currency names). */
    private statementFx: ReturnType<typeof FxTranslationHelper.buildStatementPrintFx> = null;

    public mapAnnualReportToPdfTronData(
        annualReport: IAnnualReport,
        finStatements: IFinStatements[],
        printOptions: IPrintOptions,
        organization: GetOrganizationDto | undefined,
        annualReportForAutofill: IAnnualReport,
        activeArNavigations: AnnualReportStep[],
        complementaryReportsTitles: string[],
        GICSCodesAndNames: INameId[],
        isFixedAssetsScheduleDataValid: boolean,
        predifinedLabels: Map<string, string>,
        formatLocale: string,
        translationLocale: string,
        reportLocale: string,
        companyLogo: string | null,
        notesComponentsHTMLMap?: Map<string, Map<string, string>>,
    ): IPdfTronData {
        if (annualReport.generalInformation == null || printOptions == null) {
            throw new Error('Annual report print options or general information is undefined');
        }
        // FX (REV-21512): BS/ER twin columns — null on non-FX reports or when toggled off.
        this.statementFx =
            printOptions.displayOptions.fxShowStatementDisplayColumns !== false
                ? FxTranslationHelper.buildStatementPrintFx(annualReport, finStatements)
                : null;
        if (this.statementFx != null) {
            // Ratio columns don't fit next to four currency columns (see the locked toggle).
            printOptions.displayOptions.showRatios = false;
        }
        const complementaryReportsSelection = printOptions.displayOptions.complementaryReportsSelection;

        const reportSectionIds = ReportSectionsHelper.getOrderedSectionIds(
            printOptions.displayOptions,
            activeArNavigations,
            complementaryReportsTitles,
            annualReport,
            isFixedAssetsScheduleDataValid,
            complementaryReportsSelection,
        );

        let complementaryReportsAtTheStart: IComplementaryReport[] = [];
        let complementaryReportsAtTheEnd: IComplementaryReport[] = [];
        let complementaryReportsAsAppendix: IComplementaryReport[] = [];

        if (annualReport.complementaryReports != null) {
            const selectedComplementaryReports = annualReport.complementaryReports.filter((report, index) =>
                ReportSectionsHelper.isComplementaryReportSelected(
                    complementaryReportsSelection,
                    report,
                    index,
                ),
            );
            complementaryReportsAtTheStart = selectedComplementaryReports.filter(
                (report) => report.printLocation === PrintLocation.START,
            );
            complementaryReportsAtTheEnd = selectedComplementaryReports.filter(
                (report) => report.printLocation === PrintLocation.END,
            );
            complementaryReportsAsAppendix = selectedComplementaryReports.filter(
                (report) => report.printLocation === PrintLocation.APPENDIX,
            );
        }

        const allOrderedSectionsIds = reportSectionIds.map(ReportSectionsHelper.mapSectionIdsToPdfSectionIds);
        const complementaryReportsAsAppendixWithShowTocOn = complementaryReportsAsAppendix.filter(
            (report) => report.showInTOC,
        );

        const orderedSectionsIds: PDFSectionIds[] = [];

        allOrderedSectionsIds.forEach((sectionId, index) => {
            if (
                sectionId == PDFSectionIds.APPENDIX &&
                complementaryReportsAsAppendixWithShowTocOn.length === EMPTY_ARRAY_LENGTH
            ) {
                return;
            }

            if (sectionId.includes(PDFSectionIds.COMPLEMENTARY_REPORT)) {
                const complementaryReportIndex = this.extractNumber(sectionId);

                if (
                    !ReportSectionsHelper.isComplementaryReportSelected(
                        complementaryReportsSelection,
                        annualReport.complementaryReports[complementaryReportIndex],
                        complementaryReportIndex,
                    )
                ) {
                    return;
                }

                if (
                    annualReport.complementaryReports[complementaryReportIndex] != null &&
                    annualReport.complementaryReports[complementaryReportIndex].showInTOC === false
                ) {
                    return;
                }
            }

            orderedSectionsIds.push(sectionId);
        });

        const options: IOptions = this.getOptions(printOptions, predifinedLabels, annualReport.isSealed ?? false);

        const sections = this.getSections(
            annualReport,
            printOptions,
            annualReportForAutofill,
            reportSectionIds,
            options,
            predifinedLabels,
            formatLocale,
            translationLocale,
            reportLocale,
            complementaryReportsAsAppendix,
            finStatements,
            notesComponentsHTMLMap,
        );

        let notesDetails: INotesDetails[] = [];
        if (
            annualReport.notes != null &&
            printOptions.displayOptions.showNotes &&
            printOptions.displayOptions.showNotesInDetailInToc
        ) {
            const notes = sections.find((section) => section.sectionId === PDFSectionIds.NOTES);
            // No Anhang section (the template has no NOTES step, or it is empty): no TOC details.
            if (notes != null) {
                notesDetails = this.getNotesDetailsForToc(notes);
            }
        }

        const data: IPdfTronData = {
            cover: this.getAnnualReportCoverData(printOptions.displayOptions, companyLogo),
            customer: this.getCustomerData(annualReport.generalInformation.company, GICSCodesAndNames),
            organization: this.getOrganizationData(
                organization,
                printOptions.displayOptions.showCompanyLogo,
                translationLocale,
            ),
            options: options,
            signatures: this.getSignatures(printOptions.signatureOptions),
            date: printOptions.displayOptions.dateTitle,
            text: this.getText(this.tocTitle, annualReport.generalInformation, annualReport.isSealed ?? false),
            sections: sections,
            orderedSectionsIds,
            orderedSectionsTitles: [],
            notesDetails: notesDetails,
            sectionIdToTitle: {},
            isFxReport: annualReport.setupData?.fxEnabled === true,
            hasBs: reportSectionIds.find((section) => section === SectionIds.BALANCE) != null,
            hasIs: reportSectionIds.find((section) => section === SectionIds.INCOME) != null,
            hasCfs: reportSectionIds.find((section) => section === SectionIds.CASHFLOW) != null,
            hasNts: reportSectionIds.find((section) => section === SectionIds.NOTES) != null,
            hasAOP: reportSectionIds.find((section) => section === SectionIds.AOP) != null,
            hasFas: reportSectionIds.find((section) => section === SectionIds.FAS) != null,
            hasCrs: reportSectionIds.find((section) => section.includes(SectionIds.COMPLEMENTARY_REPORT)) != null,
            hasCrsA01:
                complementaryReportsAtTheStart != null &&
                complementaryReportsAtTheStart.length > EMPTY_ARRAY_LENGTH &&
                printOptions.displayOptions.showComplementaryReports,
            hasCrsA02:
                complementaryReportsAtTheEnd != null &&
                complementaryReportsAtTheEnd.length > EMPTY_ARRAY_LENGTH &&
                printOptions.displayOptions.showComplementaryReports,
            hasCrsA03:
                complementaryReportsAsAppendix != null &&
                complementaryReportsAsAppendix.length > EMPTY_ARRAY_LENGTH &&
                printOptions.displayOptions.showComplementaryReports,
            hasEms: reportSectionIds.find((section) => section === SectionIds.EMS) != null,
            hasApbs: reportSectionIds.find((section) => section === SectionIds.APBS) != null,
            hasApis: reportSectionIds.find((section) => section === SectionIds.APIS) != null,
            hasFcp: reportSectionIds.find((section) => section === SectionIds.FINANCIAL_CLOSING_PROCESS) != null,
            currency: annualReport.generalInformation?.currency.name,
        };

        const assignSection = (id: PDFSectionIds): ISectionData | undefined => {
            const filteredSections = data.sections.filter((x: ISectionData) => x.sectionId === id);
            return filteredSections.length > 0 ? filteredSections[0] : undefined;
        };

        data.bs = assignSection(PDFSectionIds.BALANCE);

        data.is = assignSection(PDFSectionIds.INCOME);

        data.cfs = assignSection(PDFSectionIds.CASHFLOW);

        data.nts = assignSection(PDFSectionIds.NOTES);

        data.aop = assignSection(PDFSectionIds.AOP);
        if (data.aop == null && annualReport.generalInformation != null && annualReport.appropriationOfProfit != null) {
            // Build AOP data for placeholders/snapshots even when AOP is hidden in print options.
            data.aop = this.mapAopToSectionData(
                annualReport.appropriationOfProfit,
                printOptions,
                annualReport.generalInformation,
                finStatements[templateConstants.incomeTemplate],
                translationLocale,
            );
        }

        data.fas = assignSection(PDFSectionIds.FAS);

        const complementaryReportsdata = data.sections.filter((x: ISectionData) =>
            x.sectionId.includes(PDFSectionIds.COMPLEMENTARY_REPORT),
        );

        if (!data.hasCrsA01 && !data.hasCrsA02 && !data.hasCrsA03) {
            data.crs = complementaryReportsdata;
        } else {
            data.crs = [];
            data.hasCrs = false;
        }

        if (data.crs[data.crs.length - 1] !== undefined) {
            data.crs[data.crs.length - 1].isLast = true;
        }

        data.ems = assignSection(PDFSectionIds.EMS);

        data.apbs = assignSection(PDFSectionIds.APBS);

        data.apis = assignSection(PDFSectionIds.APIS);

        data.fcp = assignSection(PDFSectionIds.FCP);

        data.crsA01 = complementaryReportsdata
            .filter((report) => report.printLocation === PrintLocation.START)
            .sort((a, b) => a.sortOrderId - b.sortOrderId);

        if (data.crsA01[data.crsA01.length - 1] !== undefined) {
            data.crsA01[data.crsA01.length - 1].isLast = true;
        }

        data.crsA02 = complementaryReportsdata
            .filter((report) => report.printLocation === PrintLocation.END)
            .sort((a, b) => a.sortOrderId - b.sortOrderId);
        if (data.crsA02[data.crsA02.length - 1] !== undefined) {
            data.crsA02[data.crsA02.length - 1].isLast = true;
        }

        data.crsA03 = complementaryReportsdata
            .filter((report) => report.printLocation === PrintLocation.APPENDIX)
            .sort((a, b) => a.sortOrderId - b.sortOrderId);
        if (data.crsA03[data.crsA03.length - 1] !== undefined) {
            data.crsA03[data.crsA03.length - 1].isLast = true;
        }
        return data;
    }

    private getAnnualReportCoverData(displayOptions: IDisplayOptions, companyLogo: string | null): ICoverData {
        return {
            mainTitle: displayOptions.mainTitle,
            subTitle: displayOptions.subtitle,
            reportingPeriod: displayOptions.dateTitle,
            companyLogo,
        };
    }

    private getCustomerData(company: ICompanyDTO, GICSCodesAndNames: INameId[]): ICustomerData {
        const gics = GICSCodesAndNames.find((codeAndName) => codeAndName.id === company.GICSCode);
        return {
            companyName: company.name,
            address: company.street,
            address2: company.additionToAddress,
            zip: company.zip,
            country: company.country?.name,
            city: company.city,
            industry: gics?.name ?? null,
            taxDomicile: company.taxDomicile,
            uid: company.UID,
        };
    }

    private getOrganizationData(
        organization: GetOrganizationDto | undefined,
        showCompanyLogo: boolean,
        translationLocale: string,
    ): IOrganizationData {
        if (organization == null) {
            return {
                companyName: '',
                address: '',
                address2: null,
                zip: null,
                city: null,
                country: null,
                telephone: '',
                email: '',
                logoBase64: null,
            };
        }

        const retrievedCountriesByLanguage: countries.LocalizedCountryNames<{ select: 'official' }> =
            this.countriesService.getCountriesByLanguage(translationLocale);
        const countries = this.countriesMapper.mapCountries(retrievedCountriesByLanguage);
        const organizationCountry = countries.find((country) => country.id === organization.countryISO31661Alpha2Code);

        return {
            companyName: organization.name,
            address: organization.street + SPACE + organization.streetNumber,
            address2: organization.additionToAddress,
            zip: organization.zip,
            city: organization.city,
            country: organizationCountry != null ? organizationCountry.name : null,
            telephone: '',
            email: '',
            logoBase64: showCompanyLogo ? organization.logo?.data : null,
        };
    }

    private getSignatures(signatureOptions: ISignatureOptions): ISignatureData {
        return {
            signature1: signatureOptions.signature1,
            signature2: signatureOptions.signature2,
            place: signatureOptions.place,
            date: signatureOptions.date,
            hasSignature: signatureOptions.hasSignature,
            placeSignatureAfterAop: signatureOptions.placeSignatureAfterAop,
            isCollectiveSignature: signatureOptions.isCollectiveSignature,
        };
    }

    private getOptions(printOptions: IPrintOptions, predifinedLables: Map<string, string>, isSealed: boolean): IOptions {
        const displayOptions: IDisplayOptions = printOptions.displayOptions;
        const styleOptions: IStyleOptions = printOptions.styleOptions;
        this.applyLocalizations(predifinedLables);
        return {
            showUID: displayOptions.showUIDonCover,
            showRatios: displayOptions.showRatios,
            showCover: displayOptions.showCoverSheet,
            showTOC: displayOptions.showTableOfContents,
            showNotesInDetailInToc: displayOptions.showNotesInDetailInToc,
            showCompanyInfos: displayOptions.showCompanyInfos,
            showShareholderInfo: displayOptions.showShareholderInformation,
            disableBalanceSheetPageBreak: displayOptions.disableBalanceSheetPageBreak,
            displayAssetsAndLiabilitiesInSamePage: false,
            printClosingValueTotalsInSeparateColumn: true,
            textColor: styleOptions.textColor,
            titleColor: styleOptions.titleColor,
            highlightColor: styleOptions.backgroundColor,
            backgroundColorPY: styleOptions.backgroundColorPY,
            tableHeaderColor: styleOptions.tableHeaderColor,
            tableFooterColor: styleOptions.tableFooterColor,
            showReportCompanyLogo: displayOptions.showReportCompanyLogo,
            showAdjustmentInSums: displayOptions.showAdjustmentInSums,
            internalLabel: this.internalLabel,
            showRoundingDisclaimer:
                displayOptions.finStatementHideDecimalPlacesValues ||
                displayOptions.notesHideDecimalPlacesValues ||
                displayOptions.aopHideDecimalPlacesValues ||
                displayOptions.finStatementRoundToThousands ||
                displayOptions.notesRoundToThousands ||
                displayOptions.aopRoundToThousands,
            showAccounts: printOptions.displayOptions.showAccounts,
            repeatAppendixTitle: displayOptions.repeatAppendixTitle ?? true,
            isSealed,
            showPageNumbers: true,
        };
    }

    private getText(tableOfContentsText: string | undefined, generalInformation: IGeneralInformation, isSealed: boolean): IText {
        let balanceToDate = '';
        let balanceFromDate = '';
        let incomeFromDate = '';
        let timeStamp = '';
        try {
            balanceToDate = this.dateLocalPipe.transform(generalInformation.balanceSheetDate).toString();
            balanceFromDate = this.dateLocalPipe.transform(generalInformation.balancePreviousYearDate).toString();
            incomeFromDate = this.dateLocalPipe.transform(generalInformation.incomeCurrentYearFromDate).toString();
            timeStamp = this.dateLocalPipe.transform(new Date(), undefined, undefined, true, true).toString();
        } catch (error) { }

        return {
            py: this.pyText,
            cy: this.cyText,
            ratio: this.ratioText,
            taxDomicile: this.taxDomText,
            taxId: this.taxNrText,
            assets: this.assetsText,
            liab: this.liabText,
            page: this.pageText,
            tableOfContents: tableOfContentsText ?? null,
            balanceToDate: balanceToDate,
            balanceFromDate: balanceFromDate,
            incomeFromDate: incomeFromDate,
            from: this.printFromText,
            to: this.printToText,
            of: this.printOfText,
            roundingDisclaimer: this.roundingDisclaimer,
            timeStamp: timeStamp,
            draftLabel: isSealed ? null : this.draftLabel,
        };
    }

    public getSections(
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        annualReportForAutofill: IAnnualReport,
        reportOrder: string[],
        options: IOptions,
        predifinedLabels: Map<string, string>,
        formatLocale: string,
        translationLocale: string,
        reportLocale: string,
        complementaryReportsAsAppendix: IComplementaryReport[],
        finStatements: IFinStatements[],
        notesComponentsHTMLMap?: Map<string, Map<string, string>>,
    ): ISectionData[] {
        this.applyLocalizations(predifinedLabels);
        const sections: ISectionData[] = [];
        const complementaryReportsSelection = printOptions.displayOptions.complementaryReportsSelection;

        reportOrder.forEach((section) => {
            const factory = this.sectionFactoryService.getFactory(section);
            if (factory) {
                const sectionData = factory.createSection(
                    annualReport,
                    printOptions,
                    annualReportForAutofill,
                    reportOrder,
                    options,
                    predifinedLabels,
                    formatLocale,
                    translationLocale,
                    reportLocale,
                );
                sections.push(sectionData);
            } else {
                switch (section) {
                    case 'balance':
                        if (
                            finStatements != null &&
                            finStatements.length === templateConstants.financialStatementMinimumLength
                        ) {
                            const balanceSheetSection = this.mapBalanceSheetToSectionData(
                                finStatements[templateConstants.balanceTemplate],
                                annualReport,
                                printOptions,
                                options,
                                reportLocale,
                                translationLocale,
                                finStatements, // all fin statements for BS reclasses
                                annualReportForAutofill, // all annual report for BS reclasses
                            );

                            sections.push(balanceSheetSection);
                        }
                        break;
                    case 'income':
                        if (
                            finStatements != null &&
                            finStatements.length === templateConstants.financialStatementMinimumLength
                        ) {
                            const incomeStatementSection = this.mapIncomeStatementToSectionData(
                                finStatements[templateConstants.incomeTemplate],
                                annualReport,
                                printOptions,
                                options,
                                reportLocale,
                                translationLocale,
                                finStatements, // all fin statements for BS reclasses
                                annualReportForAutofill, // all annual report for BS reclasses
                            );
                            sections.push(incomeStatementSection);
                        }
                        break;
                    case 'APBS':
                        if (
                            finStatements != null &&
                            finStatements.length === templateConstants.financialStatementMinimumLength &&
                            printOptions.displayOptions.showAccountProofForBalance
                        ) {
                            const accountProofPrintOptions: IPrintOptions = cloneDeep<IPrintOptions>(printOptions);
                            accountProofPrintOptions.displayOptions.showAccounts = true;
                            accountProofPrintOptions.displayOptions.showRatios = false;
                            accountProofPrintOptions.displayOptions.showAccountNumbers = false;
                            const accountProofForBalanceSection = this.accountProofMapper.mapFinStatementToAccountProof(
                                finStatements[templateConstants.balanceTemplate],
                                annualReport,
                                accountProofPrintOptions,
                                options,
                                predifinedLabels,
                                translationLocale,
                            );
                            sections.push(accountProofForBalanceSection);
                        }
                        break;
                    case 'APIS':
                        if (
                            finStatements != null &&
                            finStatements.length === templateConstants.financialStatementMinimumLength &&
                            printOptions.displayOptions.showAccountProofForIncome
                        ) {
                            const accountProofPrintOptions: IPrintOptions = cloneDeep<IPrintOptions>(printOptions);
                            accountProofPrintOptions.displayOptions.showAccounts = true;
                            accountProofPrintOptions.displayOptions.showRatios = false;
                            accountProofPrintOptions.displayOptions.showAccountNumbers = false;
                            const accountProofForIncomeSection =
                                this.accountProofMapper.mapIncomeStatementToAccountProof(
                                    finStatements[templateConstants.incomeTemplate],
                                    annualReport,
                                    accountProofPrintOptions,
                                    options,
                                    predifinedLabels,
                                    translationLocale,
                                );
                            sections.push(accountProofForIncomeSection);
                        }
                        break;
                    case 'cashflow':
                        if (annualReport.cashFlowStatement != null && printOptions.displayOptions.showCfs) {
                            const cashFlowStatementSection = this.mapCashflowStatementToSectionData(
                                annualReport.cashFlowStatement,
                                annualReport,
                                finStatements,
                                annualReportForAutofill,
                                printOptions,
                                reportLocale,
                                translationLocale,
                            );
                            sections.push(cashFlowStatementSection);
                        }
                        break;
                    case 'notes':
                        if (
                            annualReport.generalInformation != null &&
                            annualReport.notes != null &&
                            printOptions.displayOptions.showNotes
                        ) {
                            const notesSection = this.mapNotesToSectionData(
                                annualReport.notes,
                                annualReport.generalInformation.noPreviousData || printOptions.displayOptions.hidePreviousYear,
                                annualReport,
                                finStatements,
                                annualReportForAutofill,
                                printOptions,
                                translationLocale,
                                reportLocale,
                                notesComponentsHTMLMap,
                            );
                            sections.push(notesSection);
                        }
                        break;
                    case 'AOP':
                        if (
                            annualReport.generalInformation != null &&
                            printOptions.displayOptions.showAop &&
                            annualReport.appropriationOfProfit != null
                        ) {
                            const appropriationOfProfitSection = this.mapAopToSectionData(
                                annualReport.appropriationOfProfit,
                                printOptions,
                                annualReport.generalInformation,
                                finStatements[templateConstants.incomeTemplate],
                                translationLocale,
                            );
                            sections.push(appropriationOfProfitSection);
                        }
                        break;
                    case 'Ems':
                        if (annualReport.equityMovementSchedule != null && printOptions.displayOptions.showEms) {
                            const equityMovementScheduleSection = this.mapEmsToSectionData(
                                annualReport,
                                finStatements,
                                printOptions,
                                formatLocale,
                                translationLocale,
                            );
                            sections.push(equityMovementScheduleSection);
                        }
                        break;
                    case 'Fas':
                        if (annualReport.fixedAssetsSchedule != null) {
                            const fixedAssetsScheduleSection = this.mapFasToSectionData(
                                annualReport,
                                finStatements,
                                predifinedLabels,
                                printOptions,
                                translationLocale,
                            );
                            sections.push(fixedAssetsScheduleSection);
                        }
                        break;
                    case 'APPENDIX':
                        if (complementaryReportsAsAppendix.length > EMPTY_ARRAY_LENGTH) {
                            sections.push({
                                sectionId: PDFSectionIds.APPENDIX,
                                title:
                                    printOptions.reportSectionsTitles != null &&
                                        printOptions.reportSectionsTitles[SectionIds.APPENDIX] != null &&
                                        printOptions.reportSectionsTitles[SectionIds.APPENDIX][translationLocale] != null
                                        ? printOptions.reportSectionsTitles[SectionIds.APPENDIX][translationLocale]
                                        : this.appendixSectionTitle,
                            });
                        }
                        break;
                    default:
                        if (
                            section.includes(SectionIds.COMPLEMENTARY_REPORT) &&
                            annualReport.complementaryReports != null &&
                            annualReport.complementaryReports.length > EMPTY_ARRAY_LENGTH &&
                            printOptions.displayOptions.showComplementaryReports
                        ) {
                            const index = this.extractNumber(section);
                            if (index == null) {
                                break;
                            }
                            if (
                                !ReportSectionsHelper.isComplementaryReportSelected(
                                    complementaryReportsSelection,
                                    annualReport.complementaryReports[index],
                                    index,
                                )
                            ) {
                                break;
                            }
                            const indexString = index.toString();
                            const complementaryReportSection = this.mapComplementaryReportToSectionData(
                                annualReport.complementaryReports[index],
                                indexString,
                            );
                            sections.push(complementaryReportSection);
                        }
                        break;
                }
            }
        });

        return sections;
    }

    // Balance Sheet
    private mapBalanceSheetToSectionData(
        finStatement: IFinStatements,
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        options: IOptions,
        reportLocale: string,
        translationLocale: string,
        finStatements: IFinStatements[], // Only used  for BS reclasses
        annualReportForAutofill: IAnnualReport, // only used for BS reclasses
    ): ISectionData {
        const cloned = cloneDeep<IFinStatements>(finStatement);
        // REV-21798: hoist reclassification accounts into `accounts` BEFORE grouping runs.
        // `applyGroupingAndSummarize` only ever walks `accounts`, so a reclass account sitting on
        // `reclassesAccounts` could never be folded into its group — it was appended afterwards by
        // `applyReclasses` and printed OUTSIDE the group. Hoisting also puts it inside its SubFLI's
        // `accounts`, so it prints under that SubFLI. The other two surfaces (the G&M table and the
        // notes pipeline) already hoist at exactly this point; print was the odd one out.
        if (printOptions.displayOptions.applyGroupingMasking && cloned.templateAndAccountsMapping?.template?.group) {
            GroupingHelper.hoistReclassesAccounts(cloned.templateAndAccountsMapping.template.group);
        }
        const groupWithMasking = printOptions.displayOptions.applyGroupingMasking ? GroupingPathHelper.generateAndAddFullSortingState(cloned.templateAndAccountsMapping.template?.group, '') : cloned.templateAndAccountsMapping.template?.group;
        const groupedAccountsMap = annualReport.groupingAndMasking?.groupedAccounts;
        const splitAccountsMap = annualReport.groupingAndMasking?.splitGroupAccounts;
        let groupingMaskingFullyApplied = cloneDeep(groupWithMasking);
        if (groupedAccountsMap && printOptions.displayOptions.applyGroupingMasking) {
            groupingMaskingFullyApplied = GroupingHelper.applyGroupingAndSummarize(
                groupWithMasking,
                groupedAccountsMap,
            );
        }
        if (splitAccountsMap && printOptions.displayOptions.applyGroupingMasking) {
            groupingMaskingFullyApplied = SplitAccountHelper.applySplitAccounts(
                groupingMaskingFullyApplied,
                splitAccountsMap,
            );
            groupingMaskingFullyApplied = GroupingHelper.recalculateGroupSummaries(
                groupingMaskingFullyApplied
            );
        }
        this.statementFx?.stampTemplateGroup(groupingMaskingFullyApplied, 'balance');
        const accountsMappingTemplateGroups = (
            groupingMaskingFullyApplied.sortedItems as IAccountMappingTemplateGroup[]
        ).filter((item) => this.isGroup(item));
        const currency = PdfTronMapperHelper.getCurrencyLabel(
            annualReport.generalInformation?.currency?.name,
            printOptions.displayOptions.finStatementRoundToThousands,
        );

        if (annualReport.generalInformation == null) {
            throw new Error("Annual report's general information is null");
        }

        if (printOptions == null) {
            throw new Error('Annual report print option is undefined');
        }

        const noPrevious = annualReport.generalInformation?.noPreviousData || printOptions.displayOptions.hidePreviousYear;

        const {
            numberOfDecimalPlacesValues,
            numberFormatValues,
            numberOfDecimalPlacesRatios,
            numberFormatRatios,
            valueDivisor,
        } = this.getNumberFormatAndDecimalPlaces(
            printOptions.displayOptions,
            printOptions.displayOptions.finStatementHideDecimalPlacesValues,
            printOptions.displayOptions.finStatementHideDecimalPlacesRatios,
            printOptions.displayOptions.finStatementRoundToThousands,
        );

        const finStatementDataAssets: IFinancialTableData[] = [];
        const finStatementDataLiabilitiesAndEquity: IFinancialTableData[] = [];
        const finStatementDataLiabilities: IFinancialTableData[] = [];
        const finStatementDataEquity: IFinancialTableData[] = [];

        let balanceIncomeAttachmentReferences: Map<string, string>;

        if (
            printOptions?.displayOptions.showNotes &&
            annualReport.notes != null &&
            annualReport.notes.accordions != null
        ) {
            const currentYearFliIdToAccounts = this.printPageHelper.getFliIdToAccountsForAnnualReport(finStatements);

            const previousYearFliIdToAccounts = this.printPageHelper.getFliIdToAccountsForAnnualReport(
                annualReportForAutofill?.fcp
                    ? annualReportForAutofill.fcp.outputFinStatements
                    : annualReportForAutofill?.finStatements,
            );

            let notesAccordions = this.notesAdjustmentsAndreclassesHelper.applyAdjustMentsAndReclasses(
                annualReport.notes.accordions,
                printOptions.displayOptions,
                {
                    currentFliIdToAccounts: currentYearFliIdToAccounts,
                    previousFliIdToAccounts: previousYearFliIdToAccounts,
                },
            );

            let notesAccordionsWithNumbering = this.notesNumberingHelper.applyNumbering(
                notesAccordions,
                translationLocale,
                printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.notesHideEmptyLines,
                reportLocale,
            );

            // get the notes column values
            if (printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.notesHideEmptyLines) {
                const notesAccordionsAfterRemoveEmptyLines = this.notesEmptyLinesRemovalHelper.removeEmptyLinesOfNotes(
                    [...notesAccordionsWithNumbering],
                    reportLocale,
                );
                balanceIncomeAttachmentReferences =
                    this.attachmentReferencesHelper.getBalanceIncomeAttachmentReferences(
                        notesAccordionsAfterRemoveEmptyLines,
                        translationLocale,
                    );
            } else {
                balanceIncomeAttachmentReferences =
                    this.attachmentReferencesHelper.getBalanceIncomeAttachmentReferences(
                        notesAccordionsWithNumbering,
                        translationLocale,
                    );
            }
        }

        if (accountsMappingTemplateGroups != null) {
            // first we loop on the main groups
            let mainGroupIndex = 0;
            accountsMappingTemplateGroups.forEach((accountsMappingTemplateGroup) => {
                let finStatementData = finStatementDataAssets;
                if (mainGroupIndex > 0) {
                    finStatementData = finStatementDataLiabilitiesAndEquity;
                }
                let tableHeaderFinancialData: IFinancialTableData;
                if (accountsMappingTemplateGroup.isEmpty) {
                    tableHeaderFinancialData = {
                        rowType: RowTypes.EMPTY_ROW,
                        t: undefined,
                    };
                    finStatementData.push(tableHeaderFinancialData);
                } else if (accountsMappingTemplateGroup.isHeader) {
                    tableHeaderFinancialData = {
                        rowType: RowTypes.HEADER_ROW,
                        t: accountsMappingTemplateGroup.header,
                        level: 1,
                    };

                    finStatementData.push(tableHeaderFinancialData);
                } else {
                    const previousDate = noPrevious
                        ? undefined
                        : this.dateLocalPipe
                            .transform(annualReport.generalInformation.balancePreviousYearDate)
                            .toString();
                    const currentDate = this.dateLocalPipe
                        .transform(annualReport.generalInformation.balanceSheetDate)
                        .toString();

                    tableHeaderFinancialData = {
                        rowType: RowTypes.TABLE_HEAD,
                        t:
                            (accountsMappingTemplateGroup.title ?? EMPTY_STRING) +
                            SPACE +
                            this.wordBeforeCurrency +
                            SPACE +
                            currency,
                        pV: noPrevious ? undefined : previousDate,
                        cV: currentDate,
                        cF: '',
                        level: 0,
                    };
                    if (this.statementFx != null) {
                        tableHeaderFinancialData.t = accountsMappingTemplateGroup.title ?? EMPTY_STRING;
                        tableHeaderFinancialData.cV =
                            currentDate +
                            LINE_BREAK +
                            PdfTronMapperHelper.getCurrencyLabel(
                                this.statementFx.functionalCurrency,
                                printOptions.displayOptions.finStatementRoundToThousands,
                            );
                        tableHeaderFinancialData.cV2 =
                            currentDate +
                            LINE_BREAK +
                            PdfTronMapperHelper.getCurrencyLabel(
                                this.statementFx.displayCurrency,
                                printOptions.displayOptions.finStatementRoundToThousands,
                            );
                        tableHeaderFinancialData.pV = noPrevious
                            ? undefined
                            : previousDate +
                              LINE_BREAK +
                              PdfTronMapperHelper.getCurrencyLabel(
                                  this.statementFx.functionalCurrency,
                                  printOptions.displayOptions.finStatementRoundToThousands,
                              );
                        tableHeaderFinancialData.pV2 = noPrevious
                            ? undefined
                            : previousDate +
                              LINE_BREAK +
                              PdfTronMapperHelper.getCurrencyLabel(
                                  this.statementFx.displayCurrency,
                                  printOptions.displayOptions.finStatementRoundToThousands,
                              );
                    }

                    if (printOptions.displayOptions.showNotes) {
                        tableHeaderFinancialData.r = this.notesColumnHeader;
                    }

                    if (printOptions.displayOptions.showRatios) {
                        tableHeaderFinancialData.pP = noPrevious ? undefined : PERCENTAGE;
                        tableHeaderFinancialData.cP = PERCENTAGE;
                    }

                    // push the table header
                    finStatementData.push(tableHeaderFinancialData);

                    // get the reference for calculating the ratios
                    const ratioReferenceOpeningAndClosing = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                        accountsMappingTemplateGroup,
                        printOptions.displayOptions.showReClassInSums,
                        printOptions.displayOptions.showAdjustmentInSums,
                    );

                    const referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation = {
                        openingReferenceValue: ratioReferenceOpeningAndClosing.openingValue,
                        closingReferenceValue: ratioReferenceOpeningAndClosing.closingValue,
                    };

                    let tableTotalFinancialData: IFinancialTableData | undefined;

                    if (printOptions.displayOptions.showGroupTotals) {
                        const groupMappingValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                            accountsMappingTemplateGroup,
                            printOptions.displayOptions.showReClassInSums,
                            printOptions.displayOptions.showAdjustmentInSums,
                        );

                        const previousValue =
                            groupMappingValues.openingValue != null
                                ? NumberUtils.roundNumber(
                                    PdfTronMapperHelper.scaleValue(groupMappingValues.openingValue, valueDivisor),
                                    false,
                                    numberOfDecimalPlacesValues,
                                )
                                : null;
                        const currentValue =
                            groupMappingValues.openingValue != null
                                ? NumberUtils.roundNumber(
                                    PdfTronMapperHelper.scaleValue(groupMappingValues.closingValue, valueDivisor),
                                    false,
                                    numberOfDecimalPlacesValues,
                                )
                                : null;

                        const formattedPreviousValue =
                            previousValue != null
                                ? this.aumFinancialNumberPipe.transform(
                                    previousValue,
                                    numberFormatValues,
                                    printOptions.displayOptions.nonBreakingHyphen,
                                )
                                : null;

                        const formattedCurrentValue =
                            currentValue != null
                                ? this.aumFinancialNumberPipe.transform(
                                    currentValue,
                                    numberFormatValues,
                                    printOptions.displayOptions.nonBreakingHyphen,
                                )
                                : null;

                        tableTotalFinancialData = {
                            rowType: RowTypes.TABLE_TOTAL,
                            t: this.totalWord + SPACE + (accountsMappingTemplateGroup.totalName ?? EMPTY_STRING),
                            pV: noPrevious ? undefined : formattedPreviousValue,
                            cV: formattedCurrentValue,
                        };
                        // «Total Passiven» only balances against the assets side in the display
                        // currency once the Umrechnungsreserve is included.
                        this.applyStatementFxValues(
                            tableTotalFinancialData,
                            accountsMappingTemplateGroup,
                            numberFormatValues,
                            valueDivisor,
                            printOptions.displayOptions.nonBreakingHyphen,
                            noPrevious,
                            mainGroupIndex > 0,
                        );

                        if (printOptions.displayOptions.showRatios) {
                            const groupRatioValues = this.accountMappingDisplayHelper.getOpeningAndClosingRatiosValues(
                                groupMappingValues.openingValue,
                                groupMappingValues.closingValue,
                                referenceValuesForRatioCalculation,
                                noPrevious,
                            );

                            const previousRatioValue =
                                groupRatioValues.openingRatioValue != null
                                    ? NumberUtils.roundNumber(
                                        groupRatioValues.openingRatioValue,
                                        false,
                                        numberOfDecimalPlacesRatios,
                                    )
                                    : null;
                            const currentRatioValue =
                                groupRatioValues.closingRatioValue != null
                                    ? NumberUtils.roundNumber(
                                        groupRatioValues.closingRatioValue,
                                        false,
                                        numberOfDecimalPlacesRatios,
                                    )
                                    : null;

                            tableTotalFinancialData.pP = noPrevious
                                ? undefined
                                : this.aumFinancialNumberPipe.transform(
                                    previousRatioValue,
                                    numberFormatRatios,
                                    printOptions.displayOptions.nonBreakingHyphen,
                                );
                            tableTotalFinancialData.cP = this.aumFinancialNumberPipe.transform(
                                currentRatioValue,
                                numberFormatRatios,
                                printOptions.displayOptions.nonBreakingHyphen,
                            );
                        }
                    }

                    if (accountsMappingTemplateGroup.sortedItems == null) {
                        throw new Error(`sortedItems array does not exist for group ${accountsMappingTemplateGroup}`);
                    }
                    let mainSubGroupIndex = 0;
                    const rawSortedData = this.mapSortedItemsToFinancialTableData(
                        accountsMappingTemplateGroup,
                        accountsMappingTemplateGroup.sortedItems as SortedMappingItem[],
                        annualReport,
                        printOptions,
                        balanceIncomeAttachmentReferences,
                        referenceValuesForRatioCalculation,
                        options,
                        1,
                        reportLocale,
                    );

                    if (printOptions.displayOptions.showGroupTotals && tableTotalFinancialData) {
                        rawSortedData.push(tableTotalFinancialData);
                    }

                    //Clean up, Remove empty text sums if they follow on a user added SubTotal
                    //Clean up, Remove empty text sums if they are before a user added SubTotal
                    const dedupedTotals: IFinancialTableData[] =
                        this.removeDuplicateTotalsPreferSubtotals(rawSortedData);

                    //Clean up, Remove totals of Hidden Groups which we don't need
                    const accountsMappingTemplateSortedData: IFinancialTableData[] =
                        this.removeHiddenGroupDuplicateTotals(dedupedTotals);

                    if (this.statementFx != null && mainGroupIndex === 1) {
                        this.insertFxReserveRow(
                            accountsMappingTemplateSortedData,
                            accountsMappingTemplateGroup,
                            cloned.templateAndAccountsMapping.template?.equityGroupName,
                            numberFormatValues,
                            valueDivisor,
                            printOptions.displayOptions.nonBreakingHyphen,
                            noPrevious,
                            printOptions.displayOptions.hideEmptyLines,
                        );
                    }

                    // Add the group also to Liabiltis or Equity
                    if (mainGroupIndex == 1 && mainSubGroupIndex == 0) {
                        finStatementDataLiabilities.push(...accountsMappingTemplateSortedData);
                    } else if (mainGroupIndex == 1 && mainSubGroupIndex == 1) {
                        finStatementDataEquity.push(...accountsMappingTemplateSortedData);
                    }

                    finStatementData.push(...accountsMappingTemplateSortedData);
                    mainSubGroupIndex++;

                    mainGroupIndex++;
                }
            });
        }

        const balanceSheetData: IBalanceSheetData = {
            assets: finStatementDataAssets,
            liabilities: finStatementDataLiabilities,
            equity: finStatementDataEquity,
            liabilitiesAndEquity: finStatementDataLiabilitiesAndEquity,
        };

        return {
            sectionId: PDFSectionIds.BALANCE,
            title:
                printOptions.reportSectionsTitles != null &&
                    printOptions.reportSectionsTitles[SectionIds.BALANCE] != null &&
                    printOptions.reportSectionsTitles[SectionIds.BALANCE][translationLocale] != null
                    ? printOptions.reportSectionsTitles[SectionIds.BALANCE][translationLocale]
                    : this.balanceSectionTitle,
            hasSignature: true,
            data: balanceSheetData,
            noPrevious: annualReport.generalInformation.noPreviousData || printOptions.displayOptions.hidePreviousYear,
            isFxFormat: this.statementFx != null,
            showReference: printOptions?.displayOptions.showNotes,
            showRatios: printOptions?.displayOptions.showRatios,
        };
    }

    private removeDuplicateTotalsPreferSubtotals(
        rawSortedData: IFinancialTableData[],
    ): IFinancialTableData[] {
        const indexesToRemove = new Set<number>(); // Use a Set to store unique indexes

        rawSortedData.forEach((item, index, array) => {
            if (index < array.length - 1) {
                const nextItem = array[index + 1];
                // Check if both items are totals and have matching cF, cV, and pV values
                if (
                    (item.rowType === RowTypes.SUB_TOTAL || item.rowType === RowTypes.GROUP_TOTAL) &&
                    (nextItem.rowType === RowTypes.GROUP_TOTAL || nextItem.rowType === RowTypes.SUB_TOTAL) &&
                    item.cF === nextItem.cF &&
                    item.cV === nextItem.cV &&
                    item.pV === nextItem.pV
                ) {
                    // Prioritize removing GROUP_TOTAL, whether it's the current item or the next
                    if (item.rowType === RowTypes.GROUP_TOTAL) {
                        indexesToRemove.add(index); // Mark the current GROUP_TOTAL for removal
                    } else if (nextItem.rowType === RowTypes.GROUP_TOTAL) {
                        indexesToRemove.add(index + 1); // Mark the next GROUP_TOTAL for removal
                    }
                }
            }
        });

        // Filter out the items marked for removal
        return rawSortedData.filter((_, index) => !indexesToRemove.has(index));
    }

    private removeHiddenGroupDuplicateTotals(
        rawSortedData: IFinancialTableData[],
    ): IFinancialTableData[] {
        const indexesToRemove = new Set<number>(); // Use a Set to store unique indexes

        rawSortedData.forEach((item, index, array) => {
            if (index < array.length - 1) {
                const nextItem = array[index + 1];
                // Check if both items are totals and have matching cF, cV, and pV values
                if (
                    (item.rowType === RowTypes.SUB_TOTAL || item.rowType === RowTypes.GROUP_TOTAL || item.rowType === RowTypes.TABLE_TOTAL) &&
                    (nextItem.rowType === RowTypes.GROUP_TOTAL || nextItem.rowType === RowTypes.SUB_TOTAL || nextItem.rowType === RowTypes.TABLE_TOTAL) &&
                    item.cF === nextItem.cF &&
                    item.cV === nextItem.cV &&
                    item.pV === nextItem.pV
                ) {
                    // Prioritize removing HIDDEN_GROUP, whether it's the current item or the next
                    if (item["fromHiddenGroup"] === true) {
                        indexesToRemove.add(index); // Mark the current HIDDEN_GROUP for removal
                    }
                }
            }
        });

        // Filter out the items marked for removal
        return rawSortedData.filter((_, index) => !indexesToRemove.has(index));
    }

    // Income Statement
    private mapIncomeStatementToSectionData(
        finStatement: IFinStatements,
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        options: IOptions,
        reportLocale: string,
        translationLocale: string,
        finStatements: IFinStatements[], // Only used for IS reclasses
        annualReportForAutofill: IAnnualReport, // only used for IS reclasses
    ): ISectionData {
        const cloned = cloneDeep<IFinStatements>(finStatement);
        // REV-21798: hoist reclassification accounts into `accounts` BEFORE grouping runs.
        // `applyGroupingAndSummarize` only ever walks `accounts`, so a reclass account sitting on
        // `reclassesAccounts` could never be folded into its group — it was appended afterwards by
        // `applyReclasses` and printed OUTSIDE the group. Hoisting also puts it inside its SubFLI's
        // `accounts`, so it prints under that SubFLI. The other two surfaces (the G&M table and the
        // notes pipeline) already hoist at exactly this point; print was the odd one out.
        if (printOptions.displayOptions.applyGroupingMasking && cloned.templateAndAccountsMapping?.template?.group) {
            GroupingHelper.hoistReclassesAccounts(cloned.templateAndAccountsMapping.template.group);
        }
        const groupWithMasking = printOptions.displayOptions.applyGroupingMasking ? GroupingPathHelper.generateAndAddFullSortingState(cloned.templateAndAccountsMapping.template?.group, '') : cloned.templateAndAccountsMapping.template?.group;
        const groupedAccountsMap = annualReport.groupingAndMasking?.groupedAccounts;
        let groupingMaskingFullyApplied = cloneDeep(groupWithMasking);
        if (groupedAccountsMap && printOptions.displayOptions.applyGroupingMasking) {
            groupingMaskingFullyApplied = GroupingHelper.applyGroupingAndSummarize(
                groupWithMasking,
                groupedAccountsMap,
            );
        }
        const accountsMappingTemplateMainGroup = groupingMaskingFullyApplied;
        const accountsMappingTemplateGroups = [groupingMaskingFullyApplied];
        this.statementFx?.stampTemplateGroup(groupingMaskingFullyApplied, 'income');
        const currency = PdfTronMapperHelper.getCurrencyLabel(
            annualReport.generalInformation?.currency?.name,
            printOptions.displayOptions.finStatementRoundToThousands,
        );

        if (annualReport.generalInformation == null || printOptions == null) {
            throw new Error("Annual report's general information is null");
        }
        const noPrevious = annualReport.generalInformation.noPreviousData || printOptions.displayOptions.hidePreviousYear;

        const {
            numberOfDecimalPlacesValues,
            numberFormatValues,
            numberOfDecimalPlacesRatios,
            numberFormatRatios,
            valueDivisor,
        } = this.getNumberFormatAndDecimalPlaces(
            printOptions.displayOptions,
            printOptions.displayOptions.finStatementHideDecimalPlacesValues,
            printOptions.displayOptions.finStatementHideDecimalPlacesRatios,
            printOptions.displayOptions.finStatementRoundToThousands,
        );

        const finStatementData: IFinancialTableData[] = [];

        if (accountsMappingTemplateMainGroup == null) {
            throw new Error('Accounts mapping template main group is null');
        }

        // get the income ratio reference
        const incomeItemForCalculatingRatio = this.getIncomeRatioReferenceItem(
            accountsMappingTemplateMainGroup,
            printOptions.displayOptions,
            noPrevious,
        );

        let referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation;

        if (incomeItemForCalculatingRatio != null) {
            const ratioReferenceOpeningAndClosing = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                incomeItemForCalculatingRatio,
                printOptions.displayOptions.showReClassInSums,
                printOptions.displayOptions.showAdjustmentInSums,
            );

            referenceValuesForRatioCalculation = {
                openingReferenceValue: ratioReferenceOpeningAndClosing.openingValue,
                closingReferenceValue: ratioReferenceOpeningAndClosing.closingValue,
            };
        } else {
            referenceValuesForRatioCalculation = {
                openingReferenceValue: 0,
                closingReferenceValue: 0,
            };
        }

        let balanceIncomeAttachmentReferences: Map<string, string> = new Map<string, string>();

        if (printOptions.displayOptions.showNotes && annualReport.notes != null) {
            const currentYearFliIdToAccounts = this.printPageHelper.getFliIdToAccountsForAnnualReport(finStatements);

            const previousYearFliIdToAccounts = this.printPageHelper.getFliIdToAccountsForAnnualReport(
                annualReportForAutofill?.fcp
                    ? annualReportForAutofill.fcp.outputFinStatements
                    : annualReportForAutofill?.finStatements,
            );

            let notesAccordions = this.notesAdjustmentsAndreclassesHelper.applyAdjustMentsAndReclasses(
                annualReport.notes.accordions,
                printOptions.displayOptions,
                {
                    currentFliIdToAccounts: currentYearFliIdToAccounts,
                    previousFliIdToAccounts: previousYearFliIdToAccounts,
                },
            );

            let notesAccordionsWithNumbering = this.notesNumberingHelper.applyNumbering(
                notesAccordions,
                translationLocale,
                printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.notesHideEmptyLines,
                reportLocale,
            );

            if (printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.notesHideEmptyLines) {
                notesAccordionsWithNumbering = this.notesEmptyLinesRemovalHelper.removeEmptyLinesOfNotes(
                    [...notesAccordionsWithNumbering],
                    reportLocale,
                );
            }

            // get the notes column values
            if (printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.notesHideEmptyLines) {
                const notesAccordionsAfterRemoveEmptyLines = this.notesEmptyLinesRemovalHelper.removeEmptyLinesOfNotes(
                    [...notesAccordionsWithNumbering],
                    reportLocale,
                );
                balanceIncomeAttachmentReferences =
                    this.attachmentReferencesHelper.getBalanceIncomeAttachmentReferences(
                        notesAccordionsAfterRemoveEmptyLines,
                        translationLocale,
                    );
            } else {
                balanceIncomeAttachmentReferences =
                    this.attachmentReferencesHelper.getBalanceIncomeAttachmentReferences(
                        notesAccordionsWithNumbering,
                        translationLocale,
                    );
            }
        }

        const currentYearIncomeDate =
            this.dateLocalPipe.transform(annualReport.generalInformation.incomeCurrentYearFromDate).toString() +
            LINE_BREAK +
            SEPERATOR +
            (annualReport.generalInformation.incomeCurrentYearToDate != null
                ? this.dateLocalPipe.transform(annualReport.generalInformation.incomeCurrentYearToDate).toString()
                : '');

        const incomePreaviousYear =
            annualReport.generalInformation.incomePreviousYearToDate != null
                ? this.dateLocalPipe.transform(annualReport.generalInformation.incomePreviousYearToDate).toString()
                : '';

        const previousYearIncomeDate = annualReport.generalInformation.noPreviousData
            ? undefined
            : this.dateLocalPipe.transform(annualReport.generalInformation.incomePreviousYearFromDate).toString() +
            LINE_BREAK +
            SEPERATOR +
            incomePreaviousYear;

        const tableHeaderFinancialData: IFinancialTableData = {
            rowType: RowTypes.TABLE_HEAD,
            t: this.wordBeforeCurrency + SPACE + currency,
            pV: noPrevious ? undefined : previousYearIncomeDate,
            cV: currentYearIncomeDate,
            cF: '',
            level: 0,
        };
        if (this.statementFx != null) {
            tableHeaderFinancialData.t = EMPTY_STRING;
            tableHeaderFinancialData.cV =
                currentYearIncomeDate +
                LINE_BREAK +
                PdfTronMapperHelper.getCurrencyLabel(
                    this.statementFx.functionalCurrency,
                    printOptions.displayOptions.finStatementRoundToThousands,
                );
            tableHeaderFinancialData.cV2 =
                currentYearIncomeDate +
                LINE_BREAK +
                PdfTronMapperHelper.getCurrencyLabel(
                    this.statementFx.displayCurrency,
                    printOptions.displayOptions.finStatementRoundToThousands,
                );
            tableHeaderFinancialData.pV = noPrevious
                ? undefined
                : previousYearIncomeDate +
                  LINE_BREAK +
                  PdfTronMapperHelper.getCurrencyLabel(
                      this.statementFx.functionalCurrency,
                      printOptions.displayOptions.finStatementRoundToThousands,
                  );
            tableHeaderFinancialData.pV2 = noPrevious
                ? undefined
                : previousYearIncomeDate +
                  LINE_BREAK +
                  PdfTronMapperHelper.getCurrencyLabel(
                      this.statementFx.displayCurrency,
                      printOptions.displayOptions.finStatementRoundToThousands,
                  );
        }


        if (printOptions.displayOptions.showRatios) {
            tableHeaderFinancialData.pP = noPrevious ? undefined : PERCENTAGE;
            tableHeaderFinancialData.cP = PERCENTAGE;
        }

        if (printOptions.displayOptions.showNotes) {
            tableHeaderFinancialData.r = this.notesColumnHeader;
        }

        finStatementData.push(tableHeaderFinancialData);
        if (printOptions == null) {
            throw new Error('Print options is null');
        }
        if (accountsMappingTemplateGroups != null) {
            accountsMappingTemplateGroups.forEach((accountsMappingTemplateGroup, index: number) => {
                let tableTotalFinancialData: IFinancialTableData | undefined;
                if (accountsMappingTemplateGroup.isEmpty) {
                    tableTotalFinancialData = {
                        rowType: RowTypes.EMPTY_ROW,
                        t: undefined,
                    };
                    finStatementData.push(tableTotalFinancialData);
                } else if (accountsMappingTemplateGroup.isHeader) {
                    tableTotalFinancialData = {
                        rowType: RowTypes.HEADER_ROW,
                        t: accountsMappingTemplateGroup.header,
                        level: 1,
                    };

                    finStatementData.push(tableTotalFinancialData);
                } else {
                    if (printOptions.displayOptions.showGroupTotals) {
                        const groupMappingValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                            accountsMappingTemplateGroup,
                            printOptions.displayOptions.showReClassInSums,
                            printOptions.displayOptions.showAdjustmentInSums,
                        );

                        const previousValue =
                            groupMappingValues.openingValue != null
                                ? NumberUtils.roundNumber(
                                    PdfTronMapperHelper.scaleValue(groupMappingValues.openingValue, valueDivisor),
                                    false,
                                    numberOfDecimalPlacesValues,
                                )
                                : null;
                        const currentValue =
                            groupMappingValues.closingValue != null
                                ? NumberUtils.roundNumber(
                                    PdfTronMapperHelper.scaleValue(groupMappingValues.closingValue, valueDivisor),
                                    false,
                                    numberOfDecimalPlacesValues,
                                )
                                : null;

                        const formattedPreviousValue =
                            previousValue != null
                                ? this.aumFinancialNumberPipe.transform(
                                    previousValue,
                                    numberFormatValues,
                                    printOptions.displayOptions.nonBreakingHyphen,
                                )
                                : null;
                        const formattedCurrentValue =
                            currentValue != null
                                ? this.aumFinancialNumberPipe.transform(
                                    currentValue,
                                    numberFormatValues,
                                    printOptions.displayOptions.nonBreakingHyphen,
                                )
                                : null;

                        tableTotalFinancialData = {
                            rowType: RowTypes.GROUP_TOTAL,
                            t: this.totalWord + SPACE + (accountsMappingTemplateGroup.totalName ?? EMPTY_STRING),
                            pV: noPrevious ? undefined : formattedPreviousValue,
                            cV: formattedCurrentValue,
                        };
                        this.applyStatementFxValues(
                            tableTotalFinancialData,
                            accountsMappingTemplateGroup,
                            numberFormatValues,
                            valueDivisor,
                            printOptions.displayOptions.nonBreakingHyphen,
                            noPrevious,
                        );

                        if (printOptions.displayOptions.showRatios) {
                            const groupRatioValues = this.accountMappingDisplayHelper.getOpeningAndClosingRatiosValues(
                                groupMappingValues.openingValue,
                                groupMappingValues.closingValue,
                                referenceValuesForRatioCalculation,
                                noPrevious,
                            );

                            const previousRatioValue =
                                groupRatioValues.openingRatioValue != null
                                    ? NumberUtils.roundNumber(
                                        groupRatioValues.openingRatioValue,
                                        false,
                                        numberOfDecimalPlacesRatios,
                                    )
                                    : null;

                            const currentRatioValue =
                                groupRatioValues.closingRatioValue != null
                                    ? NumberUtils.roundNumber(
                                        groupRatioValues.closingRatioValue,
                                        false,
                                        numberOfDecimalPlacesRatios,
                                    )
                                    : null;

                            tableTotalFinancialData.pP = noPrevious
                                ? undefined
                                : this.aumFinancialNumberPipe.transform(
                                    previousRatioValue,
                                    numberFormatRatios,
                                    printOptions.displayOptions.nonBreakingHyphen,
                                );
                            tableTotalFinancialData.cP = this.aumFinancialNumberPipe.transform(
                                currentRatioValue,
                                numberFormatRatios,
                                printOptions.displayOptions.nonBreakingHyphen,
                            );
                        }
                    }

                    if (accountsMappingTemplateGroup.sortedItems == null) {
                        throw new Error(`sortedItems Array does not exist for group ${accountsMappingTemplateGroup} `);
                    }
                    const rawSortedData = this.mapSortedItemsToFinancialTableData(
                        accountsMappingTemplateGroup,
                        accountsMappingTemplateGroup.sortedItems as SortedMappingItem[],
                        annualReport,
                        printOptions,
                        balanceIncomeAttachmentReferences,
                        referenceValuesForRatioCalculation,
                        options,
                        1,
                        reportLocale,
                    );

                    if (printOptions.displayOptions.showGroupTotals && index !== FIRST_ELEMENT_INDEX && tableTotalFinancialData) {
                        rawSortedData.push(tableTotalFinancialData);
                    }

                    //Clean up, Remove empty text sums if they follow on a user added SubTotal
                    //Clean up, Remove empty text sums if they are before a user added SubTotal
                    const dedupedTotals: IFinancialTableData[] =
                        this.removeDuplicateTotalsPreferSubtotals(rawSortedData);

                    //Clean up, Remove totals of Hidden Groups which we don't need
                    const accountsMappingTemplateSortedData: IFinancialTableData[] =
                        this.removeHiddenGroupDuplicateTotals(dedupedTotals);

                    finStatementData.push(...accountsMappingTemplateSortedData);
                }
            });
        }

        if (printOptions.displayOptions.showGroupTotals) {
            const previousValue =
                accountsMappingTemplateMainGroup.calculatedOpeningBalance != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(
                              accountsMappingTemplateMainGroup.calculatedOpeningBalance,
                              valueDivisor,
                          ),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;
            const currentValue =
                accountsMappingTemplateMainGroup.calculatedClosingBalance != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(
                              accountsMappingTemplateMainGroup.calculatedClosingBalance,
                              valueDivisor,
                          ),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;

            const formattedPreviousValue =
                previousValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        previousValue,
                        numberFormatValues,
                        printOptions.displayOptions.nonBreakingHyphen,
                    )
                    : null;

            const formattedCurrentValue =
                currentValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        currentValue,
                        numberFormatValues,
                        printOptions.displayOptions.nonBreakingHyphen,
                    )
                    : null;

            const tableTotalFinancialData: IFinancialTableData = {
                rowType: RowTypes.TABLE_TOTAL,
                t: '',
                pV: noPrevious ? undefined : formattedPreviousValue,
                cV: formattedCurrentValue,
            };
            this.applyStatementFxValues(
                tableTotalFinancialData,
                accountsMappingTemplateMainGroup,
                numberFormatValues,
                valueDivisor,
                printOptions.displayOptions.nonBreakingHyphen,
                noPrevious,
            );

            if (printOptions.displayOptions.showRatios) {
                const mainGroupMappingValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                    accountsMappingTemplateMainGroup,
                    printOptions.displayOptions.showReClassInSums,
                    printOptions.displayOptions.showAdjustmentInSums,
                );

                const mainGroupRatioValues = this.accountMappingDisplayHelper.getOpeningAndClosingRatiosValues(
                    mainGroupMappingValues.openingValue,
                    mainGroupMappingValues.closingValue,
                    referenceValuesForRatioCalculation,
                    noPrevious,
                );

                const previousRatioValue =
                    mainGroupRatioValues.openingRatioValue != null
                        ? NumberUtils.roundNumber(
                            mainGroupRatioValues.openingRatioValue,
                            false,
                            numberOfDecimalPlacesRatios,
                        )
                        : null;
                const currentRatioValue =
                    mainGroupRatioValues.closingRatioValue != null
                        ? NumberUtils.roundNumber(
                            mainGroupRatioValues.closingRatioValue,
                            false,
                            numberOfDecimalPlacesRatios,
                        )
                        : null;

                tableTotalFinancialData.pP = noPrevious
                    ? undefined
                    : this.aumFinancialNumberPipe.transform(
                        previousRatioValue,
                        numberFormatRatios,
                        printOptions.displayOptions.nonBreakingHyphen,
                    );
                tableTotalFinancialData.cP = this.aumFinancialNumberPipe.transform(
                    currentRatioValue,
                    numberFormatRatios,
                    printOptions.displayOptions.nonBreakingHyphen,
                    reportLocale,
                );
            }

            if (printOptions.displayOptions.showNotes) {
                tableTotalFinancialData.r = null;
            }

            if (
                finStatementData.length > 0 &&
                finStatementData[finStatementData.length - 1].rowType == RowTypes.SUB_TOTAL
            ) {
                const lastSubTotal = finStatementData[finStatementData.length - 1];

                if (lastSubTotal.pV == tableTotalFinancialData.pV && lastSubTotal.cV == tableTotalFinancialData.cV) {
                    lastSubTotal.rowType = tableTotalFinancialData.rowType;
                } else {
                    finStatementData.push(tableTotalFinancialData);
                }
            } else {
                finStatementData.push(tableTotalFinancialData);
            }
        }

        return {
            sectionId: PDFSectionIds.INCOME,
            title:
                printOptions.reportSectionsTitles != null &&
                    printOptions.reportSectionsTitles[SectionIds.INCOME] != null &&
                    printOptions.reportSectionsTitles[SectionIds.INCOME][translationLocale] != null
                    ? printOptions.reportSectionsTitles[SectionIds.INCOME][translationLocale]
                    : this.incomeSectionTitle,
            hasSignature: true,
            data: finStatementData,
            noPrevious: annualReport.generalInformation.noPreviousData || printOptions.displayOptions.hidePreviousYear,
            isFxFormat: this.statementFx != null,
            showReference: printOptions.displayOptions.showNotes,
            showRatios: printOptions.displayOptions.showRatios,
        };
    }

    private mapItemToFinancialTableData(
        parentGroup: IAccountMappingTemplateGroup,
        item: AccountMappingTemplateGroupItem,
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        balanceIncomeAttachmentReferences: Map<string, string>,
        referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation,
        options: IOptions,
        showFliTotal: boolean,
        noPrevious: boolean,
        hideEmptyLines: boolean,
        numberOfDecimalPlacesValues: number,
        valueDivisor: number,
        numberOfDecimalPlacesRatios: number,
        numberFormatValues: string,
        numberFormatRatios: string,
        level: number,
        locale: string,
    ): IFinancialTableData[] {
        const financialData: IFinancialTableData[] = [];
        let levelNode = level;
        let itemMappingValues: IPrintPreviewMappingValue | undefined;

        if (item.type === ItemType.EMPTY || item.type === ItemType.HEADER) {
            const itemRow: IFinancialTableData = {
                rowType: item.type !== ItemType.EMPTY ? RowTypes.HEADER_ROW : RowTypes.EMPTY_ROW,
                t: item.type !== ItemType.EMPTY ? item.caption : null,
                level: 1,
            };
            financialData.push(itemRow);
            return financialData;
        }

        itemMappingValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
            item,
            printOptions.displayOptions.showReClassInSums,
            printOptions.displayOptions.showAdjustmentInSums,
        );

        let shouldItemsAppearInForeColumn = false;
        let shouldDisplayFliTotalBelow = false;
        let shouldHideFliTotal = false;
        let shouldFliAppearInForeColumn = false;
        let shouldDisplaySumOfFliAccountsBesideIt = false;
        let accountsDirectlyMappedUnderFliSum: ICalculation | undefined;

        const flis = parentGroup.items.filter((item) => isFli(item)) as IAccountMappingTemplateGroupItemFLI[];
        const allFlisInGroupHaveNoSubflis = flis.every((fli) => {
            const subFlis = fli.accounts.filter((item) => item.type === ItemType.SUB_FLI);
            return subFlis.length === EMPTY_ARRAY_LENGTH;
        });

        if (item.type === ItemType.FLI && options.printClosingValueTotalsInSeparateColumn) {
            shouldItemsAppearInForeColumn = this.shouldBeDisplayedInForeColumn(
                item.accounts,
                options.printClosingValueTotalsInSeparateColumn,
            );
            // check if the parentGroup contains a group which is displayed -> If we have showEmptyLines off, then 0 groups shall be ignored
            let parentHasGroupThatIsDisplayed = false;

            // check if there are any groups at all
            if (parentGroup.groups.length !== EMPTY_ARRAY_LENGTH) {

                if (!hideEmptyLines) {
                    // If we DON'T hide empty lines, having groups is enough -> a group will be displayed
                    parentHasGroupThatIsDisplayed = true;

                } else {
                    // If we DO hide empty lines, we must find at least one non-zero group
                    for (const group of parentGroup.groups) {
                        const groupValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                            group,
                            printOptions.displayOptions.showReClassInSums,
                            printOptions.displayOptions.showAdjustmentInSums,
                        );

                        if (groupValues.closingValue !== 0 || groupValues.openingValue !== 0) {
                            parentHasGroupThatIsDisplayed = true;
                            break;
                        }
                    }
                }
            }

            if (allFlisInGroupHaveNoSubflis && level !== ONE && !parentHasGroupThatIsDisplayed) {
                // if fli has subflis in it or the fli is directly under the main group
                // or the group contains nested groups (which are displayed)
                // then we apply the normal fli/subfli rule and the fli does not appear
                // in the fore column
                shouldFliAppearInForeColumn = this.shouldFliBeDisplayedInForeColumn(
                    item.accounts,
                    options.printClosingValueTotalsInSeparateColumn,
                    flis,
                    printOptions,
                );
            }
            shouldDisplayFliTotalBelow = this.shouldDisplayFliTotalBelow(
                item.accounts,
                item,
                options.printClosingValueTotalsInSeparateColumn,
                shouldFliAppearInForeColumn,
            );
            shouldHideFliTotal = this.shouldHideFliTotal(item.accounts, item);

            if (shouldDisplayFliTotalBelow) {
                // this checks if there is accounts directly mapped under the fli
                // and in this case then we should add the sum of these accounts beside the fli
                shouldDisplaySumOfFliAccountsBesideIt = this.shouldDisplaySumOfFliAccountsBesideIt(item);
                accountsDirectlyMappedUnderFliSum = this.calculateSumOfAccountsDirectlyUnderFli(item);
            }
        }

        // -if show fli total is off and the item is not a subtotal and the item has subFli in it then the previous
        // and current rows will be empty
        if (
            !printOptions.displayOptions.showFliTotal &&
            item.type === ItemType.FLI &&
            item.accounts.find((item) => item.type === ItemType.SUB_FLI) != null
        ) {
            showFliTotal = false;
        } else {
            showFliTotal = true;
        }

        const previousValue =
            itemMappingValues != null && itemMappingValues.openingValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(itemMappingValues.openingValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;
        const currentValue =
            itemMappingValues != null && itemMappingValues.closingValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(itemMappingValues.closingValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;

        const formattedPreviousValue =
            previousValue != null
                ? this.aumFinancialNumberPipe.transform(
                    previousValue,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                );

        const formattedCurrentValue =
            currentValue != null
                ? this.aumFinancialNumberPipe.transform(
                    currentValue,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                );

        const formattedAccountsDirectlyMappedUnderFliPreviousSum =
            accountsDirectlyMappedUnderFliSum?.calculatedOpeningBalance != null
                ? this.aumFinancialNumberPipe.transform(
                      PdfTronMapperHelper.scaleValue(
                          accountsDirectlyMappedUnderFliSum?.calculatedOpeningBalance,
                          valueDivisor,
                      ),
                      numberFormatValues,
                      printOptions.displayOptions.nonBreakingHyphen,
                      locale,
                  )
                : this.aumFinancialNumberPipe.transform(
                      ZERO_NUMBER,
                      numberFormatValues,
                      printOptions.displayOptions.nonBreakingHyphen,
                      locale,
                  );

        const formattedAccountsDirectlyMappedUnderFliCurrentSum =
            accountsDirectlyMappedUnderFliSum?.calculatedClosingBalance != null
                ? this.aumFinancialNumberPipe.transform(
                      PdfTronMapperHelper.scaleValue(
                          accountsDirectlyMappedUnderFliSum?.calculatedClosingBalance,
                          valueDivisor,
                      ),
                      numberFormatValues,
                      printOptions.displayOptions.nonBreakingHyphen,
                      locale,
                  )
                : this.aumFinancialNumberPipe.transform(
                      ZERO_NUMBER,
                      numberFormatValues,
                      printOptions.displayOptions.nonBreakingHyphen,
                      locale,
                  );

        const itemRow: IFinancialTableData = {
            rowType: RowTypes.ROW,
            t: item.caption,
            pV:
                shouldDisplaySumOfFliAccountsBesideIt && !noPrevious
                    ? formattedAccountsDirectlyMappedUnderFliPreviousSum
                    : noPrevious || shouldDisplayFliTotalBelow || shouldHideFliTotal
                        ? undefined
                        : formattedPreviousValue,
            cV: shouldDisplaySumOfFliAccountsBesideIt
                ? formattedAccountsDirectlyMappedUnderFliCurrentSum
                : shouldDisplayFliTotalBelow || shouldHideFliTotal || shouldFliAppearInForeColumn
                    ? undefined
                    : formattedCurrentValue,
            cF: shouldFliAppearInForeColumn ? formattedCurrentValue : undefined,
            level: levelNode,
        };
        this.applyStatementFxValues(
            itemRow,
            item,
            numberFormatValues,
            valueDivisor,
            printOptions.displayOptions.nonBreakingHyphen,
            noPrevious,
        );

        if (item.type === ItemType.FLI) {
            itemRow.disableNumbering = item.disableNumbering;
        }

        if (printOptions.displayOptions.showNotes && item.type === ItemType.FLI) {
            itemRow.r = AccountMappingDisplayHelper.getAttachmentReferencesOfFli(
                item,
                balanceIncomeAttachmentReferences,
            );
        }

        let previousRatioValue: number | undefined;
        let currentRatioValue: number | undefined;
        let accountsDirectlyUnderFliPreviousRatioValues: number | undefined;
        let accountsDirectlyUnderFliCurrentRatioValues: number | undefined;

        if (printOptions.displayOptions.showRatios) {
            const itemRatioValues = this.accountMappingDisplayHelper.getOpeningAndClosingRatiosValues(
                itemMappingValues.openingValue,
                itemMappingValues.closingValue,
                referenceValuesForRatioCalculation,
                noPrevious,
            );

            const accountsDirectlyUnderFliRatioValues =
                this.accountMappingDisplayHelper.getOpeningAndClosingRatiosValues(
                    accountsDirectlyMappedUnderFliSum?.calculatedOpeningBalance,
                    accountsDirectlyMappedUnderFliSum?.calculatedClosingBalance,
                    referenceValuesForRatioCalculation,
                    noPrevious,
                );

            previousRatioValue =
                itemRatioValues.openingRatioValue != null
                    ? NumberUtils.roundNumber(itemRatioValues.openingRatioValue, false, numberOfDecimalPlacesRatios)
                    : null;
            currentRatioValue =
                itemRatioValues.closingRatioValue != null
                    ? NumberUtils.roundNumber(itemRatioValues.closingRatioValue, false, numberOfDecimalPlacesRatios)
                    : null;

            accountsDirectlyUnderFliPreviousRatioValues =
                accountsDirectlyUnderFliRatioValues?.openingRatioValue != null
                    ? NumberUtils.roundNumber(
                        accountsDirectlyUnderFliRatioValues?.openingRatioValue,
                        false,
                        numberOfDecimalPlacesRatios,
                    )
                    : null;
            accountsDirectlyUnderFliCurrentRatioValues =
                accountsDirectlyUnderFliRatioValues?.closingRatioValue != null
                    ? NumberUtils.roundNumber(
                        accountsDirectlyUnderFliRatioValues?.closingRatioValue,
                        false,
                        numberOfDecimalPlacesRatios,
                    )
                    : null;

            itemRow.pP =
                shouldDisplaySumOfFliAccountsBesideIt && !noPrevious
                    ? this.aumFinancialNumberPipe.transform(
                        accountsDirectlyUnderFliPreviousRatioValues,
                        numberFormatRatios,
                        printOptions.displayOptions.nonBreakingHyphen,
                        locale,
                    )
                    : noPrevious || !showFliTotal || shouldDisplayFliTotalBelow || shouldHideFliTotal
                        ? undefined
                        : this.aumFinancialNumberPipe.transform(
                            previousRatioValue,
                            numberFormatRatios,
                            printOptions.displayOptions.nonBreakingHyphen,
                            locale,
                        );
            itemRow.cP = shouldDisplaySumOfFliAccountsBesideIt
                ? this.aumFinancialNumberPipe.transform(
                    accountsDirectlyUnderFliCurrentRatioValues,
                    numberFormatRatios,
                    printOptions.displayOptions.nonBreakingHyphen,
                    locale,
                )
                : shouldDisplayFliTotalBelow || !showFliTotal || shouldHideFliTotal
                    ? undefined
                    : this.aumFinancialNumberPipe.transform(
                        currentRatioValue,
                        numberFormatRatios,
                        printOptions.displayOptions.nonBreakingHyphen,
                        locale,
                    );
        }

        if (item.type !== ItemType.SUB_TOTAL) {
            const isItemHasNonZeroValue = this.isItemHasNonZeroValue(currentValue, previousValue, printOptions.displayOptions.hidePreviousYear);
            const hasNonZeroSubFli =
                item.type === ItemType.FLI &&
                this.hasNonZeroSubFli(item as IAccountMappingTemplateGroupItemFLI, printOptions);

            if (item.type === ItemType.FLI && printOptions.displayOptions.showAccounts && !this.isFliHasNoChild(item as IAccountMappingTemplateGroupItemFLI) && !this.isEachFliAccountZeroValue(item as IAccountMappingTemplateGroupItemFLI, printOptions.displayOptions.hidePreviousYear)) {
                // Display FlI row when accounts are shown and FLI has child accounts with non-zero values even if they sum up to 0
                itemRow.level = levelNode;
                financialData.push(itemRow);
            } else if ((hideEmptyLines && (isItemHasNonZeroValue || hasNonZeroSubFli)) || !hideEmptyLines) {
                itemRow.level = levelNode;
                financialData.push(itemRow);
            }

            // Sort the item's accounts in-place using the helper
            if (printOptions.displayOptions.applyGroupingMasking && annualReport.groupingAndMasking?.sorting) {
                GroupingPathHelper.applySortingStateToItem(item, annualReport.groupingAndMasking.sorting);
            }

            const mappingAndReclassesAccounts: IMappingAndReclassesAccounts = {
                mappedAccounts: item.accounts,
                reclassesAccounts: item.reclassesAccounts,
            };

            const accountsFinancialData = this.mapAccountsToFinancialTableData(
                mappingAndReclassesAccounts,
                printOptions.displayOptions,
                noPrevious,
                referenceValuesForRatioCalculation,
                shouldItemsAppearInForeColumn,
                shouldDisplayFliTotalBelow,
                options.printClosingValueTotalsInSeparateColumn,
                balanceIncomeAttachmentReferences,
                PdfTronMapperHelper.getCurrencyLabel(
                    annualReport.generalInformation.currency?.name,
                    printOptions.displayOptions.finStatementRoundToThousands,
                ),
                levelNode + 1,
                annualReport.groupingAndMasking
            );
            financialData.push(...accountsFinancialData);
        } else {
            // Check if this subtotal is hidden (grouping/masking feature).
            // The stored path uses sanitizeId() which replaces spaces/special chars with '_'
            // (e.g. "Result before Tax" → "Result_before_Tax"), so we must sanitize the
            // item name before comparing, otherwise names with spaces never match.
            const groupingMasking = annualReport.groupingAndMasking;
            const isHidden = printOptions.displayOptions.applyGroupingMasking &&
                groupingMasking?.hiddenSubtotals &&
                groupingMasking.hiddenSubtotals.some(hiddenPath =>
                    hiddenPath.includes(`__subtotal__${GroupingPathHelper.sanitizeId(item.name || item.caption)}`)
                );

            if (!isHidden) {
                const subtotalFinancialData = this.mapSubtotalItemToFinancialTableData(
                    item,
                    noPrevious,
                    printOptions.displayOptions,
                    referenceValuesForRatioCalculation,
                    locale,
                    levelNode, //not level +1 because it must have the same as the flis
                );
                financialData.push(subtotalFinancialData);
            }
        }
        if (shouldDisplayFliTotalBelow && options.printClosingValueTotalsInSeparateColumn && ((hideEmptyLines && this.isItemHasNonZeroValue(item.calculatedClosingBalance, item.calculatedOpeningBalance, printOptions.displayOptions.hidePreviousYear)) || !hideEmptyLines)) {
            const extraFliTotalRow = cloneDeep<IFinancialTableData>(itemRow);
            extraFliTotalRow.rowType = RowTypes.ROW_TOTAL;
            extraFliTotalRow.t = undefined;
            extraFliTotalRow.r = undefined;
            extraFliTotalRow.cV = formattedCurrentValue;
            extraFliTotalRow.cF = undefined;
            extraFliTotalRow.pV = !noPrevious ? formattedPreviousValue : undefined;
            extraFliTotalRow.pP =
                printOptions.displayOptions.showRatios && !noPrevious
                    ? this.aumFinancialNumberPipe.transform(
                        previousRatioValue,
                        numberFormatRatios,
                        printOptions.displayOptions.nonBreakingHyphen,
                    )
                    : undefined;
            extraFliTotalRow.cP = printOptions.displayOptions.showRatios
                ? this.aumFinancialNumberPipe.transform(
                    currentRatioValue,
                    numberFormatRatios,
                    printOptions.displayOptions.nonBreakingHyphen,
                )
                : undefined;
            financialData.push(extraFliTotalRow);
        }
        return financialData;
    }

    /**
     * FX (REV-21512): display-currency twin cells for a stamped node — formatted like cV/pV,
     * empty when the node carries no stamp. No-op unless the report prints FX statement columns.
     * `includeReserve` adds the Umrechnungsreserve on top — the equity and liabilities totals
     * only balance against the assets side in the display currency once it is included (03d).
     */
    private applyStatementFxValues(
        row: IFinancialTableData,
        node: unknown,
        numberFormatValues: string,
        valueDivisor: number,
        nonBreakingHyphen: boolean,
        noPrevious: boolean,
        includeReserve = false,
    ): void {
        if (this.statementFx == null) {
            return;
        }
        const stamp = (node as { __fx?: IFxNodeStampValues } | undefined)?.__fx;
        const reserveCY = includeReserve ? this.statementFx.reserveCY : 0;
        const reservePY = includeReserve ? this.statementFx.reservePY : 0;
        row.cV2 =
            stamp?.displayCY != null && reserveCY != null
                ? this.aumFinancialNumberPipe.transform(
                    PdfTronMapperHelper.scaleValue(stamp.displayCY + reserveCY, valueDivisor),
                    numberFormatValues,
                    nonBreakingHyphen,
                )
                : EMPTY_STRING;
        row.pV2 = noPrevious
            ? undefined
            : stamp?.displayPY != null && reservePY != null
                ? this.aumFinancialNumberPipe.transform(
                    PdfTronMapperHelper.scaleValue(stamp.displayPY + reservePY, valueDivisor),
                    numberFormatValues,
                    nonBreakingHyphen,
                )
                : EMPTY_STRING;
    }

    /**
     * FX (REV-21512): the Umrechnungsreserve exists only in the display currency — it is what
     * makes the translated liabilities side balance (03d). Inserted as an item row before «Total
     * Eigenkapital» (equity is the liabilities side's last subgroup, so its total is the last
     * GROUP_TOTAL row), and that total's display cells are rewritten to include it.
     */
    private insertFxReserveRow(
        liabilityRows: IFinancialTableData[],
        liabilitiesGroup: SortedMappingItem,
        equityGroupName: string | null | undefined,
        numberFormatValues: string,
        valueDivisor: number,
        nonBreakingHyphen: boolean,
        noPrevious: boolean,
        hideEmptyLines: boolean,
    ): void {
        if (this.statementFx == null) {
            return;
        }
        const formatReserve = (value: number | null): string =>
            value != null
                ? this.aumFinancialNumberPipe.transform(
                    PdfTronMapperHelper.scaleValue(value, valueDivisor),
                    numberFormatValues,
                    nonBreakingHyphen,
                )
                : EMPTY_STRING;
        // The functional cells stay empty: the reserve only exists in the display currency.
        const reserveRow: IFinancialTableData = {
            rowType: RowTypes.ROW,
            t: this.fxReserveWord,
            pV: noPrevious ? undefined : EMPTY_STRING,
            cV: EMPTY_STRING,
            cV2: formatReserve(this.statementFx.reserveCY),
            pV2: noPrevious ? undefined : formatReserve(this.statementFx.reservePY),
        };
        // «Leere Zeilen ausblenden» drops a reserve that shows 0 (or nothing) in every year, like any
        // other empty row. The equity total is still rewritten below — it includes the exact reserve.
        const isBlankOrZero = (cell: string | undefined): boolean =>
            cell == null ||
            cell === EMPTY_STRING ||
            NumberUtils.isZero(this.aumFinancialNumberPipe.parseFormattedNumber(cell, nonBreakingHyphen));
        const showReserveRow = !(hideEmptyLines && isBlankOrZero(reserveRow.cV2) && isBlankOrZero(reserveRow.pV2));
        // Equity is identified by the template's `equityGroupName` — the marker the Equity Movement
        // Schedule uses — for BOTH the node and its total row. Never by position: HGB lists
        // Eigenkapital FIRST among the liabilities subgroups, so "the last group total" belongs to
        // another group there and would receive equity's value plus the reserve.
        const equityNode = ((liabilitiesGroup.sortedItems ?? []) as SortedMappingItem[]).find(
            (item) => this.isGroup(item) && item.name === equityGroupName,
        );
        // Its total row carries exactly the title the group-total builder composed for that node.
        const equityTotalTitle = equityNode != null ? this.totalWord + SPACE + equityNode.totalName : null;
        const equityTotalIndex =
            equityTotalTitle == null
                ? -1
                : liabilityRows.findIndex(
                    (row) => row.rowType === RowTypes.GROUP_TOTAL && row.t === equityTotalTitle,
                );
        if (equityTotalIndex < 0) {
            // No identifiable equity total (group totals off, hidden group, or a user subtotal took
            // its place): append the reserve rather than fold it into a different group's total. The
            // Passiven table total still includes it — that one is enriched at its own call site.
            if (showReserveRow) {
                liabilityRows.push(reserveRow);
            }

            return;
        }
        const equityTotalRow = liabilityRows[equityTotalIndex];
        if (showReserveRow) {
            reserveRow.level = (equityTotalRow.level ?? 0) + 1;
            liabilityRows.splice(equityTotalIndex, 0, reserveRow);
        }
        this.applyStatementFxValues(
            equityTotalRow,
            equityNode,
            numberFormatValues,
            valueDivisor,
            nonBreakingHyphen,
            noPrevious,
            true,
        );
    }

    private mapSortedItemsToFinancialTableData(
        parentGroup: IAccountMappingTemplateGroup,
        sortedItems: SortedMappingItem[],
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        balanceIncomeAttachmentReferences: Map<string, string>,
        referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation,
        options: IOptions,
        level: number,
        locale: string,
    ): IFinancialTableData[] {
        const financialData: IFinancialTableData[] = [];

        if (printOptions == null || annualReport.generalInformation == null) {
            throw new Error('Print options or general information is null or undefined');
        }

        const noPrevious = annualReport.generalInformation.noPreviousData || printOptions.displayOptions.hidePreviousYear;
        const hideEmptyLines = printOptions.displayOptions.hideEmptyLines;

        let showFliTotal = true;
        const {
            numberOfDecimalPlacesValues,
            numberFormatValues,
            numberOfDecimalPlacesRatios,
            numberFormatRatios,
            valueDivisor,
        } = this.getNumberFormatAndDecimalPlaces(
            printOptions.displayOptions,
            printOptions.displayOptions.finStatementHideDecimalPlacesValues,
            printOptions.displayOptions.finStatementHideDecimalPlacesRatios,
            printOptions.displayOptions.finStatementRoundToThousands,
        );

        for (const sortedItem of sortedItems) {
            if (this.isGroup(sortedItem)) {
                let levelNode = level;
                let groupTitleRow = undefined;

                if ((sortedItem as IAccountMappingTemplateGroup).isHeader) {
                    const financialItem: IFinancialTableData = {
                        rowType: RowTypes.HEADER_ROW,
                        t: sortedItem.header,
                        level: 1,
                    };

                    financialItem.disableNumbering = sortedItem.disableNumbering;
                    financialData.push(financialItem);
                } else if (sortedItem.isEmpty) {
                    const financialItem: IFinancialTableData = {
                        rowType: RowTypes.EMPTY_ROW,
                        t: undefined,
                    };
                    financialData.push(financialItem);
                } else {
                    if (printOptions.displayOptions.showGroupHeaders && !sortedItem.isHiddenGroup) {
                        const groupTitle: IFinancialTableData = {
                            rowType: RowTypes.GROUP_HEADER,
                            t: sortedItem.title,
                            level: levelNode,
                        };
                        groupTitleRow = groupTitle;
                        groupTitle.disableNumbering = sortedItem.disableNumbering;
                        financialData.push(groupTitle);
                    }

                    if (sortedItem.sortedItems != null) {
                        const groupsFinancialData = this.mapSortedItemsToFinancialTableData(
                            sortedItem,
                            sortedItem.sortedItems as SortedMappingItem[],
                            annualReport,
                            printOptions,
                            balanceIncomeAttachmentReferences,
                            referenceValuesForRatioCalculation,
                            options,
                            levelNode + 1,
                            locale,
                        );

                        financialData.push(...groupsFinancialData);
                    }

                    //If groupTitleRow is last item, that means there is nothing displayed between the group title and the sum.
                    const groupIsEmpty =
                        financialData.length == 0 ? false : financialData[financialData.length - 1] === groupTitleRow;
                    if (groupIsEmpty) financialData.pop();

                    if (!groupIsEmpty) {
                        const groupMappingValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                            sortedItem,
                            printOptions.displayOptions.showReClassInSums,
                            printOptions.displayOptions.showAdjustmentInSums,
                        );

                        const previousValue =
                            groupMappingValues.openingValue != null
                                ? NumberUtils.roundNumber(
                                    PdfTronMapperHelper.scaleValue(groupMappingValues.openingValue, valueDivisor),
                                    false,
                                    numberOfDecimalPlacesValues,
                                )
                                : null;
                        const currentValue =
                            groupMappingValues.closingValue != null
                                ? NumberUtils.roundNumber(
                                    PdfTronMapperHelper.scaleValue(groupMappingValues.closingValue, valueDivisor),
                                    false,
                                    numberOfDecimalPlacesValues,
                                )
                                : null;

                        const formattedPreviousValue =
                            previousValue != null
                                ? this.aumFinancialNumberPipe.transform(
                                    previousValue,
                                    numberFormatValues,
                                    printOptions.displayOptions.nonBreakingHyphen,
                                )
                                : null;
                        const formattedCurrentValue =
                            currentValue != null
                                ? this.aumFinancialNumberPipe.transform(
                                    currentValue,
                                    numberFormatValues,
                                    printOptions.displayOptions.nonBreakingHyphen,
                                )
                                : null;

                        // check if we should add extra row according to the group/fli fore column rules
                        // and if yes then the group total text should not appear so set it as undefined
                        // otherwise if we are applying the normal fli/subfli rules then the group total
                        // should appear as normal
                        const flis = sortedItem.items.filter((item) =>
                            isFli(item),
                        ) as IAccountMappingTemplateGroupItemFLI[];
                        const shouldDisplayGroupTotalBelowWithoutName = options.printClosingValueTotalsInSeparateColumn
                            ? this.shouldDisplayGroupTotalBelowWithoutName(flis, printOptions)
                            : false;

                        const groupTotalRow: IFinancialTableData = {
                            rowType: RowTypes.GROUP_TOTAL,
                            t:
                                !shouldDisplayGroupTotalBelowWithoutName && !sortedItem.isHiddenGroup
                                    ? this.totalWord + SPACE + sortedItem.totalName
                                    : undefined,
                            pV: noPrevious ? undefined : formattedPreviousValue,
                            cV: formattedCurrentValue,
                            level: levelNode,
                        };
                        this.applyStatementFxValues(
                            groupTotalRow,
                            sortedItem,
                            numberFormatValues,
                            valueDivisor,
                            printOptions.displayOptions.nonBreakingHyphen,
                            noPrevious,
                        );

                        if (printOptions.displayOptions.showNotes) {
                            groupTotalRow.r = null;
                        }

                        if (printOptions.displayOptions.showRatios) {
                            const groupRatioValues = this.accountMappingDisplayHelper.getOpeningAndClosingRatiosValues(
                                groupMappingValues.openingValue,
                                groupMappingValues.closingValue,
                                referenceValuesForRatioCalculation,
                                noPrevious,
                            );

                            const previousRatioValue =
                                groupRatioValues.openingRatioValue != null
                                    ? NumberUtils.roundNumber(
                                        groupRatioValues.openingRatioValue,
                                        false,
                                        numberOfDecimalPlacesRatios,
                                    )
                                    : null;
                            const currentRatioValue =
                                groupRatioValues.closingRatioValue != null
                                    ? NumberUtils.roundNumber(
                                        groupRatioValues.closingRatioValue,
                                        false,
                                        numberOfDecimalPlacesRatios,
                                    )
                                    : null;

                            groupTotalRow.pP = noPrevious
                                ? undefined
                                : this.aumFinancialNumberPipe.transform(
                                    previousRatioValue,
                                    numberFormatRatios,
                                    printOptions.displayOptions.nonBreakingHyphen,
                                );
                            groupTotalRow.cP = this.aumFinancialNumberPipe.transform(
                                currentRatioValue,
                                numberFormatRatios,
                                printOptions.displayOptions.nonBreakingHyphen,
                            );
                        }
                        if (
                            shouldDisplayGroupTotalBelowWithoutName ||
                            flis.length === EMPTY_ARRAY_LENGTH ||
                            !options.printClosingValueTotalsInSeparateColumn
                        ) {
                            if (sortedItem.isHiddenGroup) {
                                groupTotalRow["fromHiddenGroup"] = true; // add a custom property to identify that this total is from a hidden group, this will be used in the pdf component to decide whether to show the total or not according to the user selection
                            }
                            if (shouldDisplayGroupTotalBelowWithoutName) {
                                financialData.push(groupTotalRow);
                            } else if (sortedItem.hideTotal == false) {
                                financialData.push(groupTotalRow);
                            }
                        }

                        // If hide empty lines & group has no accounts, do not show it
                        if (hideEmptyLines && sortedItem.calculatedClosingBalance === 0 && sortedItem.calculatedOpeningBalance === 0) {
                            let hasAccounts = false;
                            for (const item of sortedItem.sortedItems) {
                                if (Array.isArray(item)) {
                                    // Check nested array items
                                    for (const nestedItem of item) {
                                        if (isAccount(nestedItem)) {
                                            hasAccounts = true;
                                            break;
                                        }
                                    }
                                } else if (isAccount(item)) {
                                    hasAccounts = true;
                                    break;
                                }
                                if (hasAccounts) break;
                            }
                            if (!hasAccounts && financialData.length > 0) {
                                // Also make sure that whatever we are popping is actually a 0 row
                                const lastRow = financialData[financialData.length - 1];
                                const nonBreakingHyphen = printOptions.displayOptions.nonBreakingHyphen;

                                const currentYearValueIsZero = lastRow.cV == null ||
                                    NumberUtils.isZero(this.aumFinancialNumberPipe.parseFormattedNumber(lastRow.cV, nonBreakingHyphen));
                                const previousYearValueIsZero = noPrevious || lastRow.pV == null ||
                                    NumberUtils.isZero(this.aumFinancialNumberPipe.parseFormattedNumber(lastRow.pV, nonBreakingHyphen));
                                const currentYearThirdColIsZero = lastRow.cF == null ||
                                    NumberUtils.isZero(this.aumFinancialNumberPipe.parseFormattedNumber(lastRow.cF, nonBreakingHyphen));

                                const isZeroRow = currentYearValueIsZero && previousYearValueIsZero && currentYearThirdColIsZero;
                                if (isZeroRow) {
                                    financialData.pop();
                                }
                            }
                        }
                    }
                }
            } else {
                financialData.push(
                    ...this.mapItemToFinancialTableData(
                        parentGroup,
                        sortedItem,
                        annualReport,
                        printOptions,
                        balanceIncomeAttachmentReferences,
                        referenceValuesForRatioCalculation,
                        options,
                        showFliTotal,
                        noPrevious,
                        hideEmptyLines,
                        numberOfDecimalPlacesValues,
                        valueDivisor,
                        numberOfDecimalPlacesRatios,
                        numberFormatValues,
                        numberFormatRatios,
                        level,
                        locale,
                    ),
                );
            }
        }
        return financialData;
    }

    private shouldFliBeDisplayedInForeColumn(
        items: AccountMappingAccountItem[],
        printClosingValueTotalsInSeparateColumn: boolean,
        flis: IAccountMappingTemplateGroupItemFLI[],
        printOptions: IPrintOptions,
    ): boolean {
        if (!printClosingValueTotalsInSeparateColumn) {
            return false;
        }

        // If "Show Zero FLIs" is off (hideEmptyLines is true), filter out zero-value FLIs
        const hideEmptyLines = printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.finStatementHideEmptyLines;
        const relevantFlis = hideEmptyLines
            ? flis.filter((fli) => {
                const fliValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                    fli,
                    printOptions.displayOptions.showReClassInSums,
                    printOptions.displayOptions.showAdjustmentInSums,
                );
                return this.isItemHasNonZeroValue(fliValues.closingValue, fliValues.openingValue, printOptions.displayOptions.hidePreviousYear) ||
                    this.hasNonZeroSubFli(fli, printOptions);
            })
            : flis;

        const numberOfRelevantFlis = relevantFlis.length;

        // if fli has of that items in it and not subflis then we apply the group/fli rule and
        // the fli appears in the fore column
        // if multiple flis in the same group with of that items in them or multiple flis without
        // of that items or sub flis in them then they should appear in fore column
        if (items.length > ONE) {
            const subFlis = items.filter((item) => item.type === ItemType.SUB_FLI);
            if (subFlis.length >= ONE) {
                return false;
            }
            const ofThatItems = items.filter((item) => item.type === ItemType.OF_THAT_ITEM);
            if (ofThatItems.length >= ONE) {
                if (!hideEmptyLines) {
                    return true;
                }

                const hasNonZeroOfThat = ofThatItems.some((item) =>
                    this.isItemHasNonZeroValue(item.calculatedClosingBalance, item.calculatedOpeningBalance, printOptions.displayOptions.hidePreviousYear),
                );

                if (hasNonZeroOfThat) {
                    return true;
                }
            }
        }

        return numberOfRelevantFlis > ONE;
    }

    private shouldBeDisplayedInForeColumn(
        items: AccountMappingAccountItem[],
        printClosingValueTotalsInSeparateColumn: boolean,
    ): boolean {
        if (!printClosingValueTotalsInSeparateColumn) {
            return false;
        }

        if (items.length > ONE) {
            const subFlis = items.filter((item) => item.type === ItemType.SUB_FLI);
            return subFlis.length > ONE;
        }

        return false;
    }

    private shouldDisplayGroupTotalBelowWithoutName(flis: IAccountMappingTemplateGroupItemFLI[], printOptions: IPrintOptions): boolean {
        // if a group has no flis then it's total should appear with a total name normally
        if (flis.length === EMPTY_ARRAY_LENGTH) {
            return false;
        }

        const allFlisInGroupHaveNoSubflis = flis.every((fli) => {
            const subFlis = fli.accounts.filter((item) => item.type === ItemType.SUB_FLI);
            return subFlis.length === 0;
        });

        // If "Show Zero FLIs" is off (hideEmptyLines is true), filter out zero-value FLIs
        const hideEmptyLines = printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.finStatementHideEmptyLines;
        const relevantFlis = hideEmptyLines
            ? flis.filter((fli) => {
                const fliValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                    fli,
                    printOptions.displayOptions.showReClassInSums,
                    printOptions.displayOptions.showAdjustmentInSums,
                );
                return this.isItemHasNonZeroValue(fliValues.closingValue, fliValues.openingValue, printOptions.displayOptions.hidePreviousYear) ||
                    this.hasNonZeroSubFli(fli, printOptions);
            })
            : flis;

        if (allFlisInGroupHaveNoSubflis && relevantFlis.length === ONE) {
            const ofThatItems = relevantFlis[FIRST_ELEMENT_INDEX].accounts.filter(
                (item) => item.type === ItemType.OF_THAT_ITEM,
            );
            return ofThatItems.length > EMPTY_ARRAY_LENGTH;
        }

        return allFlisInGroupHaveNoSubflis;
    }

    private shouldDisplayFliTotalBelow(
        items: AccountMappingAccountItem[],
        fli: IAccountMappingTemplateGroupItemFLI,
        printClosingValueTotalsInSeparateColumn: boolean,
        shouldFliBeDisplayedInForeColumn: boolean,
    ): boolean {
        const shouldDisplayItemsInForeColumn = this.shouldBeDisplayedInForeColumn(
            items,
            printClosingValueTotalsInSeparateColumn,
        );

        if (shouldFliBeDisplayedInForeColumn) {
            return false;
        }

        if (!shouldDisplayItemsInForeColumn) {
            const subFlis = items.filter((item) => item.type === ItemType.SUB_FLI);
            if (subFlis.length === ONE) {
                const item = subFlis[FIRST_ELEMENT_INDEX] as IAccountMappingSubFLI;
                if (
                    fli.calculatedClosingBalance === item.calculatedClosingBalance &&
                    fli.calculatedOpeningBalance === item.calculatedOpeningBalance
                ) {
                    return false;
                } else {
                    return true;
                }
            }
        }

        return shouldDisplayItemsInForeColumn;
    }

    private shouldHideFliTotal(items: AccountMappingAccountItem[], fli: IAccountMappingTemplateGroupItemFLI): boolean {
        const subFlis = items.filter((item) => item.type === ItemType.SUB_FLI);
        if (subFlis.length === ONE) {
            const item = subFlis[FIRST_ELEMENT_INDEX] as IAccountMappingSubFLI;
            if (
                fli.calculatedClosingBalance === item.calculatedClosingBalance &&
                fli.calculatedOpeningBalance === item.calculatedOpeningBalance
            ) {
                return true;
            } else {
                return false;
            }
        }
        return false;
    }

    private shouldDisplaySumOfFliAccountsBesideIt(fli: IAccountMappingTemplateGroupItemFLI): boolean {
        // if the fli has accounts directly mapped under it then we should display the value
        // of these accounts beside the fli in the main column
        const accounts = fli.accounts.filter(
            (item) =>
                item.type !== ItemType.EMPTY &&
                item.type !== ItemType.SUB_FLI &&
                item.type !== ItemType.OF_THAT_ITEM &&
                item.type !== ItemType.HEADER,
        );

        return accounts.length > EMPTY_ARRAY_LENGTH;
    }

    private calculateSumOfAccountsDirectlyUnderFli(fli: IAccountMappingTemplateGroupItemFLI): ICalculation {
        let sum: ICalculation = {
            calculatedClosingBalance: 0,
            calculatedOpeningBalance: 0,
        };
        const accounts = fli.accounts.filter(
            (item) =>
                item.type !== ItemType.EMPTY &&
                item.type !== ItemType.SUB_FLI &&
                item.type !== ItemType.OF_THAT_ITEM &&
                item.type !== ItemType.HEADER,
        ) as Array<IAccount | IManualAddedOrUpdatedAccount>;

        accounts.forEach((account) => {
            sum.calculatedClosingBalance = NumberUtils.sumIgnoringInfiniteNumbers(
                [sum.calculatedClosingBalance, account.calculatedClosingBalance],
                0,
            );

            sum.calculatedOpeningBalance = NumberUtils.sumIgnoringInfiniteNumbers(
                [sum.calculatedOpeningBalance, account.calculatedOpeningBalance],
                0,
            );
        });

        return sum;
    }

    private mapAccountsToFinancialTableData(
        accounts: IMappingAndReclassesAccounts,
        printDisplayOptions: IDisplayOptions,
        noPrevious: boolean,
        referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation,
        shouldItemsAppearInForeColumn: boolean,
        shouldDisplayFliTotalBelow: boolean,
        printClosingValueTotalsInSeparateColumn: boolean,
        balanceIncomeAttachmentReferences: Map<string, string>,
        currency: string,
        level: number,
        groupingMasking: IGroupingMasking,
    ): IFinancialTableData[] {
        const accountsFinancialData: IFinancialTableData[] = [];
        let showFliTotal = true;
        const hideEmptyLines = printDisplayOptions.hideEmptyLines || printDisplayOptions.finStatementHideEmptyLines;

        const {
            numberOfDecimalPlacesValues,
            numberFormatValues,
            numberOfDecimalPlacesRatios,
            numberFormatRatios,
            valueDivisor,
        } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.finStatementHideDecimalPlacesValues,
            printDisplayOptions.finStatementHideDecimalPlacesRatios,
            printDisplayOptions.finStatementRoundToThousands,
        );

        const allMappedAccounts = accounts.mappedAccounts;
        const updatedAccounts = this.accountMappingDisplayHelper.applyPrintOptionsOnAccountsAndSubFlis(printDisplayOptions, {
            mappedAccounts: allMappedAccounts,
            reclassesAccounts: accounts.reclassesAccounts,
        });

        // Prepare accounts with ratios for lookup (only regular accounts, not SubFLIs/OF_THAT_ITEMs/EMPTY/HEADER)
        const regularAccounts = updatedAccounts.filter(
            (account) =>
                account.type !== ItemType.SUB_FLI &&
                account.type !== ItemType.OF_THAT_ITEM &&
                account.type !== ItemType.EMPTY &&
                account.type !== ItemType.HEADER,
        );

        const accountsWithRatios = this.accountMappingDisplayHelper.calculateAccountsRatios(
            regularAccounts,
            referenceValuesForRatioCalculation,
        );

        // Create a map for quick lookup of accounts with ratios by index (preserving order correspondence)
        // We use index-based mapping since the order of regularAccounts corresponds to accountsWithRatios
        const accountsWithRatiosMap = new Map<number, any>();
        const regularAccountsIndexMap = new Map<any, number>();

        regularAccounts.forEach((account, index) => {
            regularAccountsIndexMap.set(account, index);
        });

        accountsWithRatios.forEach((child, index) => {
            if (isAccountWithRatio(child)) {
                accountsWithRatiosMap.set(index, child);
            }
        });

        // Process all items in their original order
        for (const item of updatedAccounts) {
            if (item.type === ItemType.EMPTY || item.type === ItemType.HEADER) {
                // Handle EMPTY and HEADER items
                if (printDisplayOptions.showAccounts) {
                    const itemSubRow: IFinancialTableData = {
                        rowType: item.type !== ItemType.EMPTY ? RowTypes.HEADER_ROW : RowTypes.EMPTY_ROW,
                        t: item.type !== ItemType.EMPTY ? item.caption : null,
                        level: 1,
                        id: item.name,
                    };
                    accountsFinancialData.push(itemSubRow);
                }
            } else if (item.type === ItemType.SUB_FLI || item.type === ItemType.OF_THAT_ITEM) {
                // Handle SUB_FLI and OF_THAT_ITEM
                const subFli = item;

                if (subFli.accounts.length === EMPTY_ARRAY_LENGTH && !printDisplayOptions.showFliTotal) {
                    showFliTotal = false;
                }

                const fliMappingValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                    subFli,
                    printDisplayOptions.showReClassInSums,
                    printDisplayOptions.showAdjustmentInSums,
                );
                const fliMappingRatioValues = this.accountMappingDisplayHelper.getOpeningAndClosingRatiosValues(
                    fliMappingValues.openingValue,
                    fliMappingValues.closingValue,
                    referenceValuesForRatioCalculation,
                    noPrevious,
                );

                const previousValue =
                    fliMappingValues.openingValue != null
                        ? NumberUtils.roundNumber(
                              PdfTronMapperHelper.scaleValue(fliMappingValues.openingValue, valueDivisor),
                              false,
                              numberOfDecimalPlacesValues,
                          )
                        : null;
                const currentValue =
                    fliMappingValues.closingValue != null
                        ? NumberUtils.roundNumber(
                              PdfTronMapperHelper.scaleValue(fliMappingValues.closingValue, valueDivisor),
                              false,
                              numberOfDecimalPlacesValues,
                          )
                        : null;

                const formattedPreviousValue =
                    previousValue != null
                        ? this.aumFinancialNumberPipe.transform(
                            previousValue,
                            numberFormatValues,
                            printDisplayOptions.nonBreakingHyphen,
                        )
                        : null;
                const formattedCurrentValue =
                    currentValue != null
                        ? this.aumFinancialNumberPipe.transform(
                            currentValue,
                            numberFormatValues,
                            printDisplayOptions.nonBreakingHyphen,
                        )
                        : null;

                let subFliSubRow: IFinancialTableData;

                const isSubFliWithTwoZeroValuesAndNoAccountMapped =
                    NumberUtils.isZero(previousValue) &&
                    NumberUtils.isZero(currentValue) &&
                    subFli.accounts.length === 0;

                if (subFli.type === ItemType.OF_THAT_ITEM) {
                    const caption: string = `${subFli.caption} ${currency} ${formattedCurrentValue} (${currency} ${formattedPreviousValue})`;
                    subFliSubRow = {
                        rowType: RowTypes.OF_THAT,
                        t: caption,
                        pV: '',
                        cV: '',
                        id: subFli.name,
                        level: level,
                    };
                } else {
                    subFliSubRow = {
                        rowType: RowTypes.SUB_ROW,
                        t: subFli.caption,
                        pV:
                            (noPrevious || !showFliTotal) && !isSubFliWithTwoZeroValuesAndNoAccountMapped
                                ? undefined
                                : formattedPreviousValue,
                        cV:
                            (!showFliTotal || shouldItemsAppearInForeColumn) &&
                                !isSubFliWithTwoZeroValuesAndNoAccountMapped
                                ? undefined
                                : formattedCurrentValue,
                        cF: shouldItemsAppearInForeColumn ? formattedCurrentValue : undefined,
                        id: subFli.name,
                        level: level,
                    };
                }

                this.applyStatementFxValues(
                    subFliSubRow,
                    subFli,
                    numberFormatValues,
                    valueDivisor,
                    printDisplayOptions.nonBreakingHyphen,
                    noPrevious,
                );
                subFliSubRow.disableNumbering = subFli.disableNumbering;

                if (printDisplayOptions.showNotes) {
                    subFliSubRow.r = AccountMappingDisplayHelper.getAttachmentReferencesOfFli(
                        subFli,
                        balanceIncomeAttachmentReferences,
                    );
                }

                if (printDisplayOptions.showRatios) {
                    const previousRatioValue =
                        fliMappingRatioValues.openingRatioValue != null
                            ? NumberUtils.roundNumber(
                                fliMappingRatioValues.openingRatioValue,
                                false,
                                numberOfDecimalPlacesRatios,
                            )
                            : null;
                    const currentRatioValue =
                        fliMappingRatioValues.closingRatioValue != null
                            ? NumberUtils.roundNumber(
                                fliMappingRatioValues.closingRatioValue,
                                false,
                                numberOfDecimalPlacesRatios,
                            )
                            : null;

                    subFliSubRow.pP =
                        (noPrevious || !showFliTotal) && !isSubFliWithTwoZeroValuesAndNoAccountMapped
                            ? undefined
                            : this.aumFinancialNumberPipe.transform(
                                previousRatioValue,
                                numberFormatRatios,
                                printDisplayOptions.nonBreakingHyphen,
                            );
                    subFliSubRow.cP =
                        !showFliTotal && !isSubFliWithTwoZeroValuesAndNoAccountMapped
                            ? undefined
                            : this.aumFinancialNumberPipe.transform(
                                currentRatioValue,
                                numberFormatRatios,
                                printDisplayOptions.nonBreakingHyphen,
                            );
                }

                const isItemHasNonZeroValue = this.isItemHasNonZeroValue(currentValue, previousValue, printDisplayOptions.hidePreviousYear);

                if ((hideEmptyLines && isItemHasNonZeroValue) || !hideEmptyLines) {
                    accountsFinancialData.push(subFliSubRow);
                }

                // REV-21798: a SubFLI can hold nothing but reclassification accounts, which live
                // on `reclassesAccounts` and never in `accounts`. Testing only `accounts` skipped
                // the whole block for such a SubFLI, so the reclass account was missing from print
                // and the print preview. The FLI-level path has no such guard, which is why this
                // only ever went wrong one level down.
                const subFliHasContent =
                    subFli.accounts.length > EMPTY_ARRAY_LENGTH ||
                    (subFli.reclassesAccounts?.length ?? EMPTY_ARRAY_LENGTH) > EMPTY_ARRAY_LENGTH;
                if (subFliHasContent) {
                    const subFliMappingAndReclassesAccounts: IMappingAndReclassesAccounts = {
                        mappedAccounts: subFli.accounts,
                        reclassesAccounts: subFli.reclassesAccounts,
                    };

                    const subFliAccountsRows = this.mapAccountsToFinancialTableData(
                        subFliMappingAndReclassesAccounts,
                        printDisplayOptions,
                        noPrevious,
                        referenceValuesForRatioCalculation,
                        shouldItemsAppearInForeColumn,
                        shouldDisplayFliTotalBelow,
                        printClosingValueTotalsInSeparateColumn,
                        balanceIncomeAttachmentReferences,
                        currency,
                        level + 1,
                        groupingMasking
                    );

                    accountsFinancialData.push(...subFliAccountsRows);
                }

                // Reset showFliTotal for the next iteration
                showFliTotal = true;
            } else {
                // Handle regular accounts
                if (printDisplayOptions.showAccounts) {
                    // Find the index of this item in updatedAccounts to get the corresponding processed account
                    const regularAccountIndex = regularAccountsIndexMap.get(item);
                    if (regularAccountIndex !== undefined) {
                        const child = accountsWithRatiosMap.get(regularAccountIndex);
                        if (child && isAccountWithRatio(child)) {
                            const previousValue =
                                child.calculatedOpeningBalance != null
                                    ? NumberUtils.roundNumber(
                                        PdfTronMapperHelper.scaleValue(child.calculatedOpeningBalance, valueDivisor),
                                        false,
                                        numberOfDecimalPlacesValues,
                                    )
                                    : null;
                            const currentValue =
                                child.calculatedClosingBalance != null
                                    ? NumberUtils.roundNumber(
                                        PdfTronMapperHelper.scaleValue(child.calculatedClosingBalance, valueDivisor),
                                        false,
                                        numberOfDecimalPlacesValues,
                                    )
                                    : null;

                            const formattedPreviousValue =
                                previousValue != null
                                    ? this.aumFinancialNumberPipe.transform(
                                        previousValue,
                                        numberFormatValues,
                                        printDisplayOptions.nonBreakingHyphen,
                                    )
                                    : null;
                            const formattedCurrentValue =
                                currentValue != null
                                    ? this.aumFinancialNumberPipe.transform(
                                        currentValue,
                                        numberFormatValues,
                                        printDisplayOptions.nonBreakingHyphen,
                                    )
                                    : null;

                            const maskedAccountName = MaskingHelper.getAccountDisplayName(
                                child,
                                undefined,
                                groupingMasking?.renamedAccounts,
                                false,
                                printDisplayOptions.showAccountNumbers,
                                printDisplayOptions.applyGroupingMasking
                            );
                            const accountName = child.type === AccountType.UNCOVERED_SHORTFALL ? '' : maskedAccountName;

                            const accountSubRow: IFinancialTableData = {
                                rowType: RowTypes.ACCOUNT,
                                t: accountName,
                                pV: noPrevious ? undefined : formattedPreviousValue,
                                cV: !printClosingValueTotalsInSeparateColumn ? formattedCurrentValue : undefined,
                                cF: printClosingValueTotalsInSeparateColumn ? formattedCurrentValue : undefined,
                                id: child.name,
                                level: level,
                            };
                            this.applyStatementFxValues(
                                accountSubRow,
                                child,
                                numberFormatValues,
                                valueDivisor,
                                printDisplayOptions.nonBreakingHyphen,
                                noPrevious,
                            );

                            if (printDisplayOptions.showNotes) {
                                accountSubRow.r = null;
                            }
                            if (printDisplayOptions.showRatios) {
                                const previousRatioValue =
                                    child.openingRatioValue != null && Number.isFinite(child.openingRatioValue)
                                        ? NumberUtils.roundNumber(
                                            child.openingRatioValue,
                                            false,
                                            numberOfDecimalPlacesRatios,
                                        )
                                        : null;
                                const currentRatioValue =
                                    child.closingRatioValue != null && Number.isFinite(child.closingRatioValue)
                                        ? NumberUtils.roundNumber(
                                            child.closingRatioValue,
                                            false,
                                            numberOfDecimalPlacesRatios,
                                        )
                                        : null;

                                accountSubRow.pP = noPrevious
                                    ? undefined
                                    : this.aumFinancialNumberPipe.transform(
                                        previousRatioValue,
                                        numberFormatRatios,
                                        printDisplayOptions.nonBreakingHyphen,
                                    );
                                accountSubRow.cP = this.aumFinancialNumberPipe.transform(
                                    currentRatioValue,
                                    numberFormatRatios,
                                    printDisplayOptions.nonBreakingHyphen,
                                );
                            }

                            const isAccountEmpty =
                                (currentValue == null || NumberUtils.isZero(currentValue)) &&
                                (noPrevious || printDisplayOptions.hidePreviousYear || previousValue == null || NumberUtils.isZero(previousValue));

                            if (!hideEmptyLines || !isAccountEmpty) {
                                accountsFinancialData.push(accountSubRow);
                            }
                        }
                    }
                }
            }
        }

        return accountsFinancialData;
    }

    private mapSubtotalItemToFinancialTableData(
        subtotal: IAccountMappingTemplateGroupItemSubtotal,
        noPrevious: boolean,
        printDisplayOptions: IDisplayOptions,
        referenceValuesForRatioCalculation: IReferenceValueForRatioCalculation,
        locale: string,
        level?: number,
    ): IFinancialTableData {
        const {
            numberOfDecimalPlacesValues,
            numberFormatValues,
            numberOfDecimalPlacesRatios,
            numberFormatRatios,
            valueDivisor,
        } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.finStatementHideDecimalPlacesValues,
            printDisplayOptions.finStatementHideDecimalPlacesRatios,
            printDisplayOptions.finStatementRoundToThousands,
        );

        const subtotalMappingValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
            subtotal,
            printDisplayOptions.showReClassInSums,
            printDisplayOptions.showAdjustmentInSums,
        );

        const subtotalMappingRatioValues = this.accountMappingDisplayHelper.getOpeningAndClosingRatiosValues(
            subtotalMappingValues.openingValue,
            subtotalMappingValues.closingValue,
            referenceValuesForRatioCalculation,
            noPrevious,
        );

        const previousValue =
            subtotalMappingValues.openingValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(subtotalMappingValues.openingValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;
        const currentValue =
            subtotalMappingValues.closingValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(subtotalMappingValues.closingValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;

        const formattedPreviousValue =
            previousValue != null
                ? this.aumFinancialNumberPipe.transform(
                    previousValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;
        const formattedCurrentValue =
            currentValue != null
                ? this.aumFinancialNumberPipe.transform(
                    currentValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;

        const subtotalFinancialData: IFinancialTableData = {
            rowType: RowTypes.SUB_TOTAL,
            t: subtotal.caption,
            pV: noPrevious ? undefined : formattedPreviousValue,
            cV: formattedCurrentValue,
            level: level,
        };

        subtotalFinancialData.disableNumbering = subtotal.disableNumbering;
        // Subtotals carry an FX stamp like every other total — without it the display cells stay empty.
        this.applyStatementFxValues(
            subtotalFinancialData,
            subtotal,
            numberFormatValues,
            valueDivisor,
            printDisplayOptions.nonBreakingHyphen,
            noPrevious,
        );

        if (printDisplayOptions.showRatios) {
            const previousRatioValue =
                subtotalMappingRatioValues.openingRatioValue != null
                    ? NumberUtils.roundNumber(
                        subtotalMappingRatioValues.openingRatioValue,
                        false,
                        numberOfDecimalPlacesRatios,
                    )
                    : null;
            const currentRatioValue =
                subtotalMappingRatioValues.closingRatioValue != null
                    ? NumberUtils.roundNumber(
                        subtotalMappingRatioValues.closingRatioValue,
                        false,
                        numberOfDecimalPlacesRatios,
                    )
                    : null;

            subtotalFinancialData.pP = noPrevious
                ? undefined
                : this.aumFinancialNumberPipe.transform(
                    previousRatioValue,
                    numberFormatRatios,
                    printDisplayOptions.nonBreakingHyphen,
                );
            subtotalFinancialData.cP = this.aumFinancialNumberPipe.transform(
                currentRatioValue,
                numberFormatRatios,
                printDisplayOptions.nonBreakingHyphen,
                locale,
            );
        }

        return subtotalFinancialData;
    }

    private getIncomeRatioReferenceItem(
        incomeMappingTemplateGroup: IAccountMappingTemplateGroup,
        printDisplayOptions: IDisplayOptions,
        noPreviousData: boolean,
    ): IItemForCalculatingRatioOption | undefined {
        const incomeItemNameForCalculatingRatio = printDisplayOptions.incomeItemNameForCalculatingRatio;
        const incomeItemsOptionsForCalculatingRatio = this.printPageHelper.getIncomeItemsOptionsForCalculatingRatio(
            incomeMappingTemplateGroup,
            printDisplayOptions.showAdjustmentInSums,
            printDisplayOptions.showReClassInSums,
            noPreviousData ?? false,
        );

        const incomeItemForCalculatingRatio: IItemForCalculatingRatioOption | undefined =
            incomeItemsOptionsForCalculatingRatio.find((item) => item.name === incomeItemNameForCalculatingRatio);

        return incomeItemForCalculatingRatio;
    }

    // Cash flow Statement
    private mapCashflowStatementToSectionData(
        cashFlowStatement: ICashFlowStatement,
        annualReport: IAnnualReport,
        finStatements: IFinStatements[],
        annualReportForAutofill: IAnnualReport,
        printOptions: IPrintOptions,
        reportLocale: string,
        translationLocale: string,
    ): ISectionData {
        const cashFlowStatementFinancialData: IFinancialTableData[] = [];
        const tablesData: IFinancialTableData[][] = [];
        if (printOptions == null || annualReport.generalInformation == null) {
            throw new Error('Print options or general information is null or undefined');
        }

        const noPrevious = annualReport.generalInformation.noPreviousData || printOptions.displayOptions.hidePreviousYear;
        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printOptions.displayOptions,
            printOptions.displayOptions.finStatementHideDecimalPlacesValues,
            printOptions.displayOptions.finStatementHideDecimalPlacesRatios,
            printOptions.displayOptions.finStatementRoundToThousands,
        );

        const currentYearFliIdToAccounts = this.printPageHelper.getFliIdToAccountsForAnnualReport(finStatements);

        // we are deciding here which fin statement to use instead of using the ngrx data
        // we are deciding with the same logic as there. If fcp was used, we use the fcp output
        // otherwise the normal fin statements
        const previousYearFliIdToAccounts = this.printPageHelper.getFliIdToAccountsForAnnualReport(
            annualReportForAutofill?.fcp
                ? annualReportForAutofill.fcp.outputFinStatements
                : annualReportForAutofill?.finStatements,
        );

        const fliIdToType = this.printPageHelper.getFliIdToTypeForAnnualReport(finStatements);

        const calculatedCashFlowStatement = this.cashFlowAdjustmentsAndReclassesHelper.applyAdjustMentsAndReclasses(
            cashFlowStatement,
            printOptions.displayOptions,
            {
                currentFliIdToAccounts: currentYearFliIdToAccounts,
                previousFliIdToAccounts: previousYearFliIdToAccounts,
            },
            fliIdToType,
        );

        if (calculatedCashFlowStatement == null) {
            throw new Error('Calculated cash flow statement is null or undefined');
        }

        const currentYearIncomeDate =
            this.dateLocalPipe.transform(annualReport.generalInformation.incomeCurrentYearFromDate).toString() +
            LINE_BREAK +
            SEPERATOR +
            (annualReport.generalInformation.incomeCurrentYearToDate != null
                ? this.dateLocalPipe.transform(annualReport.generalInformation.incomeCurrentYearToDate).toString()
                : '');
        const incomePreviousYear =
            annualReport.generalInformation.incomePreviousYearToDate != null
                ? this.dateLocalPipe.transform(annualReport.generalInformation.incomePreviousYearToDate).toString()
                : '';

        const previousYearIncomeDate = annualReport.generalInformation.noPreviousData
            ? undefined
            : this.dateLocalPipe.transform(annualReport.generalInformation.incomePreviousYearFromDate).toString() +
            LINE_BREAK +
            SEPERATOR +
            incomePreviousYear;

        const cashFlowTableHeaderData: IFinancialTableData = {
            rowType: RowTypes.TABLE_HEAD,
            t:
                this.wordBeforeCurrency +
                SPACE +
                PdfTronMapperHelper.getCurrencyLabel(
                    annualReport.generalInformation.currency?.name,
                    printOptions.displayOptions.finStatementRoundToThousands,
                ),
            cV: currentYearIncomeDate,
            pV: noPrevious ? undefined : previousYearIncomeDate,
        };

        // https://modeso.atlassian.net/browse/REV-19411 Here we should always calculate including all decimal numbers and round it evenutally if needed
        const total1CurrentValue = CashFlowStatementPrintPreviewHelper.calculateAllActivitiesCurrentYearTotals(
            calculatedCashFlowStatement,
            2,
        );

        // https://modeso.atlassian.net/browse/REV-19411 Here we should always calculate including all decimal numbers and round it evenutally if needed
        const total1PreviousValue = CashFlowStatementPrintPreviewHelper.calculateAllActivitiesPreviousYearTotals(
            calculatedCashFlowStatement,
            2,
        );

        const previousValue =
            total1PreviousValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(total1PreviousValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;
        const currentValue =
            total1CurrentValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(total1CurrentValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;

        const formattedPreviousValue =
            previousValue != null
                ? this.aumFinancialNumberPipe.transform(
                    previousValue,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                )
                : null;
        const formattedCurrentValue =
            currentValue != null
                ? this.aumFinancialNumberPipe.transform(
                    currentValue,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                );

        const cashFlowTableTotalData: IFinancialTableData = {
            rowType: RowTypes.TABLE_TOTAL,
            t: cashFlowStatement.total.total1,
            cV: formattedCurrentValue,
            pV: noPrevious ? undefined : formattedPreviousValue,
        };

        let balanceIncomeAttachmentReferences: Map<string, string> = new Map<string, string>();

        if (printOptions.displayOptions.showNotes && annualReport.notes != null) {
            cashFlowTableHeaderData.r = this.notesColumnHeader;
            cashFlowTableTotalData.r = null;

            let notesAccordionsWithNumbering = this.notesNumberingHelper.applyNumbering(
                annualReport.notes.accordions,
                translationLocale,
                printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.notesHideEmptyLines,
                reportLocale,
            );

            if (printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.notesHideEmptyLines) {
                const notesAccordionsAfterRemoveEmptyLines = this.notesEmptyLinesRemovalHelper.removeEmptyLinesOfNotes(
                    [...notesAccordionsWithNumbering],
                    reportLocale,
                );
                balanceIncomeAttachmentReferences =
                    this.attachmentReferencesHelper.getBalanceIncomeAttachmentReferences(
                        notesAccordionsAfterRemoveEmptyLines,
                        translationLocale,
                    );
            } else {
                balanceIncomeAttachmentReferences =
                    this.attachmentReferencesHelper.getBalanceIncomeAttachmentReferences(
                        notesAccordionsWithNumbering,
                        translationLocale,
                    );
            }
        }

        cashFlowStatementFinancialData.push(cashFlowTableHeaderData);

        const cashFlowTotalOperatingActivityTableFinancialData = this.mapCashFlowStatementActivityTableToFinancialData(
            calculatedCashFlowStatement.operatingActivities,
            annualReport,
            printOptions,
            balanceIncomeAttachmentReferences,
        );

        cashFlowStatementFinancialData.push(...cashFlowTotalOperatingActivityTableFinancialData);

        const cashFlowTotalInvestingActivityTableFinancialData =
            this.mapCashFlowStatementInvestingActivityTableToFinancialData(
                calculatedCashFlowStatement.investingActivities,
                annualReport,
                printOptions,
                balanceIncomeAttachmentReferences,
            );

        cashFlowStatementFinancialData.push(...cashFlowTotalInvestingActivityTableFinancialData);

        const cashFlowTotalFinancingActivityTableFinancialData = this.mapCashFlowStatementActivityTableToFinancialData(
            calculatedCashFlowStatement.financingActivities,
            annualReport,
            printOptions,
            balanceIncomeAttachmentReferences,
        );

        cashFlowStatementFinancialData.push(...cashFlowTotalFinancingActivityTableFinancialData);

        cashFlowStatementFinancialData.push(cashFlowTableTotalData);
        tablesData.push([...cashFlowStatementFinancialData]);
        const cashFlowTotalTableData = this.mapCashFlowStatementTotalTableToFinancialData(
            calculatedCashFlowStatement.total,
            noPrevious,
            printOptions.displayOptions,
        );

        cashFlowStatementFinancialData.push(...cashFlowTotalTableData);
        tablesData.push(cashFlowTotalTableData);
        return {
            sectionId: PDFSectionIds.CASHFLOW,
            title:
                printOptions.reportSectionsTitles != null &&
                    printOptions.reportSectionsTitles[SectionIds.CASHFLOW] != null &&
                    printOptions.reportSectionsTitles[SectionIds.CASHFLOW][translationLocale] != null
                    ? printOptions.reportSectionsTitles[SectionIds.CASHFLOW][translationLocale]
                    : this.cashFlowSectionTitle,
            data: cashFlowStatementFinancialData,
            noPrevious: annualReport.generalInformation.noPreviousData || printOptions.displayOptions.hidePreviousYear,
            showReference: printOptions.displayOptions.showNotes,
            showRatios: printOptions.displayOptions.showRatios,
            tables: tablesData,
        };
    }

    private mapCashFlowStatementActivityTableToFinancialData(
        cashFlowStatementActivity: ICashFlowFinancingActivities | ICashFlowOperatingActivities,
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        balanceIncomeAttachmentReferences: Map<string, string>,
    ): IFinancialTableData[] {
        const cashFlowActivityFinancialTableData: IFinancialTableData[] = [];

        if (printOptions == null || annualReport.generalInformation == null) {
            throw new Error('print options or general information is null or undefined');
        }

        const noPrevious = annualReport.generalInformation.noPreviousData || printOptions.displayOptions.hidePreviousYear;
        const hideEmptyLines =
            printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.finStatementHideEmptyLines;

        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printOptions.displayOptions,
            printOptions.displayOptions.finStatementHideDecimalPlacesValues,
            printOptions.displayOptions.finStatementHideDecimalPlacesRatios,
            printOptions.displayOptions.finStatementRoundToThousands,
        );

        cashFlowStatementActivity.entries.forEach((entry) => {
            const previousValue =
                entry.manuallyEnteredPreviousValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.manuallyEnteredPreviousValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : entry.calculatedPreviousValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.calculatedPreviousValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;
            const currentValue =
                entry.manuallyEnteredCurrentValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.manuallyEnteredCurrentValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : entry.calculatedCurrentValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.calculatedCurrentValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;

            const formattedPreviousValue =
                previousValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        previousValue,
                        numberFormatValues,
                        printOptions.displayOptions.nonBreakingHyphen,
                    )
                    : this.aumFinancialNumberPipe.transform(
                        ZERO_NUMBER,
                        numberFormatValues,
                        printOptions.displayOptions.nonBreakingHyphen,
                    );
            const formattedCurrentValue =
                currentValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        currentValue,
                        numberFormatValues,
                        printOptions.displayOptions.nonBreakingHyphen,
                    )
                    : this.aumFinancialNumberPipe.transform(
                        ZERO_NUMBER,
                        numberFormatValues,
                        printOptions.displayOptions.nonBreakingHyphen,
                    );
            const cashFlowStatementActivityTableRow: IFinancialTableData = {
                rowType: RowTypes.ROW,
                t: entry.name ?? EMPTY_STRING,
                pV: noPrevious ? undefined : formattedPreviousValue,
                cV: formattedCurrentValue,
            };

            if (printOptions && printOptions.displayOptions.showNotes) {
                cashFlowStatementActivityTableRow.r = CashFlowStatementPrintPreviewHelper.getAttachmentReferencesOfFli(
                    entry.fliId,
                    balanceIncomeAttachmentReferences,
                );
            }

            //Remove empty lines if needed
            if (
                (hideEmptyLines &&
                    cashFlowStatementActivityTableRow.pV != null &&
                    !NumberUtils.isZero(+cashFlowStatementActivityTableRow.pV)) ||
                (hideEmptyLines &&
                    cashFlowStatementActivityTableRow.cV != null &&
                    !NumberUtils.isZero(+cashFlowStatementActivityTableRow.cV)) ||
                !hideEmptyLines
            ) {
                cashFlowActivityFinancialTableData.push(cashFlowStatementActivityTableRow);
            }
        });

        // https://modeso.atlassian.net/browse/REV-19411 Here we should always calculate including all decimal numbers and round it evenutally if needed
        const totalCurrentValues = CashFlowStatementPrintPreviewHelper.calculateActivityCurrentYearTotal(
            cashFlowStatementActivity.entries,
            2,
        );
        // https://modeso.atlassian.net/browse/REV-19411 Here we should always calculate including all decimal numbers and round it evenutally if needed
        const totalPreviousValues = CashFlowStatementPrintPreviewHelper.calculateActivityPreviousYearTotal(
            cashFlowStatementActivity.entries,
            2,
        );

        const previousTotalValue =
            totalPreviousValues != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(totalPreviousValues, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;
        const currentTotalValue =
            totalCurrentValues != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(totalCurrentValues, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;

        const formattedPreviousValue =
            previousTotalValue != null
                ? this.aumFinancialNumberPipe.transform(
                    previousTotalValue,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                );
        const formattedCurrentValue =
            currentTotalValue != null
                ? this.aumFinancialNumberPipe.transform(
                    currentTotalValue,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                );

        const cashFlowActivityTableTotalRowData: IFinancialTableData = {
            rowType: RowTypes.GROUP_TOTAL,
            t: cashFlowStatementActivity.title,
            pV: noPrevious ? undefined : formattedPreviousValue,
            cV: formattedCurrentValue,
        };

        cashFlowActivityFinancialTableData.push(cashFlowActivityTableTotalRowData);

        return cashFlowActivityFinancialTableData;
    }

    private mapCashFlowStatementInvestingActivityTableToFinancialData(
        cashFlowStatementInvestingActivity: ICashFlowInvestingActivities,
        annualReport: IAnnualReport,
        printOptions: IPrintOptions,
        balanceIncomeAttachmentReferences: Map<string, string>,
    ): IFinancialTableData[] {
        const cashFlowInvestingActivityFinancialTableData: IFinancialTableData[] = [];

        if (printOptions == null || annualReport.generalInformation == null) {
            throw new Error('print options or general information is null or undefined');
        }

        const noPrevious = annualReport.generalInformation.noPreviousData || printOptions.displayOptions.hidePreviousYear;
        const hideEmptyLines =
            printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.finStatementHideEmptyLines;

        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printOptions.displayOptions,
            printOptions.displayOptions.finStatementHideDecimalPlacesValues,
            printOptions.displayOptions.finStatementHideDecimalPlacesRatios,
            printOptions.displayOptions.finStatementRoundToThousands,
        );

        cashFlowStatementInvestingActivity.entries.forEach((entry) => {
            const previousValue =
                entry.calculatedPreviousValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.calculatedPreviousValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;

            const currentValue =
                entry.calculatedCurrentValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.calculatedCurrentValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;

            const formattedPreviousValue =
                previousValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        previousValue,
                        numberFormatValues,
                        printOptions.displayOptions.nonBreakingHyphen,
                    )
                    : this.aumFinancialNumberPipe.transform(
                        ZERO_NUMBER,
                        numberFormatValues,
                        printOptions.displayOptions.nonBreakingHyphen,
                    );
            const formattedCurrentValue =
                currentValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        currentValue,
                        numberFormatValues,
                        printOptions.displayOptions.nonBreakingHyphen,
                    )
                    : this.aumFinancialNumberPipe.transform(
                        ZERO_NUMBER,
                        numberFormatValues,
                        printOptions.displayOptions.nonBreakingHyphen,
                    );

            const cashFlowStatementInvestingActivityTableRow: IFinancialTableData = {
                rowType: entry.parentFli == null ? RowTypes.ROW : RowTypes.SUB_ROW,
                t: entry.name ?? EMPTY_STRING,
                pV: noPrevious ? undefined : formattedPreviousValue,
                cV: formattedCurrentValue,
            };

            // No value required for ROWs
            if (cashFlowStatementInvestingActivityTableRow.rowType == RowTypes.ROW) {
                cashFlowStatementInvestingActivityTableRow.pV = undefined;
                cashFlowStatementInvestingActivityTableRow.cV = undefined;
            }

            if (printOptions?.displayOptions.showNotes) {
                cashFlowStatementInvestingActivityTableRow.r =
                    CashFlowStatementPrintPreviewHelper.getAttachmentReferencesOfFli(
                        entry.fliId,
                        balanceIncomeAttachmentReferences,
                    );
            }

            //Don't remove Rows for now
            if (cashFlowStatementInvestingActivityTableRow.rowType != RowTypes.ROW) {
                //Remove empty lines if needed
                if (
                    (hideEmptyLines &&
                        !NumberUtils.isZero(+cashFlowStatementInvestingActivityTableRow.pV) &&
                        cashFlowStatementInvestingActivityTableRow.pV != null) ||
                    (hideEmptyLines &&
                        !NumberUtils.isZero(+cashFlowStatementInvestingActivityTableRow.cV) &&
                        cashFlowStatementInvestingActivityTableRow.cV != null) ||
                    !hideEmptyLines
                ) {
                    cashFlowInvestingActivityFinancialTableData.push(cashFlowStatementInvestingActivityTableRow);
                }
            } else {
                cashFlowInvestingActivityFinancialTableData.push(cashFlowStatementInvestingActivityTableRow);
            }
        });

        //Iterate over rows, to see if they need to be removed or not
        if (hideEmptyLines) {
            this.removeConsecutiveRows(cashFlowInvestingActivityFinancialTableData);
        }

        const totalCurrentValues = CashFlowStatementPrintPreviewHelper.calculateActivityCurrentYearTotal(
            cashFlowStatementInvestingActivity.entries,
            numberOfDecimalPlacesValues,
        );
        const totalPreviousValues = CashFlowStatementPrintPreviewHelper.calculateActivityPreviousYearTotal(
            cashFlowStatementInvestingActivity.entries,
            numberOfDecimalPlacesValues,
        );

        const previousTotalValue =
            totalPreviousValues != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(totalPreviousValues, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;
        const currentTotalValue =
            totalCurrentValues != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(totalCurrentValues, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;

        const formattedPreviousValue =
            previousTotalValue != null
                ? this.aumFinancialNumberPipe.transform(
                    previousTotalValue,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                );
        const formattedCurrentValue =
            currentTotalValue != null
                ? this.aumFinancialNumberPipe.transform(
                    currentTotalValue,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printOptions.displayOptions.nonBreakingHyphen,
                );

        const cashFlowInvestingActivityTableTotalRowData: IFinancialTableData = {
            rowType: RowTypes.GROUP_TOTAL,
            t: cashFlowStatementInvestingActivity.title,
            pV: noPrevious ? undefined : formattedPreviousValue,
            cV: formattedCurrentValue,
        };

        cashFlowInvestingActivityFinancialTableData.push(cashFlowInvestingActivityTableTotalRowData);

        return cashFlowInvestingActivityFinancialTableData;
    }

    private removeConsecutiveRows(cashFlowInvestingActivityFinancialTableData: IFinancialTableData[]) {
        let indicesToRemove = [];

        // Iterate over the array to find indices of rows to remove
        for (let i = 0; i < cashFlowInvestingActivityFinancialTableData.length - 1; i++) {
            const currentRow = cashFlowInvestingActivityFinancialTableData[i];
            const nextRow = cashFlowInvestingActivityFinancialTableData[i + 1];

            // If both current and next rows are of type ROW, mark the current one for removal
            if (currentRow.rowType === RowTypes.ROW && nextRow.rowType === RowTypes.ROW) {
                indicesToRemove.push(i);
            }
        }

        // Additional check for the last row
        const lastRowIndex = cashFlowInvestingActivityFinancialTableData.length - 1;
        if (lastRowIndex >= 0 && cashFlowInvestingActivityFinancialTableData[lastRowIndex].rowType === RowTypes.ROW) {
            indicesToRemove.push(lastRowIndex);
        }

        // Remove the identified rows in reverse order to avoid index shifting
        for (let i = indicesToRemove.length - 1; i >= 0; i--) {
            cashFlowInvestingActivityFinancialTableData.splice(indicesToRemove[i], 1);
        }
    }

    private mapCashFlowStatementTotalTableToFinancialData(
        cashFlowTotal: ICashFlowTotal,
        noPrevious: boolean,
        printDisplayOptions: IDisplayOptions,
    ): IFinancialTableData[] {
        const cashFlowTotalTableData: IFinancialTableData[] = [];

        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.finStatementHideDecimalPlacesValues,
            printDisplayOptions.finStatementHideDecimalPlacesRatios,
            printDisplayOptions.finStatementRoundToThousands,
        );

        const cashFlowTotalTableHeaderRow: IFinancialTableData = {
            rowType: RowTypes.TABLE_HEAD,
            t: this.cashFlowTotalTableHeader != null ? this.cashFlowTotalTableHeader : null,
        };

        cashFlowTotalTableData.push(cashFlowTotalTableHeaderRow);

        const previousBeginningPeriodValue =
            cashFlowTotal.previousOpeningCashAndCashEquivalents != null
                ? NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(cashFlowTotal.previousOpeningCashAndCashEquivalents, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                )
                : null;
        const currentBeginningPeriodValue =
            cashFlowTotal.openingCashAndCashEquivalents != null
                ? NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(cashFlowTotal.openingCashAndCashEquivalents, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                )
                : null;

        const formattedBeginningOfPeriodPreviousValue =
            previousBeginningPeriodValue != null
                ? this.aumFinancialNumberPipe.transform(
                    previousBeginningPeriodValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );
        const formattedBeginningOfPeriodCurrentValue =
            currentBeginningPeriodValue != null
                ? this.aumFinancialNumberPipe.transform(
                    currentBeginningPeriodValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );

        const cashFlowTotalAtBeginningOfPeriodRow: IFinancialTableData = {
            rowType: RowTypes.ROW,
            t: this.cashAndCashAtBeginningOfThePeriod != null ? this.cashAndCashAtBeginningOfThePeriod : null,
            cV: formattedBeginningOfPeriodCurrentValue,
            pV: noPrevious ? undefined : formattedBeginningOfPeriodPreviousValue,
        };

        cashFlowTotalTableData.push(cashFlowTotalAtBeginningOfPeriodRow);

        const previousEndOfPeriodValue =
            cashFlowTotal.previousClosingCashAndCashEquivalents != null
                ? NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(cashFlowTotal.previousClosingCashAndCashEquivalents, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                )
                : null;
        const currentEndOfPeriodValue =
            cashFlowTotal.closingCashAndCashEquivalents != null
                ? NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(cashFlowTotal.closingCashAndCashEquivalents, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                )
                : null;

        const formattedEndOfPeriodPreviousValue =
            previousEndOfPeriodValue != null
                ? this.aumFinancialNumberPipe.transform(
                    previousEndOfPeriodValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );
        const formattedEndOfPeriodCurrentValue =
            currentEndOfPeriodValue != null
                ? this.aumFinancialNumberPipe.transform(
                    currentEndOfPeriodValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );

        const cashFlowTotalAtEndOfPeriodRow: IFinancialTableData = {
            rowType: RowTypes.ROW,
            t: this.cashAndCashAtEndOfThePeriod,
            cV: formattedEndOfPeriodCurrentValue,
            pV: noPrevious ? undefined : formattedEndOfPeriodPreviousValue,
        };

        cashFlowTotalTableData.push(cashFlowTotalAtEndOfPeriodRow);

        const cashAndCashEquivalentsPreviousClosing =
            cashFlowTotal.previousClosingCashAndCashEquivalents != null
                ? NumberUtils.roundNumber(cashFlowTotal.previousClosingCashAndCashEquivalents)
                : null;
        const cashAndCashEquivalentsPreviousOpening =
            cashFlowTotal.previousOpeningCashAndCashEquivalents != null
                ? NumberUtils.roundNumber(cashFlowTotal.previousOpeningCashAndCashEquivalents)
                : null;

        const cashAndCashEquivalentsClosing =
            cashFlowTotal.closingCashAndCashEquivalents != null
                ? NumberUtils.roundNumber(cashFlowTotal.closingCashAndCashEquivalents)
                : null;
        const cashAndCashEquivalentsOpening =
            cashFlowTotal.openingCashAndCashEquivalents != null
                ? NumberUtils.roundNumber(cashFlowTotal.openingCashAndCashEquivalents)
                : null;

        // Scale the difference, not the operands: rounding scaled operands to two places and then
        // formatting would round twice (12'497 → 12.50 → 13 instead of 12).
        // https://modeso.atlassian.net/browse/REV-19411 Here we should always calculate including all decimal numbers and round it evenutally if needed
        const previousTotal2Value =
            cashAndCashEquivalentsPreviousClosing != null && cashAndCashEquivalentsPreviousOpening != null
                ? PdfTronMapperHelper.scaleValue(
                      NumberUtils.subtractNumbers(
                          cashAndCashEquivalentsPreviousClosing,
                          cashAndCashEquivalentsPreviousOpening,
                          false,
                          2,
                      ),
                      valueDivisor,
                  )
                : null;

        // https://modeso.atlassian.net/browse/REV-19411 Here we should always calculate including all decimal numbers and round it evenutally if needed
        const currentTotal2Value =
            cashAndCashEquivalentsClosing != null && cashAndCashEquivalentsOpening != null
                ? PdfTronMapperHelper.scaleValue(
                      NumberUtils.subtractNumbers(
                          cashAndCashEquivalentsClosing,
                          cashAndCashEquivalentsOpening,
                          false,
                          2,
                      ),
                      valueDivisor,
                  )
                : null;

        const formattedTotalPreviousValue =
            previousTotal2Value != null
                ? this.aumFinancialNumberPipe.transform(
                    previousTotal2Value,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );
        const formattedTotalCurrentValue =
            currentTotal2Value != null
                ? this.aumFinancialNumberPipe.transform(
                    currentTotal2Value,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );

        const cashFlowTotalRow: IFinancialTableData = {
            rowType: RowTypes.TABLE_TOTAL,
            t: cashFlowTotal.total2,
            cV: formattedTotalCurrentValue,
            pV: noPrevious ? undefined : formattedTotalPreviousValue,
        };

        cashFlowTotalTableData.push(cashFlowTotalRow);

        return cashFlowTotalTableData;
    }

    private mapNotesRichtextToSubSectionItem(notesData: INotesComponent, translationLocale: string): IItem {
        let subSectionItem: IItem = {} as IItem;

        // this is just for type issues to make sure this component is a richtext
        if (Array.isArray(notesData.data)) {
            const richtext = notesData.data.find((richText) => richText.language === translationLocale);

            const componentTitle = notesData.componentTitle
                ? notesData.componentTitle[translationLocale]
                : richtext?.title;
            subSectionItem = {
                itemType: NotesItemsTypes.RICH_TEXT,
                data: richtext?.text
                    ? HtmlContentMapper.replaceCKTablesWithTablesForDocxTemplater(richtext.text)
                    : EMPTY_STRING,
                componentTitle,
            };
        }

        return subSectionItem;
    }

    private mapNotesEquityToSubSectionItem(
        component: INotesComponent,
        noPrevious: boolean,
        printDisplayOptions: IDisplayOptions,
        translationLocale: string,
        reportLocale: string,
    ): IItem {
        const data: IEquity = component.data;
        const itemData: INotesTableData[] = [];

        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.notesHideDecimalPlacesValues,
            printDisplayOptions.notesHideDecimalPlacesRatios,
            printDisplayOptions.notesRoundToThousands,
        );

        const previousDate = noPrevious ? undefined : this.dateLocalPipe.transform(data.previousDate).toString();
        const currentDate = this.dateLocalPipe.transform(data.currentDate).toString();

        const notesEquityTableDatesHeaderData: INotesTableData = {
            rowType: RowTypes.TABLE_HEAD,
            t: null,
            cV2: currentDate,
            pV2: noPrevious ? undefined : previousDate,
        };
        itemData.push(notesEquityTableDatesHeaderData);

        const tableTitle =
            data.options.textTitle[translationLocale] || data.options.textTitle[reportLocale] || EMPTY_STRING;

        const notesEquityTableHeaderData: INotesTableData = {
            rowType: RowTypes.TABLE_HEAD,
            t: tableTitle,
            cV1: data.options.currentValueTitle1[translationLocale] || data.options.currentValueTitle1[reportLocale],
            cV2: data.options.currentValueTitle2[translationLocale] || data.options.currentValueTitle2[reportLocale],
            pV1: noPrevious
                ? undefined
                : data.options.previousValueTitle1[translationLocale] || data.options.previousValueTitle1[reportLocale],
            pV2: noPrevious
                ? undefined
                : data.options.previousValueTitle2[translationLocale] || data.options.previousValueTitle2[reportLocale],
        };
        itemData.push(notesEquityTableHeaderData);

        data.rows.forEach((row) => {
            const formattedPreviousValue1 = this.formatNumber(
                printDisplayOptions.nonBreakingHyphen,
                row.previousValue1,
                numberOfDecimalPlacesValues,
                numberFormatValues,
                valueDivisor,
            );
            const formattedCurrentValue1 = this.formatNumber(
                printDisplayOptions.nonBreakingHyphen,
                row.currentValue1,
                numberOfDecimalPlacesValues,
                numberFormatValues,
                valueDivisor,
            );
            const formattedPreviousValue2 = this.formatNumber(
                printDisplayOptions.nonBreakingHyphen,
                row.previousValue2,
                numberOfDecimalPlacesValues,
                numberFormatValues,
                valueDivisor,
            );
            const formattedCurrentValue2 = this.formatNumber(
                printDisplayOptions.nonBreakingHyphen,
                row.currentValue2,
                numberOfDecimalPlacesValues,
                numberFormatValues,
                valueDivisor,
            );

            const notesEquityTableRowData: INotesTableData = {
                rowType: RowTypes.ROW,
                t: row.text,
                cV1: formattedCurrentValue1,
                cV2: formattedCurrentValue2,
                pV1: noPrevious ? undefined : formattedPreviousValue1,
                pV2: noPrevious ? undefined : formattedPreviousValue2,
            };

            itemData.push(notesEquityTableRowData);
        });

        if (data.totalRow) {
            const formattedTotalPreviousValue1 = this.formatNumber(
                printDisplayOptions.nonBreakingHyphen,
                data.totalRow.previousValue1,
                numberOfDecimalPlacesValues,
                numberFormatValues,
                valueDivisor,
            );
            const formattedTotalCurrentValue1 = this.formatNumber(
                printDisplayOptions.nonBreakingHyphen,
                data.totalRow.currentValue1,
                numberOfDecimalPlacesValues,
                numberFormatValues,
                valueDivisor,
            );
            const formattedTotalPreviousValue2 = this.formatNumber(
                printDisplayOptions.nonBreakingHyphen,
                data.totalRow.previousValue2,
                numberOfDecimalPlacesValues,
                numberFormatValues,
                valueDivisor,
            );
            const formattedTotalCurrentValue2 = this.formatNumber(
                printDisplayOptions.nonBreakingHyphen,
                data.totalRow.currentValue2,
                numberOfDecimalPlacesValues,
                numberFormatValues,
                valueDivisor,
            );
            const notesEquityTableTotalRowData: INotesTableData = {
                rowType: RowTypes.TABLE_TOTAL,
                t: data.totalRow.text,
                cV1: formattedTotalCurrentValue1,
                cV2: formattedTotalCurrentValue2,
                pV1: noPrevious ? undefined : formattedTotalPreviousValue1,
                pV2: noPrevious ? undefined : formattedTotalPreviousValue2,
            };
            itemData.push(notesEquityTableTotalRowData);
        }

        const componentTitle = component.componentTitle
            ? component.componentTitle[translationLocale] || component.componentTitle[reportLocale]
            : undefined;

        const subSectionItem: IItem = {
            itemType: NotesItemsTypes.EQUITY,
            data: itemData,
            componentTitle: componentTitle ?? EMPTY_STRING,
        };

        return subSectionItem;
    }

    private formatNumber(
        nonBreakingHyphen: boolean,
        inputNumber?: number,
        numberOfDecimalPlacesValues?: number,
        numberFormatValues?: string,
        valueDivisor = NO_VALUE_DIVISOR,
    ): string {
        const inputNumberValue =
            inputNumber != null && inputNumber.toString() != ''
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(inputNumber, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;

        const formattedinputNumberValue =
            inputNumberValue != null
                ? this.aumFinancialNumberPipe.transform(inputNumberValue, numberFormatValues, nonBreakingHyphen)
                : null;

        return formattedinputNumberValue;
    }

    private mapNotesFinancialLineItemToSubSectionItem(
        component: INotesComponent,
        generalInformation: IGeneralInformation,
        printDisplayOptions: IDisplayOptions,
        addNumbering: boolean,
        reportLocale: string,
        translationLocale: string,
        hasFootnotes: boolean,
        fliFxStamps: Map<string, IFxNodeStampValues> | null = null,
        fxDisplayCurrencyName = '',
    ): IItem {
        const financialLineItemData = component.data as IFinancialLineItemComponent;

        const itemData: IFinancialTableData[] = [];
        const tablesData: IFinancialTableData[][] = [];
        const noPrevious = generalInformation.noPreviousData || printDisplayOptions.hidePreviousYear;
        const currency = PdfTronMapperHelper.getCurrencyLabel(
            generalInformation.currency?.name,
            printDisplayOptions.notesRoundToThousands,
        );
        fxDisplayCurrencyName =
            PdfTronMapperHelper.getCurrencyLabel(fxDisplayCurrencyName, printDisplayOptions.notesRoundToThousands) ??
            '';

        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.notesHideDecimalPlacesValues,
            printDisplayOptions.notesHideDecimalPlacesRatios,
            printDisplayOptions.notesRoundToThousands,
        );

        const previousDate = noPrevious
            ? undefined
            : this.dateLocalPipe.transform(generalInformation.balancePreviousYearDate).toString();
        const currentDate = this.dateLocalPipe.transform(generalInformation.balanceSheetDate).toString();

        const localizedComponentTitle = component.componentTitle
            ? component.componentTitle?.[translationLocale] || component.componentTitle?.[reportLocale]
            : financialLineItemData.options.text?.[translationLocale] ||
            financialLineItemData.options.text?.[reportLocale];

        // FX (REV-21512): the 3-state chip drives the printed columns; an absent option means
        // `both` on FX reports. Display values come from the per-account stamps.
        const isFxReport = fliFxStamps != null;
        const fxState = effectiveFxCurrencyDisplay(financialLineItemData.options, isFxReport);
        const fxStampOf = (account: { id?: unknown; accountName?: string }): IFxNodeStampValues | undefined =>
            fliFxStamps?.get(fliFxStampKeyOf(account));
        const formatFxValue = (value: number | null | undefined): string | null =>
            value == null || !NumberUtils.isFinite(value)
                ? null
                : this.aumFinancialNumberPipe.transform(
                      NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(value, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      ),
                      numberFormatValues,
                      printDisplayOptions.nonBreakingHyphen,
                  );

        if (financialLineItemData.flis == null) {
            financialLineItemData.flis = [];
        }

        financialLineItemData.flis.forEach((fli, index) => {
            const numericPreviousValues = fli.accounts.map((acc) => acc.previousValue);
            const numericCurrentValues = fli.accounts.map((acc) => acc.currentValue);

            const accountsPreviousValueSum = this.processValues(numericPreviousValues);
            const accountsCurrentValueSum = this.processValues(numericCurrentValues);
            let totalCurrentValue = 0;
            let totalPreviousValue = 0;
            const totalRowText = fli.totalRow.accountName;
            if (NumberUtils.isFinite(accountsPreviousValueSum)) {
                totalPreviousValue = NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(accountsPreviousValueSum, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                );
            }

            if (NumberUtils.isFinite(accountsCurrentValueSum)) {
                totalCurrentValue = NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(accountsCurrentValueSum, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                );
            }

            // The FLI total is the sum of the displayed account rows
            // (computed above). Those rows are already reclass/adjustment-aware
            // via NotesAdjustmentsAndReclassesHelper, so the total stays
            // consistent with the visible line items. (Previously the total was
            // overwritten from the live FLI aggregate, which decoupled it from
            // the rows whenever the note snapshot and the mapping diverged.)
            /*** Translated */
            const tableRowData: IFinancialTableData[] = [];
            // Editor semantics (recomputeFxTwinValues): ANY missing stamp nulls the whole
            // display total — a partial sum would look settled while excluding accounts.
            let fxTotalCY: number | null = 0;
            let fxTotalPY: number | null = 0;
            fli.accounts.forEach((account) => {
                let currentValue: number | null = null;
                if (account.currentValue != null && NumberUtils.isFinite(account.currentValue)) {
                    currentValue = NumberUtils.roundNumber(
                        PdfTronMapperHelper.scaleValue(account.currentValue, valueDivisor),
                        false,
                        numberOfDecimalPlacesValues,
                    );
                }

                let previousValue: number | null = null;
                if (account.previousValue != null && NumberUtils.isFinite(account.previousValue)) {
                    previousValue = NumberUtils.roundNumber(
                        PdfTronMapperHelper.scaleValue(account.previousValue, valueDivisor),
                        false,
                        numberOfDecimalPlacesValues,
                    );
                }

                const formattedPreviousValue =
                    previousValue != null
                        ? this.aumFinancialNumberPipe.transform(
                            previousValue,
                            numberFormatValues,
                            printDisplayOptions.nonBreakingHyphen,
                        )
                        : null;
                const formattedCurrentValue =
                    currentValue != null
                        ? this.aumFinancialNumberPipe.transform(
                            currentValue,
                            numberFormatValues,
                            printDisplayOptions.nonBreakingHyphen,
                        )
                        : null;

                const fliTablerowData: IFinancialTableData = {
                    rowType: RowTypes.ROW,
                    t: account.accountName,
                    pV: noPrevious ? undefined : formattedPreviousValue,
                    cV: formattedCurrentValue,
                };

                // FX (REV-21512): display-currency cells — computed, never stored.
                if (fxState !== 'functional') {
                    const stamp = fxStampOf(account);
                    const displayCY = formatFxValue(stamp?.displayCY);
                    const displayPY = formatFxValue(stamp?.displayPY);
                    fxTotalCY = stamp?.displayCY == null || fxTotalCY == null ? null : fxTotalCY + stamp.displayCY;
                    fxTotalPY = stamp?.displayPY == null || fxTotalPY == null ? null : fxTotalPY + stamp.displayPY;
                    if (fxState === 'both') {
                        fliTablerowData.cV2 = displayCY;
                        fliTablerowData.pV2 = noPrevious ? undefined : displayPY;
                    } else {
                        fliTablerowData.cV = displayCY;
                        fliTablerowData.pV = noPrevious ? undefined : displayPY;
                    }
                }

                tableRowData.push(fliTablerowData);
            });

            const formattedPreviousValue =
                totalPreviousValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        totalPreviousValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;
            const formattedCurrentValue =
                totalCurrentValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        totalCurrentValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;

            // REV-21347: resolve a pre-migration locale-object caption to a string (report language,
            // then the print's active locale) so it never renders / prints as "[object Object]".
            let rowText = getCaptionString(fli.caption, reportLocale, translationLocale);

            if ((fli as IFLIForNotes).itemType === ItemType.OF_THAT_ITEM) {
                rowText = `${rowText} ${currency} ${formattedCurrentValue} (${currency} ${formattedPreviousValue} ${this.wordBeforeCurrency}) `;
            } else if (!isFxReport) {
                rowText = rowText + SPACE + this.wordBeforeCurrency;
            }

            let formattedCurrentIncomeYearDate: string | undefined;
            let formattedPreviousIncomeYearDate: string | undefined;

            if (fli.belongingTemplateType === TemplateType.INCOME) {
                formattedCurrentIncomeYearDate = this.dateLocalPipe.transform(generalInformation.incomeCurrentYearFromDate) + LINE_BREAK + SEPERATOR + this.dateLocalPipe.transform(generalInformation.incomeCurrentYearToDate);
                formattedPreviousIncomeYearDate = this.dateLocalPipe.transform(generalInformation.incomePreviousYearFromDate) + LINE_BREAK + SEPERATOR + this.dateLocalPipe.transform(generalInformation.incomePreviousYearToDate);
            }

            const currentDateHeader =
                fli.belongingTemplateType === TemplateType.INCOME ? formattedCurrentIncomeYearDate : currentDate;
            const previousDateHeader =
                fli.belongingTemplateType === TemplateType.INCOME ? formattedPreviousIncomeYearDate : previousDate;

            // FX (REV-21512): the currency moves out of the title — every date column carries its
            // own currency on a second line (same font as the date), however many columns print.
            const fxPrimaryCurrency = fxState === 'display' ? fxDisplayCurrencyName : currency;
            const fliTableHeaderData: IFinancialTableData = isFxReport
                ? {
                      rowType: RowTypes.TABLE_HEAD,
                      t: rowText,
                      pV: noPrevious ? undefined : previousDateHeader + LINE_BREAK + fxPrimaryCurrency,
                      cV: currentDateHeader + LINE_BREAK + fxPrimaryCurrency,
                  }
                : {
                      rowType: RowTypes.TABLE_HEAD,
                      t: rowText + SPACE + currency,
                      pV: noPrevious ? undefined : previousDateHeader,
                      cV: currentDateHeader,
                  };
            if (fxState === 'both') {
                fliTableHeaderData.cV2 = currentDateHeader + LINE_BREAK + fxDisplayCurrencyName;
                fliTableHeaderData.pV2 = noPrevious
                    ? undefined
                    : previousDateHeader + LINE_BREAK + fxDisplayCurrencyName;
            }
            itemData.push(fliTableHeaderData);
            itemData.push(...tableRowData);

            const fliTableTotalData: IFinancialTableData = {
                rowType: RowTypes.TABLE_TOTAL,
                t: totalRowText ?? this.totalWord,
                pV: noPrevious ? undefined : formattedPreviousValue,
                cV: formattedCurrentValue,
            };
            if (fxState === 'both') {
                fliTableTotalData.cV2 = formatFxValue(fxTotalCY);
                fliTableTotalData.pV2 = noPrevious ? undefined : formatFxValue(fxTotalPY);
            } else if (fxState === 'display') {
                fliTableTotalData.cV = formatFxValue(fxTotalCY);
                fliTableTotalData.pV = noPrevious ? undefined : formatFxValue(fxTotalPY);
            }

            itemData.push(fliTableTotalData);

            // for https://modeso.atlassian.net/browse/REV-14530 add tables in separated form
            const singleTableData = [];
            singleTableData.push(fliTableHeaderData);
            singleTableData.push(...tableRowData);
            singleTableData.push(fliTableTotalData);
            tablesData.push(singleTableData);

            // Footnotes
            if (
                hasFootnotes &&
                financialLineItemData.footnotes &&
                financialLineItemData.footnotes.length > 0 &&
                financialLineItemData.footnotes[index]
            ) {
                const fliFootnoteData: IFinancialTableData = {
                    rowType: RowTypes.FOOTNOTE,
                    t: financialLineItemData.footnotes[index],
                };
                itemData.push(fliFootnoteData);
                singleTableData.push(fliFootnoteData);
            }
        });

        const subSectionItem: IItem = {
            itemType: NotesItemsTypes.FINANCIAL_LINE_ITEM,
            data: itemData,
            tables: tablesData,
            componentTitle: localizedComponentTitle ?? EMPTY_STRING,
        };
        // Only stamped when BOTH currencies print — the template then picks its four-column
        // layout. A single-currency table (and every non-FX report) keeps the normal FLI
        // placeholder, which still has exactly two value columns to fill.
        if (fxState === 'both') {
            subSectionItem.isFxFormat = true;
        }
        return subSectionItem;
    }

    // Function to get the sum, single value, or zero based on the number of numeric values
    processValues(values) {
        const numericValues = values.filter((value) => typeof value === 'number');

        if (numericValues.length >= 2) {
            return NumberUtils.sumNumbers(numericValues);
        } else if (numericValues.length === 1) {
            return numericValues[0];
        } else {
            return 0;
        }
    }

    private mapNotesFreeTableWithAmountsToSubSectionItem(
        component: INotesComponent,
        generalInformation: IGeneralInformation,
        printDisplayOptions: IDisplayOptions,
        translationLocale: string,
        reportLocale: string,
        hasFootnotes: boolean,
    ): IItem {
        const freeTableData: IFreeTable = component.data;
        const itemData: IFinancialTableData[] = [];
        const noPrevious = generalInformation.noPreviousData || printDisplayOptions.hidePreviousYear;

        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.notesHideDecimalPlacesValues,
            printDisplayOptions.notesHideDecimalPlacesRatios,
            printDisplayOptions.notesRoundToThousands,
        );

        const previousDate = noPrevious
            ? undefined
            : this.dateLocalPipe.transform(generalInformation.balancePreviousYearDate).toString();
        const currentDate = this.dateLocalPipe.transform(generalInformation.balanceSheetDate).toString();
        const freeTableTitle =
            freeTableData.options.title?.[translationLocale] ?? freeTableData.options.title?.[reportLocale];
        const tableHeaderData: IFinancialTableData = {
            rowType: RowTypes.TABLE_HEAD,
            t: freeTableTitle ?? EMPTY_STRING,
            pV: noPrevious ? undefined : previousDate,
            cV: currentDate,
        };
        itemData.push(tableHeaderData);

        let totalCurrentValue = 0;
        let totalPreviousValue = 0;

        freeTableData.rows.forEach((row) => {
            let currentValue: number | null = null;
            if (row.currentValue != null && NumberUtils.isFinite(row.currentValue)) {
                currentValue = NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(row.currentValue, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                );
                totalCurrentValue = NumberUtils.sumNumbers(
                    [totalCurrentValue, currentValue],
                    false,
                    numberOfDecimalPlacesValues,
                );
            }

            let previousValue: number | null = null;
            if (row.previousValue != null && NumberUtils.isFinite(row.previousValue)) {
                previousValue = NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(row.previousValue, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                );
                totalPreviousValue = NumberUtils.sumNumbers(
                    [totalPreviousValue, previousValue],
                    false,
                    numberOfDecimalPlacesValues,
                );
            }

            const formattedPreviousValue =
                previousValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        previousValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;
            const formattedCurrentValue =
                currentValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        currentValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;

            const tableRowData: IFinancialTableData = {
                rowType: RowTypes.ROW,
                t: row.text,
                pV: noPrevious ? undefined : formattedPreviousValue,
                cV: formattedCurrentValue,
            };

            itemData.push(tableRowData);
        });

        if (freeTableData.options.totalRow) {
            const formattedPreviousValue =
                totalPreviousValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        totalPreviousValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;
            const formattedCurrentValue =
                totalCurrentValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        totalCurrentValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;

            const tableTotalData: IFinancialTableData = {
                rowType: RowTypes.TABLE_TOTAL,
                t: freeTableData.totalRow?.text || this.totalWord,
                pV: noPrevious ? undefined : formattedPreviousValue,
                cV: formattedCurrentValue,
            };
            itemData.push(tableTotalData);
        }

        // Footnotes
        if (
            hasFootnotes &&
            freeTableData.footnotes &&
            freeTableData.footnotes.length > 0 &&
            freeTableData.footnotes[0]
        ) {
            const fliFootnoteData: IFinancialTableData = {
                rowType: RowTypes.FOOTNOTE,
                t: freeTableData.footnotes[0],
            };
            itemData.push(fliFootnoteData);
        }

        const subSectionItem: IItem = {
            itemType: NotesItemsTypes.FREE_TABLE_WITH_AMOUNTS,
            data: itemData,
            componentTitle: component.componentTitle
                ? `${component.componentTitle[translationLocale]}`.trim() ||
                `${component.componentTitle[reportLocale]}`.trim()
                : EMPTY_STRING,
        };

        return subSectionItem;
    }

    private mapNotesToSectionData(
        notes: INotes,
        noPrevious: boolean,
        annualReport: IAnnualReport,
        finStatements: IFinStatements[],
        annualReportForAutofill: IAnnualReport,
        printOptions: IPrintOptions,
        translationLocale: string,
        reportLocale: string,
        notesComponentsHTMLMap?: Map<string, Map<string, string>>,
    ): ISectionData {
        const notesSubSections: ISubsection[] = [];

        if (printOptions == null) {
            throw new Error('Print options is null or undefined');
        }

        const currentYearFliIdToAccounts = this.printPageHelper.getFliIdToAccountsForAnnualReport(finStatements);

        const previousYearFliIdToAccounts = this.printPageHelper.getFliIdToAccountsForAnnualReport(
            annualReportForAutofill?.fcp
                ? annualReportForAutofill.fcp.outputFinStatements
                : annualReportForAutofill?.finStatements,
        );

        // FX (REV-21512): per-account display stamps for the FLI notes — same engine as the
        // conversion table; null on non-FX reports (every consumer then behaves exactly as today).
        const notesFliFxStamps = FxTranslationHelper.buildFliFxStampsForPrint(
            annualReport,
            finStatements,
            this.flisMapper,
            GroupingHelper.applyAllGroupingMaskingTransformations,
        );
        const fxDisplayCurrencyName = annualReport.generalInformation?.displayCurrency?.name ?? '';

        let notesAccordions = this.notesAdjustmentsAndreclassesHelper.applyAdjustMentsAndReclasses(
            notes.accordions,
            printOptions.displayOptions,
            {
                currentFliIdToAccounts: currentYearFliIdToAccounts,
                previousFliIdToAccounts: previousYearFliIdToAccounts,
            },
        );


        let notesAccordionsWithNumbering = this.notesNumberingHelper.applyNumbering(
            notesAccordions,
            translationLocale,
            printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.notesHideEmptyLines,
            reportLocale,
        );

        notesAccordionsWithNumbering = cloneDeep(this.notesVisibilityHelper.removeHiddenNotes(
            [...notesAccordionsWithNumbering],
        ));

        if (printOptions.displayOptions.hideEmptyLines || printOptions.displayOptions.notesHideEmptyLines) {
            notesAccordionsWithNumbering = this.notesEmptyLinesRemovalHelper.removeEmptyLinesOfNotes(
                [...notesAccordionsWithNumbering],
                reportLocale,
            );
        }

        notesAccordionsWithNumbering.forEach((accordion, index) => {
            const accordionItems: IItem[] = [];

            accordion.data.forEach((data, componentIndex) => {
                if (printOptions == null || annualReport.generalInformation == null) {
                    throw new Error('Print options or general information is null or undefined');
                }

                let richTextSubsectionItem: IItem | null;
                let equitySubsectionItem: IItem | null;
                let freeTableSubsectionItem: IItem | null;
                let selectSubsectionItem: IItem | null;
                let inputSubsectionItem: IItem | null;
                let superTableSubsectionItem: IItem | null;

                const hasFootnotes = data.hasFootnotes;

                switch (data.componentName) {
                    case ComponentType.RICH_TEXT:
                        richTextSubsectionItem = this.mapNotesRichtextToSubSectionItem(data, translationLocale);

                        accordionItems.push(richTextSubsectionItem);
                        break;

                    case ComponentType.EQUITY:
                        equitySubsectionItem = this.mapNotesEquityToSubSectionItem(
                            data,
                            noPrevious,
                            printOptions.displayOptions,
                            translationLocale,
                            reportLocale,
                        );

                        accordionItems.push(equitySubsectionItem);
                        break;

                    case ComponentType.FINANCIAL_LINE_ITEM: {
                        const fliComponentSubsection = this.mapNotesFinancialLineItemToSubSectionItem(
                            data,
                            annualReport.generalInformation,
                            printOptions.displayOptions,
                            data.addNumbering,
                            translationLocale,
                            reportLocale,
                            hasFootnotes,
                            notesFliFxStamps,
                            fxDisplayCurrencyName,
                        );

                        accordionItems.push(fliComponentSubsection);
                        break;
                    }

                    case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                        freeTableSubsectionItem = this.mapNotesFreeTableWithAmountsToSubSectionItem(
                            data,
                            annualReport.generalInformation,
                            printOptions.displayOptions,
                            translationLocale,
                            reportLocale,
                            hasFootnotes,
                        );
                        accordionItems.push(freeTableSubsectionItem);
                        break;

                    case ComponentType.SELECT:
                        selectSubsectionItem = this.mapNotesSelectToSubSectionItem(
                            data,
                            annualReport.generalInformation,
                            translationLocale,
                            reportLocale,
                            hasFootnotes,
                        );
                        accordionItems.push(selectSubsectionItem);
                        break;

                    case ComponentType.INPUT:
                        inputSubsectionItem = this.mapNotesInputToSubSectionItem(
                            data.data as INotesInputComponent,
                            noPrevious,
                            annualReport.generalInformation,
                            printOptions.displayOptions,
                            translationLocale,
                            reportLocale,
                        );

                        accordionItems.push(inputSubsectionItem);
                        break;

                    case ComponentType.MOVEMENT_SCHEDULE: {
                        const movementScheduleComponentSection = this.mapNotesMovementScheduleToSubSectionItem(
                            data,
                            annualReport.generalInformation,
                            printOptions.displayOptions,
                            data.addNumbering,
                            reportLocale,
                            translationLocale,
                            hasFootnotes,
                            annualReportForAutofill?.generalInformation,
                        );

                        accordionItems.push(movementScheduleComponentSection);
                        break;
                    }

                    case ComponentType.SUPER_TABLE: {
                        let componentHtml = '';
                        if (notesComponentsHTMLMap != null) {
                            const accordionComponentsHTMLMap = notesComponentsHTMLMap.get(index.toString());
                            if (accordionComponentsHTMLMap != null) {
                                componentHtml = accordionComponentsHTMLMap.get(`${data.componentId}_${componentIndex}`);
                                componentHtml = HtmlContentMapper.extractTables(componentHtml);
                            }
                        }

                        superTableSubsectionItem = {
                            componentId: data.componentId,
                            itemType: NotesItemsTypes.SUPER_TABLE,
                            data: componentHtml,
                            componentTitle:
                                data.componentTitle?.[translationLocale] || data.componentTitle?.[reportLocale],
                            amountOfRows: data?.data?.rows?.length,
                        };
                        accordionItems.push(superTableSubsectionItem);
                        break;
                    }

                    default:
                        throw new Error('Notes accordion data has invalid component name');
                }
            });

            const accordionSubSection: ISubsection = {
                sectionId: index,
                title: accordion.title[translationLocale] || accordion.title[reportLocale],
                items: accordionItems,
            };

            notesSubSections.push(accordionSubSection);
        });

        return {
            sectionId: PDFSectionIds.NOTES,
            title:
                printOptions.reportSectionsTitles != null &&
                    printOptions.reportSectionsTitles[SectionIds.NOTES] != null &&
                    printOptions.reportSectionsTitles[SectionIds.NOTES][translationLocale] != null
                    ? printOptions.reportSectionsTitles[SectionIds.NOTES][translationLocale]
                    : this.notesSectionTitle,
            subsections: notesSubSections,
            noPrevious: annualReport.generalInformation?.noPreviousData || printOptions.displayOptions.hidePreviousYear,
            showReference: printOptions.displayOptions.showNotes,
            showRatios: printOptions.displayOptions.showRatios,
        };
    }
    private mapNotesInputToSubSectionItem(
        inputData: INotesInputComponent,
        noPrevious: boolean,
        generalInformation: IGeneralInformation,
        displayOptions: IDisplayOptions,
        translationLocale: string,
        reportLocale: string,
    ): IItem {
        const itemData: IFinancialTableData[] = [];

        if (noPrevious == false) {
            // Test if the item compares years
            noPrevious = !inputData.options.compareYears;
        }

        const title = inputData.options.title?.[translationLocale] || inputData.options.title?.[reportLocale];

        const previousDate = noPrevious
            ? undefined
            : this.dateLocalPipe.transform(generalInformation.balancePreviousYearDate).toString();
        const currentDate = this.dateLocalPipe.transform(generalInformation.balanceSheetDate).toString();

        const tableHeaderData: IFinancialTableData = {
            rowType: RowTypes.TABLE_HEAD,
            t: title ?? EMPTY_STRING,
            pV: noPrevious
                ? undefined
                : inputData.options.dataType === IInputDataType.CURRENCY
                    ? previousDate + ' in ' +
                      PdfTronMapperHelper.getCurrencyLabel(
                          generalInformation.currency?.name,
                          displayOptions.notesRoundToThousands,
                      )
                    : previousDate,
            cV:
                inputData.options.dataType === IInputDataType.CURRENCY
                    ? currentDate + ' in ' +
                      PdfTronMapperHelper.getCurrencyLabel(
                          generalInformation.currency?.name,
                          displayOptions.notesRoundToThousands,
                      )
                    : currentDate,
            compareYears: inputData.options.compareYears,
        };
        itemData.push(tableHeaderData);

        let previousValue: string;
        let currentValue: string;

        if (inputData.options.dataType == IInputDataType.TEXT) {
            previousValue = inputData.previousYearValue as string;

            currentValue = inputData.currentYearValue as string;
        } else if (
            inputData.options.dataType == IInputDataType.NUMBER ||
            inputData.options.dataType == IInputDataType.CURRENCY
        ) {
            const { numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
                displayOptions,
                displayOptions.notesHideDecimalPlacesValues,
                displayOptions.notesHideDecimalPlacesRatios,
                displayOptions.notesRoundToThousands,
                inputData.options.numberOfDecimalPlaces,
            );

            // Only CURRENCY inputs are amounts; NUMBER inputs (counts, FTE) keep their scale.
            const inputDivisor =
                inputData.options.dataType == IInputDataType.CURRENCY ? valueDivisor : NO_VALUE_DIVISOR;
            previousValue = this.aumFinancialNumberPipe.transform(
                PdfTronMapperHelper.scaleValue(inputData.previousYearValue as number, inputDivisor),
                numberFormatValues,
                displayOptions.nonBreakingHyphen,
            );
            currentValue = this.aumFinancialNumberPipe.transform(
                PdfTronMapperHelper.scaleValue(inputData.currentYearValue as number, inputDivisor),
                numberFormatValues,
                displayOptions.nonBreakingHyphen,
            );
        }

        const tableRowData: IFinancialTableData = {
            rowType: RowTypes.ROW,
            t: inputData.options.text[translationLocale] ?? inputData.options.text[reportLocale],
            pV: noPrevious ? undefined : previousValue,
            cV: currentValue,
            dataType: inputData.options.dataType,
            compareYears: inputData.options.compareYears,
        };

        itemData.push(tableRowData);

        const subSectionItem: IItem = {
            itemType: noPrevious ? NotesItemsTypes.INPUT_NO_PREVIOUS : NotesItemsTypes.INPUT,
            data: itemData,
        };

        return subSectionItem;
    }

    private mapNotesMovementScheduleToSubSectionItem(
        component: INotesComponent,
        generalInformation: IGeneralInformation,
        printDisplayOptions: IDisplayOptions,
        addNumbering: boolean,
        reportLocale: string,
        translationLocale: string,
        hasFootnotes: boolean,
        generalInformationOfPreviousAnnualReport?: IGeneralInformation,

    ): IItem {
        const movementsScheduleData = component.data as IMovementScheduleComponent;

        const tablesData: INotesMovementScheduleData[][] = [];
        const itemsData: INotesMovementScheduleData[] = [];

        let componentTitle: string;
        if (movementsScheduleData.flis.length) {
            componentTitle = component.componentTitle
                ? component.componentTitle[translationLocale] || component.componentTitle[reportLocale]
                : movementsScheduleData.flis[0].headers.title?.[translationLocale] ??
                movementsScheduleData.flis[0].headers.title?.[reportLocale];
        }

        movementsScheduleData.flis.forEach((fli, index) => {
            const msDataForFli = this.mapNotesMovementScheduleToSubSectionItemData(
                movementsScheduleData,
                fli,
                generalInformation,
                printDisplayOptions,
                reportLocale,
                translationLocale,
                false,
                generalInformationOfPreviousAnnualReport,
            );
            itemsData.push(...msDataForFli.itemData);
            tablesData.push(msDataForFli.tableData);

            // Footnotes
            if (
                hasFootnotes &&
                movementsScheduleData.footnotes &&
                movementsScheduleData.footnotes.length > 0 &&
                movementsScheduleData.footnotes[0]
            ) {
                const fliFootnoteData: IFinancialTableData = {
                    rowType: RowTypes.FOOTNOTE,
                    t: movementsScheduleData.footnotes[index],
                };
                itemsData.push(fliFootnoteData);
                msDataForFli.tableData.push(fliFootnoteData);
            }

            if (movementsScheduleData.previousMovementScheduleComponent != null && !printDisplayOptions.hidePreviousYear) {
                const previousFli = movementsScheduleData.previousMovementScheduleComponent.flis.find(
                    (previousFli) => previousFli.id === fli.id,
                );
                if (previousFli == undefined) {
                    return;
                }

                const previousMovementScheduleComponentData = this.mapNotesMovementScheduleToSubSectionItemData(
                    movementsScheduleData.previousMovementScheduleComponent,
                    previousFli,
                    generalInformation,
                    printDisplayOptions,
                    reportLocale,
                    translationLocale,
                    true,
                    generalInformationOfPreviousAnnualReport
                );

                itemsData.push(...previousMovementScheduleComponentData.itemData);
                // for https://modeso.atlassian.net/browse/REV-14530 add tables in separated form
                tablesData.push(previousMovementScheduleComponentData.tableData);

                // Footnotes
                if (
                    hasFootnotes &&
                    movementsScheduleData.previousMovementScheduleComponent.footnotes &&
                    movementsScheduleData.previousMovementScheduleComponent.footnotes.length > 0 &&
                    movementsScheduleData.previousMovementScheduleComponent.footnotes[0]
                ) {
                    const fliFootnoteData: IFinancialTableData = {
                        rowType: RowTypes.FOOTNOTE,
                        t: movementsScheduleData.previousMovementScheduleComponent.footnotes[index],
                    };
                    itemsData.push(fliFootnoteData);
                    previousMovementScheduleComponentData.tableData.push(fliFootnoteData);
                }
            }
        });

        const subSectionItem: IItem = {
            itemType: NotesItemsTypes.MOVEMENT_SCHEDULE,
            data: itemsData,
            componentTitle: componentTitle ?? EMPTY_STRING,
            // for https://modeso.atlassian.net/browse/REV-14530 add tables in separated form
            tables: tablesData,
        };

        return subSectionItem;
    }

    private mapNotesMovementScheduleToSubSectionItemData(
        movementsScheduleData: IMovementScheduleComponent,
        fli: IMovementScheduleFli,
        generalInformation: IGeneralInformation,
        printDisplayOptions: IDisplayOptions,
        reportLocale: string,
        translationLocale: string,
        usePreviousYearData: boolean = false,
        generalInformationOfPreviousAnnualReport: IGeneralInformation,
    ): { itemData: INotesMovementScheduleData[]; tableData: INotesMovementScheduleData[] } {
        const itemData: INotesMovementScheduleData[] = [];
        let noPrevious = generalInformation.noPreviousData;
        const currency = PdfTronMapperHelper.getCurrencyLabel(
            generalInformation.currency?.name,
            printDisplayOptions.notesRoundToThousands,
        );

        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.notesHideDecimalPlacesValues,
            printDisplayOptions.notesHideDecimalPlacesRatios,
            printDisplayOptions.notesRoundToThousands,
        );
        // The last `true` snaps legacy local-midnight values (e.g. 2024-12-30T23:00:00Z) to the nearest
        // day so the correct calendar date renders without any timezone handling; clean dates are unchanged.
        let previousDate = noPrevious
            ? undefined
            : this.dateLocalPipe.transform(generalInformation.balancePreviousYearDate, undefined, undefined, undefined, undefined, true).toString();
        let currentDate = this.dateLocalPipe.transform(generalInformation.balanceSheetDate, undefined, undefined, undefined, undefined, true).toString();
        if (usePreviousYearData) {
            // Use the previous-year Movement Schedule's own stored dates so the table no longer depends
            // on the prior-year annual report being loaded into the store at render time (it is not on a
            // plain reload).
            currentDate = this.dateLocalPipe.transform(movementsScheduleData.currentBalanceYearDate, undefined, undefined, undefined, undefined, true).toString();
            previousDate = noPrevious
                ? undefined
                : this.dateLocalPipe.transform(movementsScheduleData.previousBalanceYearDate, undefined, undefined, undefined, undefined, true).toString();
        }
        const caption =
            fli.caption != null ? fli.caption[translationLocale] || fli.caption[reportLocale] : EMPTY_STRING;
        let rowText = `${caption}`;

        let totalCurrentValue = 0;
        let totalPreviousValue = 0;

        const numericOpenValues = fli.accounts.map((acc) => acc.openValue);
        const numericCloseValues = fli.accounts.map((acc) => acc.closeValue);

        const accountsPreviousValueSum = this.processValues(numericOpenValues);
        const accountsCurrentValueSum = this.processValues(numericCloseValues);
        if (isFinite(accountsPreviousValueSum)) {
            totalPreviousValue = NumberUtils.roundNumber(
                PdfTronMapperHelper.scaleValue(accountsPreviousValueSum, valueDivisor),
                false,
                numberOfDecimalPlacesValues,
            );
        }

        if (isFinite(accountsCurrentValueSum)) {
            totalCurrentValue = NumberUtils.roundNumber(
                PdfTronMapperHelper.scaleValue(accountsCurrentValueSum, valueDivisor),
                false,
                numberOfDecimalPlacesValues,
            );
        }

        const fliTablerowData: INotesMovementScheduleData[] = [];

        fli.accounts.forEach((account) => {
            let currentValue: number | null = null;
            if (account.closeValue != null && NumberUtils.isFinite(account.closeValue)) {
                currentValue = NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(account.closeValue, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                );
            }

            let previousValue: number | null = null;
            if (account.openValue != null && NumberUtils.isFinite(account.openValue)) {
                previousValue = NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(account.openValue, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                );
            }

            const formattedPreviousValue =
                previousValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        previousValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;
            const formattedCurrentValue =
                currentValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        currentValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;
            const formattedCol1Value =
                account.column1 != null
                    ? this.aumFinancialNumberPipe.transform(
                        PdfTronMapperHelper.scaleValue(account.column1, valueDivisor),
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;
            const formattedCol2Value =
                account.column2 != null
                    ? this.aumFinancialNumberPipe.transform(
                        PdfTronMapperHelper.scaleValue(account.column2, valueDivisor),
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;
            const formattedCol3Value =
                account.column3 != null
                    ? this.aumFinancialNumberPipe.transform(
                        PdfTronMapperHelper.scaleValue(account.column3, valueDivisor),
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;
            const formattedCol4Value =
                account.column4 != null
                    ? this.aumFinancialNumberPipe.transform(
                        PdfTronMapperHelper.scaleValue(account.column4, valueDivisor),
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : null;

            const fliTablerowDataItem: INotesMovementScheduleData = {
                rowType: RowTypes.ROW,
                t: account.text,
                pV: noPrevious ? undefined : formattedPreviousValue,
                cV: formattedCurrentValue,
                col1: formattedCol1Value,
                col2: formattedCol2Value,
                col3: formattedCol3Value,
                col4: formattedCol4Value,
            };

            fliTablerowData.push(fliTablerowDataItem);
        });

        const formattedTotalPreviousValue =
            totalPreviousValue != null
                ? this.aumFinancialNumberPipe.transform(
                    totalPreviousValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;
        const formattedTotalCurrentValue =
            totalCurrentValue != null
                ? this.aumFinancialNumberPipe.transform(
                    totalCurrentValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;
        const formattedTotalCol1Value =
            fli.totalRow.column1 != null
                ? this.aumFinancialNumberPipe.transform(
                    PdfTronMapperHelper.scaleValue(fli.totalRow.column1, valueDivisor),
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;
        const formattedTotalCol2Value =
            fli.totalRow.column2 != null
                ? this.aumFinancialNumberPipe.transform(
                    PdfTronMapperHelper.scaleValue(fli.totalRow.column2, valueDivisor),
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;
        const formattedTotalCol3Value =
            fli.totalRow.column3 != null
                ? this.aumFinancialNumberPipe.transform(
                    PdfTronMapperHelper.scaleValue(fli.totalRow.column3, valueDivisor),
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;
        const formattedTotalCol4Value =
            fli.totalRow.column4 != null
                ? this.aumFinancialNumberPipe.transform(
                    PdfTronMapperHelper.scaleValue(fli.totalRow.column4, valueDivisor),
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;

        if ((fli as IMovementScheduleFliForNotes).itemType === ItemType.OF_THAT_ITEM) {
            rowText = `${rowText} ${currency} ${formattedTotalCurrentValue} (${currency} ${formattedTotalPreviousValue} ${this.wordBeforeCurrency}) `;
        } else {
            rowText = `${rowText} ${this.wordBeforeCurrency} `;
        }

        let formattedCurrentIncomeYearDate: string | undefined;
        let formattedPreviousIncomeYearDate: string | undefined;

        if (fli.templateType === TemplateType.INCOME) {
            formattedCurrentIncomeYearDate = movementsScheduleData.currentIncomeYearDate.replace(
                SEPERATOR,
                LINE_BREAK + SEPERATOR,
            );
            formattedPreviousIncomeYearDate = movementsScheduleData.previousIncomeYearDate.replace(
                SEPERATOR,
                LINE_BREAK + SEPERATOR,
            );
        }

        const fliTableHeaderData: INotesMovementScheduleData = {
            rowType: RowTypes.TABLE_HEAD,
            t: rowText + currency,
            pV: noPrevious
                ? undefined
                : fli.templateType === TemplateType.INCOME
                    ? formattedPreviousIncomeYearDate
                    : previousDate,
            cV: fli.templateType === TemplateType.INCOME ? formattedCurrentIncomeYearDate : currentDate,
            col1: fli.headers.column1[translationLocale] || fli.headers.column1[reportLocale],
            col2: fli.headers.column2[translationLocale] || fli.headers.column2[reportLocale],
            col3: fli.headers.column3[translationLocale] || fli.headers.column3[reportLocale],
            col4: fli.headers.column4[translationLocale] || fli.headers.column4[reportLocale],
        };
        itemData.push(fliTableHeaderData);
        itemData.push(...fliTablerowData);
        const tableData: INotesMovementScheduleData[] = [];
        tableData.push(fliTableHeaderData);
        tableData.push(...fliTablerowData);

        const fliTableTotalData: INotesMovementScheduleData = {
            rowType: RowTypes.TABLE_TOTAL,
            t: fli.totalRow?.text || this.totalWord,
            pV: noPrevious ? undefined : formattedTotalPreviousValue,
            cV: formattedTotalCurrentValue,
            col1: formattedTotalCol1Value,
            col2: formattedTotalCol2Value,
            col3: formattedTotalCol3Value,
            col4: formattedTotalCol4Value,
        };
        itemData.push(fliTableTotalData);
        // for https://modeso.atlassian.net/browse/REV-14530 add tables in separated form
        tableData.push(fliTableTotalData);

        return { itemData, tableData };
    }

    // FCP
    private mapFcpToSectionData(): ISectionData {
        //TODO, implement in Module
        return {
            sectionId: PDFSectionIds.FCP,
            title: this.fcpSectionTitle,
            hasSignature: false,
            data: {},
        };
    }

    // AOP
    private mapAopToSectionData(
        appropriationOfProfit: IAppropriationOfProfit,
        printOptions: IPrintOptions,
        generalInformation: IGeneralInformation,
        incomeStatement: IFinStatements,
        translationLocale: string,
    ): ISectionData {
        const appropriationOfProfitFinancialData: IFinancialTableData[] = [];
        const tablesData: IFinancialTableData[][] = [];

        const profitCalculationTableFinancialData = this.mapAopProfitCalculationTableToFinancialData(
            appropriationOfProfit,
            generalInformation,
            printOptions.displayOptions,
            incomeStatement,
        );

        appropriationOfProfitFinancialData.push(...profitCalculationTableFinancialData);
        tablesData.push(profitCalculationTableFinancialData);

        const proposalCalculationTableFinancialData = this.mapAopProposalOutputTableToFinancialData(
            appropriationOfProfit,
            generalInformation,
            incomeStatement,
            printOptions.displayOptions,
        );

        appropriationOfProfitFinancialData.push(...proposalCalculationTableFinancialData);
        tablesData.push(proposalCalculationTableFinancialData);

        const ccrTablesData: IFinancialTableData[][] = [];
        if (appropriationOfProfit.ccrOutput != null && appropriationOfProfit.showCcrTable !== false) {
            const ccrTableFinancialData = this.mapAopCcrTableToFinancialData(
                appropriationOfProfit.ccrOutput,
                generalInformation,
                printOptions.displayOptions,
            );
            appropriationOfProfitFinancialData.push(...ccrTableFinancialData);
            ccrTablesData.push(ccrTableFinancialData);
        }

        return {
            sectionId: PDFSectionIds.AOP,
            title:
                printOptions.reportSectionsTitles != null &&
                    printOptions.reportSectionsTitles[SectionIds.AOP] != null &&
                    printOptions.reportSectionsTitles[SectionIds.AOP][translationLocale] != null
                    ? printOptions.reportSectionsTitles[SectionIds.AOP][translationLocale]
                    : this.aopSectionTitle,
            hasSignature: printOptions.signatureOptions.placeSignatureAfterAop,
            data: appropriationOfProfitFinancialData,
            tables: tablesData,
            ccrTables: ccrTablesData.length > 0 ? ccrTablesData : undefined,
            noPrevious: generalInformation.noPreviousData || printOptions.displayOptions.hidePreviousYear,
            showReference: printOptions.displayOptions.showNotes,
            showRatios: printOptions.displayOptions.showRatios,
            description: appropriationOfProfit.aopDescription,
            ccrFootnote: appropriationOfProfit.showCcrTable !== false ? appropriationOfProfit.ccrOutput?.ccrFootnote : undefined,
        };
    }

    public mapAopProfitCalculationTableToFinancialData(
        appropriationOfProfit: IAppropriationOfProfit,
        generalInformation: IGeneralInformation,
        printDisplayOptions: IDisplayOptions,
        incomeStatement: IFinStatements,
    ): IFinancialTableData[] {
        const profitCalculationTableFinancialData: IFinancialTableData[] = [];
        const noPrevious = generalInformation.noPreviousData || printDisplayOptions.hidePreviousYear;
        const hideEmptyLines = printDisplayOptions.hideEmptyLines || printDisplayOptions.aopHideEmptyLines;

        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.aopHideDecimalPlacesValues,
            printDisplayOptions.aopHideDecimalPlacesValues,
            printDisplayOptions.aopRoundToThousands,
        );

        const previousDate = noPrevious
            ? undefined
            : this.dateLocalPipe.transform(generalInformation.balancePreviousYearDate).toString();
        const currentDate = this.dateLocalPipe.transform(generalInformation.balanceSheetDate).toString();

        const profitCalculationTableHeader: IFinancialTableData = {
            rowType: RowTypes.TABLE_HEAD,
            t:
                appropriationOfProfit.profitCalculationOutput?.profitCalcaulationHeaderText +
                SPACE +
                this.wordBeforeCurrency +
                SPACE +
                PdfTronMapperHelper.getCurrencyLabel(
                    generalInformation.currency?.name,
                    printDisplayOptions.aopRoundToThousands,
                ),
            pV: noPrevious ? undefined : previousDate,
            cV: currentDate,
        };

        profitCalculationTableFinancialData.push(profitCalculationTableHeader);

        let profitCalculationAccountValues: IPrintPreviewMappingValue | undefined;
        if (appropriationOfProfit != null && appropriationOfProfit.profitCalculationOutput.account != null) {
            // REV-XXXXX: refresh `closingReClass` / `openingReClass` from the
            // LIVE income-statement account before delegating to the helper.
            // The snapshot stored on `profitCalculationOutput.account` was
            // captured when the user picked the profit account; subsequent
            // edits (e.g. legacy reclasses tab on an old report) don't
            // propagate back here. Looking up the live account by composite
            // key (number + name) keeps the print preview value in sync with
            // whatever's currently displayed everywhere else.
            const refreshedAccount = this.refreshProfitAccountReclasses(
                appropriationOfProfit.profitCalculationOutput.account,
                incomeStatement,
            );
            profitCalculationAccountValues =
                this.aopAdjustmentsAdnReclassesHelper.calculateAccountWithAdjustmentsAndReclasses(
                    refreshedAccount,
                    printDisplayOptions,
                );
        }

        const previousValue =
            profitCalculationAccountValues?.openingValue != null
                ? NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(profitCalculationAccountValues.openingValue, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                )
                : null;
        const currentValue =
            profitCalculationAccountValues?.closingValue != null
                ? NumberUtils.roundNumber(
                    PdfTronMapperHelper.scaleValue(profitCalculationAccountValues.closingValue, valueDivisor),
                    false,
                    numberOfDecimalPlacesValues,
                )
                : null;

        const formattedPreviousValue =
            previousValue != null
                ? this.aumFinancialNumberPipe.transform(
                    previousValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;
        const formattedCurrentValue =
            currentValue != null
                ? this.aumFinancialNumberPipe.transform(
                    currentValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;

        const rowText =
            appropriationOfProfit?.profitCalculationOutput.customAccountName != null &&
                appropriationOfProfit?.profitCalculationOutput.customAccountName.trim().length > EMPTY_ARRAY_LENGTH
                ? appropriationOfProfit?.profitCalculationOutput.customAccountName
                : appropriationOfProfit?.profitCalculationOutput.account != null
                    ? appropriationOfProfit?.profitCalculationOutput.account.name
                    : null;

        const profitCalculationAccountRow: IFinancialTableData = {
            rowType: RowTypes.ROW,
            t: rowText,
            pV: noPrevious ? undefined : formattedPreviousValue?.toString(),
            cV: formattedCurrentValue?.toString(),
        };

        if (
            (hideEmptyLines && profitCalculationAccountRow.pV != null && !NumberUtils.isZero(previousValue)) ||
            (hideEmptyLines && profitCalculationAccountRow.cV != null && !NumberUtils.isZero(currentValue)) ||
            !hideEmptyLines
        ) {
            profitCalculationTableFinancialData.push(profitCalculationAccountRow);
        }

        appropriationOfProfit?.profitCalculationOutput.profitEntries.forEach((entry) => {
            const entryPreviousValue =
                entry.previousValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.previousValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;
            const entryCurrentValue =
                entry.currentValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.currentValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;

            const formattedRowPreviousValue =
                entryPreviousValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        entryPreviousValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : this.aumFinancialNumberPipe.transform(
                        ZERO_NUMBER,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    );
            const formattedRowCurrentValue =
                entryCurrentValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        entryCurrentValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : this.aumFinancialNumberPipe.transform(
                        ZERO_NUMBER,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    );

            const proposalOutputAccountRow: IFinancialTableData = {
                rowType: RowTypes.ROW,
                t: entry.name,
                pV: noPrevious ? undefined : formattedRowPreviousValue,
                cV: formattedRowCurrentValue,
            };

            if (
                (hideEmptyLines && entry.currentValue != null) ||
                (hideEmptyLines && entry.previousValue != null) ||
                !hideEmptyLines
            ) {
                profitCalculationTableFinancialData.push(proposalOutputAccountRow);
            }
        });

        const aopAnnualProfitCurrentValue =
            this.aopAdjustmentsAdnReclassesHelper.calculateCurrentAnnualProfitWithAdjustmentsAndReclasses(
                incomeStatement,
                printDisplayOptions,
            );

        const aopAnnualProfitPreviousValue =
            this.aopAdjustmentsAdnReclassesHelper.calculatePreviousAnnualProfitWithAdjustmentsAndReclasses(
                incomeStatement,
                printDisplayOptions,
            );

        const annualProfitPreviousValue =
            aopAnnualProfitPreviousValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(aopAnnualProfitPreviousValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;
        const annualProfitCurrentValue =
            aopAnnualProfitCurrentValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(aopAnnualProfitCurrentValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;

        const formattedProfitPreviousValue =
            annualProfitPreviousValue != null
                ? this.aumFinancialNumberPipe.transform(
                    annualProfitPreviousValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );
        const formattedProfitCurrentValue =
            annualProfitCurrentValue != null
                ? this.aumFinancialNumberPipe.transform(
                    annualProfitCurrentValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );

        const profitCalculationAnnualProfitRow: IFinancialTableData = {
            rowType: RowTypes.ROW,
            t:
                appropriationOfProfit?.profitCalculationOutput.profitLabel != null
                    ? appropriationOfProfit?.profitCalculationOutput.profitLabel
                    : this.aopAnnualProfitName,
            pV: noPrevious ? undefined : formattedProfitPreviousValue,
            cV: formattedProfitCurrentValue,
        };

        if (
            (hideEmptyLines && annualProfitPreviousValue != null && !NumberUtils.isZero(annualProfitPreviousValue)) ||
            (hideEmptyLines && annualProfitCurrentValue != null && !NumberUtils.isZero(annualProfitCurrentValue)) ||
            !hideEmptyLines
        ) {
            profitCalculationTableFinancialData.push(profitCalculationAnnualProfitRow);
        }

        const currentAvailableRetainedEarnings = this.calculateCurrentAvailableRetainedEarnings(
            appropriationOfProfit?.profitCalculationOutput,
            aopAnnualProfitCurrentValue,
            aopAnnualProfitPreviousValue,
        );

        const previousAvailableRetainedEarnings = this.calculatePreviousAvailableRetainedEarnings(
            appropriationOfProfit?.profitCalculationOutput,
            aopAnnualProfitPreviousValue,
            aopAnnualProfitCurrentValue,
        );

        const availableRetainedEarningsPreviousValue =
            previousAvailableRetainedEarnings != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(previousAvailableRetainedEarnings, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;
        const availableRetainedEarningsCurrentValue =
            currentAvailableRetainedEarnings != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(currentAvailableRetainedEarnings, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;

        const formattedAvailableRetainedEarningsPreviousValue =
            availableRetainedEarningsPreviousValue != null
                ? this.aumFinancialNumberPipe.transform(
                    availableRetainedEarningsPreviousValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;
        const formattedAvailableRetainedEarningsCurrentValue =
            availableRetainedEarningsCurrentValue != null
                ? this.aumFinancialNumberPipe.transform(
                    availableRetainedEarningsCurrentValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : null;

        const proposalOutputAvailableRetainedEarnings: IFinancialTableData = {
            rowType: RowTypes.TABLE_TOTAL,
            t: appropriationOfProfit?.proposalOutput?.proposalAvailableRetainedEarningText,
            pV: noPrevious ? undefined : formattedAvailableRetainedEarningsPreviousValue,
            cV: formattedAvailableRetainedEarningsCurrentValue,
        };

        if (
            (hideEmptyLines && availableRetainedEarningsPreviousValue != null && !NumberUtils.isZero(availableRetainedEarningsPreviousValue)) ||
            (hideEmptyLines && availableRetainedEarningsCurrentValue != null && !NumberUtils.isZero(availableRetainedEarningsCurrentValue)) ||
            !hideEmptyLines
        ) {
            profitCalculationTableFinancialData.push(proposalOutputAvailableRetainedEarnings);
        }

        return profitCalculationTableFinancialData;
    }

    /**
     * REV-XXXXX: refresh `closingReClass` / `openingReClass` on a saved
     * profit-calculation account snapshot using the LIVE income statement.
     *
     * The snapshot was captured at the moment the user picked the profit
     * account on the AoP page; reclass edits made afterwards (legacy
     * reclasses tab on an old report, or new transactional reclasses on a
     * new report) won't have propagated back to that snapshot. The print
     * preview must use the live values to stay consistent with what's
     * shown in every other step.
     *
     * The other fields on the snapshot (`calculatedClosingBalance`,
     * `calculatedOpeningBalance`, `closingAdjustment`, `openingAdjustment`,
     * `name`, etc.) are intentionally NOT refreshed — they are tied to the
     * specific account selection the user made and to the adjustments
     * they entered alongside it.
     *
     * Composite-key lookup (`accountNumber` + `name`) with a fallback to
     * `accountNumber`-alone if no exact match. Returns the original
     * snapshot unchanged when no live account is found, so a recently
     * deleted / renamed account still renders with its last-known values.
     */
    private refreshProfitAccountReclasses(
        savedAccount: IAccount,
        incomeStatement: IFinStatements | null | undefined,
    ): IAccount {
        if (savedAccount == null || incomeStatement == null) return savedAccount;

        // REV-20899: For legacy ARs, reclasses entered in the legacy
        // reclasses tab live on the FLI-mapped account references inside the
        // template (not the raw `templateAndAccountsMapping.accounts` list —
        // those may carry stale or pre-reclass values depending on when the
        // AR was last saved). Walk the template first so we always pick up
        // the same account instance the reclasses editor mutates, then fall
        // back to the raw accounts list as a safety net.
        const liveFromTemplate = this.findAccountInTemplate(
            savedAccount,
            incomeStatement?.templateAndAccountsMapping?.template ?? null,
        );
        if (liveFromTemplate != null) {
            return {
                ...savedAccount,
                closingReClass: liveFromTemplate.closingReClass,
                openingReClass: liveFromTemplate.openingReClass,
            };
        }

        const rawAccounts = incomeStatement?.templateAndAccountsMapping?.accounts;
        if (!Array.isArray(rawAccounts) || rawAccounts.length === 0) return savedAccount;
        const savedName = savedAccount.name ?? '';
        let live = rawAccounts.find(
            (a) => a.accountNumber === savedAccount.accountNumber && (a.name ?? '') === savedName,
        );
        if (live == null) {
            const sameNumber = rawAccounts.filter((a) => a.accountNumber === savedAccount.accountNumber);
            if (sameNumber.length === 1) live = sameNumber[0];
        }
        if (live == null) return savedAccount;
        return {
            ...savedAccount,
            closingReClass: live.closingReClass,
            openingReClass: live.openingReClass,
        };
    }

    /**
     * REV-20899: Walk a template recursively and return the live IAccount
     * matching `savedAccount` by composite key (accountNumber + name) so
     * that the print preview reads the FLI-mapped instance the legacy
     * reclasses editor mutates. Falls back to accountNumber-only when
     * there's a unique match. Returns `undefined` if no live account is
     * found.
     */
    private findAccountInTemplate(
        savedAccount: IAccount,
        template: IAccountMappingTemplate | null,
    ): IAccount | undefined {
        if (template?.group == null || savedAccount == null) return undefined;
        const collected: IAccount[] = [];
        this.collectAccountsFromGroup(template.group, collected);
        if (collected.length === 0) return undefined;
        const savedName = savedAccount.name ?? '';
        const exact = collected.find(
            (a) => a.accountNumber === savedAccount.accountNumber && (a.name ?? '') === savedName,
        );
        if (exact != null) return exact;
        const sameNumber = collected.filter((a) => a.accountNumber === savedAccount.accountNumber);
        if (sameNumber.length === 1) return sameNumber[0];
        return undefined;
    }

    private collectAccountsFromGroup(group: IAccountMappingTemplateGroup, out: IAccount[]): void {
        if (group?.items != null) {
            for (const item of group.items) {
                if (item.type === ItemType.SUB_TOTAL || item.type === ItemType.HEADER || item.type === ItemType.EMPTY) {
                    continue;
                }
                const fli = item as IAccountMappingTemplateGroupItemFLI;
                if (fli.accounts == null) continue;
                for (const acc of fli.accounts) {
                    if (acc == null) continue;
                    if (acc.type === ItemType.SUB_FLI || acc.type === ItemType.OF_THAT_ITEM) {
                        const subFli = acc as IAccountMappingSubFLI;
                        if (subFli.accounts != null) {
                            for (const subAcc of subFli.accounts) {
                                if (subAcc != null) out.push(subAcc);
                            }
                        }
                    } else if (
                        acc.type !== ItemType.EMPTY &&
                        acc.type !== ItemType.HEADER
                    ) {
                        out.push(acc as IAccount);
                    }
                }
            }
        }
        if (group?.groups != null) {
            for (const innerGroup of group.groups) {
                this.collectAccountsFromGroup(innerGroup, out);
            }
        }
    }

    public mapAopProposalOutputTableToFinancialData(
        appropriationOfProfit: IAppropriationOfProfit,
        generalInformation: IGeneralInformation,
        incomeStatement: IFinStatements,
        printDisplayOptions: IDisplayOptions,
    ): IFinancialTableData[] {
        const proposalOutputTableFinancialData: IFinancialTableData[] = [];
        const noPrevious = generalInformation.noPreviousData || printDisplayOptions.hidePreviousYear;
        const hideEmptyLines = printDisplayOptions.hideEmptyLines || printDisplayOptions.aopHideEmptyLines;

        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.aopHideDecimalPlacesValues,
            printDisplayOptions.aopHideDecimalPlacesRatios,
            printDisplayOptions.aopRoundToThousands,
        );

        const previousDate = noPrevious
            ? undefined
            : this.dateLocalPipe.transform(generalInformation.balancePreviousYearDate).toString();
        const currentDate = this.dateLocalPipe.transform(generalInformation.balanceSheetDate).toString();

        const proposalOutputTableHeader: IFinancialTableData = {
            rowType: RowTypes.TABLE_HEAD,
            t:
                appropriationOfProfit?.proposalOutput.proposalDescription != null
                    ? appropriationOfProfit?.proposalOutput.proposalDescription
                    : appropriationOfProfit?.proposalOutput?.proposalAvailableRetainedEarningText +
                    SPACE +
                    this.wordBeforeCurrency +
                    SPACE +
                    PdfTronMapperHelper.getCurrencyLabel(
                        generalInformation.currency?.name,
                        printDisplayOptions.aopRoundToThousands,
                    ),
            pV: noPrevious ? undefined : previousDate,
            cV: currentDate,
        };

        proposalOutputTableFinancialData.push(proposalOutputTableHeader);

        appropriationOfProfit?.proposalOutput.proposalEntries.forEach((entry) => {
            const previousValue =
                entry.previousValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.previousValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;
            const currentValue =
                entry.currentValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.currentValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;

            const formattedPreviousValue =
                previousValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        previousValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : this.aumFinancialNumberPipe.transform(
                        ZERO_NUMBER,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    );
            const formattedCurrentValue =
                currentValue != null
                    ? this.aumFinancialNumberPipe.transform(
                        currentValue,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    )
                    : this.aumFinancialNumberPipe.transform(
                        ZERO_NUMBER,
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    );
            const proposalOutputAccountRow: IFinancialTableData = {
                rowType: RowTypes.ROW,
                t: entry.name,
                pV: noPrevious ? undefined : formattedPreviousValue,
                cV: formattedCurrentValue,
            };

            if (
                (hideEmptyLines && entry.currentValue != null) ||
                (hideEmptyLines && entry.previousValue != null) ||
                !hideEmptyLines
            ) {
                proposalOutputTableFinancialData.push(proposalOutputAccountRow);
            }
        });

        const aopAnnualProfitCurrentValue =
            this.aopAdjustmentsAdnReclassesHelper.calculateCurrentAnnualProfitWithAdjustmentsAndReclasses(
                incomeStatement,
                printDisplayOptions,
            );

        const aopAnnualProfitPreviousValue =
            this.aopAdjustmentsAdnReclassesHelper.calculatePreviousAnnualProfitWithAdjustmentsAndReclasses(
                incomeStatement,
                printDisplayOptions,
            );

        const currentAvailableRetainedEarnings = this.calculateCurrentAvailableRetainedEarnings(
            appropriationOfProfit?.profitCalculationOutput,
            aopAnnualProfitCurrentValue,
            aopAnnualProfitPreviousValue,
        );

        const previousAvailableRetainedEarnings = this.calculatePreviousAvailableRetainedEarnings(
            appropriationOfProfit?.profitCalculationOutput,
            aopAnnualProfitPreviousValue,
            aopAnnualProfitCurrentValue,
        );

        let previousCarriedForwardValue: number | null = null;
        if (previousAvailableRetainedEarnings != null) {
            previousCarriedForwardValue = this.calculatePreviousCarriedForwardValue(
                previousAvailableRetainedEarnings,
                appropriationOfProfit?.proposalOutput.proposalEntries,
            );
        }

        let currentCarriedForwardValue: number | null = null;
        if (currentAvailableRetainedEarnings != null) {
            currentCarriedForwardValue = this.calculateCurrentCarriedForwardValue(
                currentAvailableRetainedEarnings,
                appropriationOfProfit?.proposalOutput.proposalEntries,
            );
        }

        const previousValue =
            previousCarriedForwardValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(previousCarriedForwardValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;
        const currentValue =
            currentCarriedForwardValue != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(currentCarriedForwardValue, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;

        const formattedPreviousValue =
            previousValue != null
                ? this.aumFinancialNumberPipe.transform(
                    previousValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );
        const formattedCurrentValue =
            currentValue != null
                ? this.aumFinancialNumberPipe.transform(
                    currentValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );

        const proposalOutputCarriedForwardRow: IFinancialTableData = {
            rowType: RowTypes.ROW,
            t: appropriationOfProfit.proposalOutput.proposalCarriedForwardText,
            pV: noPrevious ? undefined : formattedPreviousValue,
            cV: formattedCurrentValue,
        };

        if (
            (hideEmptyLines && previousValue != null && !NumberUtils.isZero(previousValue)) ||
            (hideEmptyLines && currentValue != null && !NumberUtils.isZero(currentValue)) ||
            !hideEmptyLines
        ) {
            proposalOutputTableFinancialData.push(proposalOutputCarriedForwardRow);
        }

        const previousAvailableRetainedEarningsValue =
            previousAvailableRetainedEarnings != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(previousAvailableRetainedEarnings, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;
        const currentAvailableRetainedEarningsValue =
            currentAvailableRetainedEarnings != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(currentAvailableRetainedEarnings, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null;

        const formattedPreviousAvailableRetainedEarningsValue =
            previousAvailableRetainedEarningsValue != null
                ? this.aumFinancialNumberPipe.transform(
                    previousAvailableRetainedEarningsValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );
        const formattedCurrentAvailableRetainedEarningsValue =
            currentAvailableRetainedEarningsValue != null
                ? this.aumFinancialNumberPipe.transform(
                    currentAvailableRetainedEarningsValue,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                )
                : this.aumFinancialNumberPipe.transform(
                    ZERO_NUMBER,
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );

        const proposalOutputAvailableRetainedEarnings: IFinancialTableData = {
            rowType: RowTypes.TABLE_TOTAL,
            t: appropriationOfProfit?.proposalOutput.proposalAvailableRetainedEarningText,
            pV: noPrevious ? undefined : formattedPreviousAvailableRetainedEarningsValue,
            cV: formattedCurrentAvailableRetainedEarningsValue,
        };

        if (
            (hideEmptyLines && previousAvailableRetainedEarningsValue != null && !NumberUtils.isZero(previousAvailableRetainedEarningsValue)) ||
            (hideEmptyLines && currentAvailableRetainedEarningsValue != null && !NumberUtils.isZero(currentAvailableRetainedEarningsValue)) ||
            !hideEmptyLines
        ) {
            proposalOutputTableFinancialData.push(proposalOutputAvailableRetainedEarnings);
        }

        return proposalOutputTableFinancialData;
    }

    public mapAopCcrTableToFinancialData(
        ccr: IAopCcr,
        generalInformation: IGeneralInformation,
        printDisplayOptions: IDisplayOptions,
    ): IFinancialTableData[] {
        const ccrTableData: IFinancialTableData[] = [];
        const noPrevious = generalInformation.noPreviousData || printDisplayOptions.hidePreviousYear;

        const { numberOfDecimalPlacesValues, numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.aopHideDecimalPlacesValues,
            printDisplayOptions.aopHideDecimalPlacesValues,
            printDisplayOptions.aopRoundToThousands,
        );

        const previousDate = noPrevious
            ? undefined
            : this.dateLocalPipe.transform(generalInformation.balancePreviousYearDate).toString();
        const currentDate = this.dateLocalPipe.transform(generalInformation.balanceSheetDate).toString();

        // ── Header ──────────────────────────────────────────────────────────────
        ccrTableData.push({
            rowType: RowTypes.TABLE_HEAD,
            t: ccr.ccrDescription ?? '',
            pV: noPrevious ? undefined : previousDate,
            cV: currentDate,
        });

        // ── Derive balance values from the stored equity account ────────────────
        // CY opening  = account calculatedOpeningBalance
        // CY closing  = account calculatedClosingBalance  (actual account value — not recomputed)
        // PY closing  = CY opening  (end of previous year = beginning of current year)
        // PY opening  = PY closing – Σ(entry.previousValue)  (derived; valid when diff = 0)
        // Fallbacks: when no account is linked the user may have entered the PY balances manually;
        // these are stored as extra fields (manualPreviousOpeningBalance / manualPreviousClosingBalance).
        const ccrPersisted = ccr as IAopCcr & { manualPreviousOpeningBalance?: number | null; manualPreviousClosingBalance?: number | null };
        // REV-20899 / REV-21191: include transactional reclasses in displayed balances.
        // Supports multiple equity accounts (equityAccounts[]) with fallback to legacy equityAccount.
        const resolvedCcrAccounts = ccr.equityAccounts != null
            ? ccr.equityAccounts
            : (ccr.equityAccount ? [ccr.equityAccount] : []);

        const ccrOpeningValues = resolvedCcrAccounts.map((a) => {
            const rc = a.openingReClass != null && NumberUtils.isFinite(a.openingReClass) ? a.openingReClass : 0;
            return a.calculatedOpeningBalance != null ? a.calculatedOpeningBalance + rc : (rc !== 0 ? rc : null);
        });
        const openingCurRaw = ccrOpeningValues.every((v) => v == null)
            ? null
            : NumberUtils.roundNumber(
                  PdfTronMapperHelper.scaleValue(
                      ccrOpeningValues.reduce<number>((s, v) => s + (v ?? 0), 0),
                      valueDivisor,
                  ),
                  false,
                  numberOfDecimalPlacesValues,
              );

        const ccrClosingValues = resolvedCcrAccounts.map((a) => {
            const rc = a.closingReClass != null && NumberUtils.isFinite(a.closingReClass) ? a.closingReClass : 0;
            return a.calculatedClosingBalance != null ? a.calculatedClosingBalance + rc : (rc !== 0 ? rc : null);
        });
        const closingCurRaw = ccrClosingValues.every((v) => v == null)
            ? null
            : NumberUtils.roundNumber(
                  PdfTronMapperHelper.scaleValue(
                      ccrClosingValues.reduce<number>((s, v) => s + (v ?? 0), 0),
                      valueDivisor,
                  ),
                  false,
                  numberOfDecimalPlacesValues,
              );
        const closingPrevRaw = !noPrevious
            ? openingCurRaw != null
                ? openingCurRaw
                : ccrPersisted.manualPreviousClosingBalance != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(ccrPersisted.manualPreviousClosingBalance, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null
            : null;
        const sumPrev = (ccr.ccrEntries ?? []).reduce((acc, e) => acc + (e.previousValue ?? 0), 0);
        const openingPrevRaw = !noPrevious
            ? ccrPersisted.manualPreviousOpeningBalance != null
                ? NumberUtils.roundNumber(
                      PdfTronMapperHelper.scaleValue(ccrPersisted.manualPreviousOpeningBalance, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : closingPrevRaw != null
                ? NumberUtils.roundNumber(
                      // closingPrevRaw is already in the print unit; only the raw entry sum needs scaling.
                      closingPrevRaw - PdfTronMapperHelper.scaleValue(sumPrev, valueDivisor),
                      false,
                      numberOfDecimalPlacesValues,
                  )
                : null
            : null;

        // ── Opening balance row ─────────────────────────────────────────────────
        const openingCurFormatted = openingCurRaw != null
            ? this.aumFinancialNumberPipe.transform(openingCurRaw, numberFormatValues, printDisplayOptions.nonBreakingHyphen)
            : this.aumFinancialNumberPipe.transform(ZERO_NUMBER, numberFormatValues, printDisplayOptions.nonBreakingHyphen);

        const openingPrevFormatted = openingPrevRaw != null
            ? this.aumFinancialNumberPipe.transform(openingPrevRaw, numberFormatValues, printDisplayOptions.nonBreakingHyphen)
            : undefined;

        ccrTableData.push({
            rowType: RowTypes.ROW,
            t: ccr.totalCapitalContributionsAtTheBeginningOfTheYearText ?? '',
            pV: openingPrevFormatted,
            cV: openingCurFormatted,
        });

        // ── Entry rows ─────────────────────────────────────────────────────────
        (ccr.ccrEntries ?? []).forEach((entry) => {
            const prevVal =
                entry.previousValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.previousValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;
            const curVal =
                entry.currentValue != null
                    ? NumberUtils.roundNumber(
                          PdfTronMapperHelper.scaleValue(entry.currentValue, valueDivisor),
                          false,
                          numberOfDecimalPlacesValues,
                      )
                    : null;

            const formattedPrev = prevVal != null
                ? this.aumFinancialNumberPipe.transform(prevVal, numberFormatValues, printDisplayOptions.nonBreakingHyphen)
                : this.aumFinancialNumberPipe.transform(ZERO_NUMBER, numberFormatValues, printDisplayOptions.nonBreakingHyphen);
            const formattedCur = curVal != null
                ? this.aumFinancialNumberPipe.transform(curVal, numberFormatValues, printDisplayOptions.nonBreakingHyphen)
                : this.aumFinancialNumberPipe.transform(ZERO_NUMBER, numberFormatValues, printDisplayOptions.nonBreakingHyphen);

            ccrTableData.push({
                rowType: RowTypes.ROW,
                t: entry.name ?? '',
                pV: noPrevious ? undefined : formattedPrev,
                cV: formattedCur,
            });
        });

        // ── Closing balance row ─────────────────────────────────────────────────
        const closingCur = closingCurRaw;
        const closingPrev = closingPrevRaw;

        const closingCurFormatted = closingCur != null
            ? this.aumFinancialNumberPipe.transform(closingCur, numberFormatValues, printDisplayOptions.nonBreakingHyphen)
            : this.aumFinancialNumberPipe.transform(ZERO_NUMBER, numberFormatValues, printDisplayOptions.nonBreakingHyphen);
        const closingPrevFormatted = closingPrev != null
            ? this.aumFinancialNumberPipe.transform(closingPrev, numberFormatValues, printDisplayOptions.nonBreakingHyphen)
            : undefined;

        ccrTableData.push({
            rowType: RowTypes.TABLE_TOTAL,
            t: ccr.totalCapitalContributionsAtTheEndOfTheYearText ?? '',
            pV: closingPrevFormatted,
            cV: closingCurFormatted,
        });

        return ccrTableData;
    }

    private calculateCurrentAvailableRetainedEarnings(
        profitCalculationOutput: IAopProfitCalculation,
        aopAnnualProfitCurrentValue: number | null,
        aopAnnualProfitPreviousValue: number | null,
    ): number | null {
        let currentAvailableRetainedEarnings: number | null;
        const profitCalculationTableData: IProfitCalculationTableData[] = [];

        profitCalculationTableData.push({
            name: profitCalculationOutput?.account != null ? profitCalculationOutput.account : null,
            currentValue:
                profitCalculationOutput?.account?.calculatedClosingBalance != null
                    ? profitCalculationOutput.account.calculatedClosingBalance
                    : null,
            previousValue:
                profitCalculationOutput?.account?.calculatedOpeningBalance != null
                    ? profitCalculationOutput.account.calculatedOpeningBalance
                    : null,
        });

        if (profitCalculationOutput != null) {
            profitCalculationTableData.push(...profitCalculationOutput.profitEntries);
        }

        profitCalculationTableData.push({
            name: profitCalculationOutput?.profitLabel,
            currentValue: aopAnnualProfitCurrentValue,
            previousValue: aopAnnualProfitPreviousValue,
        });
        profitCalculationTableData.push({
            name: profitCalculationOutput.availableRetainedEarningsText,
            currentValue: null,
            previousValue: null,
        });

        const entriesValue = profitCalculationTableData.map((entry) => entry.currentValue);
        const sumOfEntriesValue = NumberUtils.sumIgnoringInfiniteNumbers(entriesValue, undefined);

        const lastRowValue = profitCalculationTableData[profitCalculationTableData.length - ONE].currentValue;

        let lastRowRoundedValue: number | null = null;
        if (lastRowValue != null && NumberUtils.isFinite(lastRowValue)) {
            lastRowRoundedValue = NumberUtils.roundNumber(lastRowValue);
        }

        if (
            (sumOfEntriesValue != null && NumberUtils.isFinite(sumOfEntriesValue)) ||
            (lastRowRoundedValue != null && NumberUtils.isFinite(lastRowRoundedValue))
        ) {
            currentAvailableRetainedEarnings = NumberUtils.subtractNumbers(
                sumOfEntriesValue ?? 0,
                lastRowRoundedValue ?? 0,
            );
        } else {
            currentAvailableRetainedEarnings = null;
        }

        return currentAvailableRetainedEarnings;
    }
    private calculateCurrentCarriedForwardValue(
        currentAvailableRetainedEarnings: number,
        proposalEntries: IAppropriationOfProfitEntry[],
    ): number | null {
        let currentCarriedForwardValue: number | null;

        const carriedValues: (number | null)[] = [];
        if (proposalEntries.length !== EMPTY_ARRAY_LENGTH) {
            for (let index = FIRST_ELEMENT_INDEX; index < proposalEntries.length; index++) {
                carriedValues.push(proposalEntries[index].currentValue);
            }
        }

        const sumOfCarriedValues = NumberUtils.sumIgnoringInfiniteNumbers(carriedValues, undefined);

        if (
            (currentAvailableRetainedEarnings == null || !NumberUtils.isFinite(currentAvailableRetainedEarnings)) &&
            (sumOfCarriedValues == null || NumberUtils.isFinite(sumOfCarriedValues))
        ) {
            currentCarriedForwardValue = null;
        } else {
            currentCarriedForwardValue = NumberUtils.subtractNumbers(
                currentAvailableRetainedEarnings ?? 0,
                sumOfCarriedValues ?? 0,
            );
        }

        return currentCarriedForwardValue;
    }

    private calculatePreviousAvailableRetainedEarnings(
        profitCalculationOutput: IAopProfitCalculation,
        aopAnnualProfitPreviousValue: number | null,
        aopAnnualProfitCurrentValue: number | null,
    ): number | null {
        let previousAvailableRetainedEarnings: number | null;
        const profitCalculationTableData: IProfitCalculationTableData[] = [];

        profitCalculationTableData.push({
            name: profitCalculationOutput?.account != null ? profitCalculationOutput.account : null,
            currentValue:
                profitCalculationOutput?.account?.calculatedClosingBalance != null
                    ? profitCalculationOutput.account.calculatedClosingBalance
                    : null,
            previousValue:
                profitCalculationOutput?.account?.calculatedOpeningBalance != null
                    ? profitCalculationOutput.account.calculatedOpeningBalance
                    : null,
        });

        if (profitCalculationOutput != null) {
            profitCalculationTableData.push(...profitCalculationOutput.profitEntries);
        }

        profitCalculationTableData.push({
            name: profitCalculationOutput?.profitLabel,
            currentValue: aopAnnualProfitCurrentValue,
            previousValue: aopAnnualProfitPreviousValue,
        });

        profitCalculationTableData.push({
            name: profitCalculationOutput.availableRetainedEarningsText,
            currentValue: null,
            previousValue: null,
        });

        const entriesValue = profitCalculationTableData.map((entry) => entry.previousValue);
        const sumOfEntriesValue = NumberUtils.sumIgnoringInfiniteNumbers(entriesValue, undefined);

        const lastRowValue = profitCalculationTableData[profitCalculationTableData.length - ONE].previousValue;
        let lastRowRoundedValue: number | null = null;
        if (lastRowValue != null && NumberUtils.isFinite(lastRowValue)) {
            lastRowRoundedValue = NumberUtils.roundNumber(lastRowValue);
        }

        if (
            (sumOfEntriesValue != null && NumberUtils.isFinite(sumOfEntriesValue)) ||
            (lastRowRoundedValue != null && NumberUtils.isFinite(lastRowRoundedValue))
        ) {
            previousAvailableRetainedEarnings = NumberUtils.subtractNumbers(
                sumOfEntriesValue ?? 0,
                lastRowRoundedValue ?? 0,
            );
        } else {
            previousAvailableRetainedEarnings = null;
        }

        return previousAvailableRetainedEarnings;
    }

    private calculatePreviousCarriedForwardValue(
        previousAvailableRetainedEarnings: number,
        proposalEntries: IAppropriationOfProfitEntry[],
    ): number | null {
        let previousCarriedForwardValue: number | null;

        const carriedValues: (number | null)[] = [];

        if (proposalEntries != null) {
            for (let index = FIRST_ELEMENT_INDEX; index < proposalEntries.length; index++) {
                carriedValues.push(proposalEntries[index].previousValue);
            }
        }

        const sumOfCarriedValues = NumberUtils.sumIgnoringInfiniteNumbers(carriedValues, undefined);

        if (
            (previousAvailableRetainedEarnings == null || !NumberUtils.isFinite(previousAvailableRetainedEarnings)) &&
            (sumOfCarriedValues == null || NumberUtils.isFinite(sumOfCarriedValues))
        ) {
            previousCarriedForwardValue = null;
        } else {
            previousCarriedForwardValue = NumberUtils.subtractNumbers(
                previousAvailableRetainedEarnings ?? 0,
                sumOfCarriedValues ?? 0,
            );
        }

        return previousCarriedForwardValue;
    }

    // EMS
    private mapEmsToSectionData(
        annualReport: IAnnualReport,
        finStatements: IFinStatements[],
        printOptions: IPrintOptions,
        locale: string,
        translationLocale: string,
    ): ISectionData {
        if (printOptions == null || annualReport.generalInformation == null) {
            throw new Error('Print options or general information is null or undefined');
        }

        if (annualReport.equityMovementSchedule == null) {
            throw new Error('EMS is null or undefined');
        }

        const equityMovementScheduleDates = EquityMovementScheduleHelper.prepareEquityMovementScheduleDates(
            annualReport.generalInformation,
            locale,
        );

        const balanceMapping: ITemplateAndAccountsMapping = cloneDeep<ITemplateAndAccountsMapping>(
            finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping,
        );
        const balanceMappingTemplate: IAccountMappingTemplate | null = cloneDeep<IAccountMappingTemplate | null>(
            balanceMapping?.template,
        );

        const equityGroupFlis = EquityMovementScheduleHelper.extractFlisFromEquityGroup(
            [balanceMappingTemplate.group],
            balanceMappingTemplate.equityGroupName,
        );

        const mappingFlis = EquityMovementScheduleHelper.getMovementScheduleFliInputData(equityGroupFlis, null);

        // Normalize like the edit page so pre-REV-21541 data prints the same columns.
        const normalizedEntries = EquityMovementScheduleHelper.normalizeEntries(
            annualReport.equityMovementSchedule.equityMovementEntries,
        );
        const columnCaptions = EquityMovementScheduleHelper.buildEmsPrintColumnCaptions(normalizedEntries, mappingFlis);

        const currency =
            annualReport.generalInformation?.currency?.name != null
                ? this.wordBeforeCurrency +
                  SPACE +
                  PdfTronMapperHelper.getCurrencyLabel(
                      annualReport.generalInformation.currency?.name,
                      printOptions.displayOptions.finStatementRoundToThousands,
                  )
                : EMPTY_STRING;

        const hasNoPreviousData = !!annualReport.generalInformation.noPreviousData;
        const rowTitles = EquityMovementScheduleHelper.resolveEmsRowTitles(
            annualReport.equityMovementSchedule,
            equityMovementScheduleDates,
            hasNoPreviousData,
        );
        const tables: IEquityMovementSchedulePeriodTable[] = [];

        if (!hasNoPreviousData && !printOptions.displayOptions.hidePreviousYear) {
            tables.push(
                this.buildEmsPeriodTable(
                    EquityMovementScheduleHelper.buildEmsPeriodCaption(
                        this.emsBusinessYearLabel ?? EMPTY_STRING,
                        equityMovementScheduleDates.previousPeriodIsFullYear,
                        equityMovementScheduleDates.previousYearOpeningDate,
                        equityMovementScheduleDates.previousYearClosingDate,
                    ),
                    currency,
                    columnCaptions,
                    EquityMovementScheduleHelper.buildEmsPrintPeriodRows(
                        normalizedEntries,
                        mappingFlis,
                        'previous',
                        hasNoPreviousData,
                    ),
                    this.emsRowLabel(
                        rowTitles.previousOpeningTitle,
                        equityMovementScheduleDates.previousYearOpeningDate,
                    ),
                    this.emsRowLabel(
                        rowTitles.previousClosingTitle,
                        equityMovementScheduleDates.previousYearClosingDate,
                    ),
                    printOptions.displayOptions,
                ),
            );
        }

        // First year: the opening date lives in the previous-year slot.
        const currentYearOpeningDate =
            equityMovementScheduleDates.currentYearOpeningDate ?? equityMovementScheduleDates.previousYearOpeningDate;
        tables.push(
            this.buildEmsPeriodTable(
                EquityMovementScheduleHelper.buildEmsPeriodCaption(
                    this.emsBusinessYearLabel ?? EMPTY_STRING,
                    equityMovementScheduleDates.currentPeriodIsFullYear,
                    currentYearOpeningDate,
                    equityMovementScheduleDates.currentYearClosingDate,
                ),
                currency,
                columnCaptions,
                EquityMovementScheduleHelper.buildEmsPrintPeriodRows(
                    normalizedEntries,
                    mappingFlis,
                    'current',
                    hasNoPreviousData,
                ),
                this.emsRowLabel(rowTitles.currentOpeningTitle, currentYearOpeningDate),
                this.emsRowLabel(
                    rowTitles.currentClosingTitle,
                    equityMovementScheduleDates.currentYearClosingDate,
                ),
                printOptions.displayOptions,
            ),
        );

        const equityMovementScheduleData: IEquityMovementScheduleData = {
            tables,
            textAdditionAboveEms: annualReport.equityMovementSchedule.textAdditionAboveEms,
            textAdditionBelowEms: annualReport.equityMovementSchedule.textAdditionBelowEms,
        };

        return {
            sectionId: PDFSectionIds.EMS,
            title:
                printOptions.reportSectionsTitles != null &&
                    printOptions.reportSectionsTitles[SectionIds.EMS] != null &&
                    printOptions.reportSectionsTitles[SectionIds.EMS][translationLocale] != null
                    ? printOptions.reportSectionsTitles[SectionIds.EMS][translationLocale]
                    : this.emsSectionTitle,
            data: equityMovementScheduleData,
            noPrevious: annualReport.generalInformation.noPreviousData || printOptions.displayOptions.hidePreviousYear,
            showReference: printOptions.displayOptions.showNotes,
            showRatios: printOptions.displayOptions.showRatios,
        };
    }

    /** Saved titles already carry their date; only the fallback needs it appended. */
    private emsRowLabel(savedTitle: string | null | undefined, date: string | undefined): string {
        if (savedTitle != null && savedTitle !== EMPTY_STRING) {
            return savedTitle;
        }
        const fallbackLabel = this.emsRowTitleLabel ?? EMPTY_STRING;
        return date != null ? fallbackLabel + SPACE + date : fallbackLabel;
    }

    private buildEmsPeriodTable(
        caption: string,
        currencyHeader: string,
        columnCaptions: Array<string | null>,
        periodRows: IEmsPrintPeriodRows,
        openingLabel: string,
        closingLabel: string,
        printDisplayOptions: IDisplayOptions,
    ): IEquityMovementSchedulePeriodTable {
        const rows: IEquityMovementScheduleFinancialTableData[] = [];

        const tableHeader: IEquityMovementScheduleFinancialTableData = {
            rowType: RowTypes.TABLE_HEAD,
            t: currencyHeader,
        };
        let columnsCount = ONE;
        columnCaptions.forEach((columnCaption) => {
            tableHeader[`col${columnsCount}`] = columnCaption ?? this.emsTableTotalEquityHeader;
            columnsCount++;
        });
        rows.push(tableHeader);

        rows.push(
            this.mapEmsMovementsTotalRowToTableData(
                periodRows.opening,
                RowTypes.GROUP_TOTAL,
                openingLabel,
                printDisplayOptions,
            ),
        );
        rows.push(...this.mapEmsMovementsToTableData(periodRows.movements, printDisplayOptions));
        rows.push(
            this.mapEmsMovementsTotalRowToTableData(
                periodRows.closing,
                RowTypes.TABLE_TOTAL,
                closingLabel,
                printDisplayOptions,
            ),
        );

        // colCount keeps the pre-existing convention: value columns + the name column.
        return { caption, rows, colCount: columnsCount };
    }

    private mapEmsMovementsToTableData(
        movementsRows: IEquityMovementSchedulePrintPreviewTableRow[],
        printDisplayOptions: IDisplayOptions,
    ): IEquityMovementScheduleFinancialTableData[] {
        const movementsRowsData: IEquityMovementScheduleFinancialTableData[] = [];
        const { numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.finStatementHideDecimalPlacesValues,
            printDisplayOptions.finStatementHideDecimalPlacesRatios,
            printDisplayOptions.finStatementRoundToThousands,
        );

        movementsRows.forEach((movementRow) => {
            const movementRowFinancialData: IEquityMovementScheduleFinancialTableData = {
                rowType: RowTypes.ROW,
                t: movementRow.movementName != null ? movementRow.movementName : EMPTY_STRING,
            };

            let colCount = ONE;

            for (const fliName in movementRow.flis) {
                if (movementRow.flis.hasOwnProperty(fliName)) {
                    movementRowFinancialData[`col${colCount}`] = this.aumFinancialNumberPipe.transform(
                        PdfTronMapperHelper.scaleValue(movementRow.flis[fliName] ?? 0, valueDivisor),
                        numberFormatValues,
                        printDisplayOptions.nonBreakingHyphen,
                    );
                    colCount++;
                }
            }

            movementsRowsData.push(movementRowFinancialData);
        });

        return movementsRowsData;
    }
    private mapNotesSelectToSubSectionItem(
        component: INotesComponent,
        generalInformation: IGeneralInformation,
        translationLocale: string,
        reportLocale: string,
        hasFootnotes: boolean,
    ): IItem {
        const selectData: INotesSelectComponent = component.data;

        const itemData: IFinancialTableData[] = [];

        const previousDate = generalInformation.noPreviousData
            ? undefined
            : this.dateLocalPipe.transform(generalInformation.balancePreviousYearDate).toString();
        const currentDate = this.dateLocalPipe.transform(generalInformation.balanceSheetDate).toString();

        const title = selectData.options.title?.[translationLocale] ?? selectData.options.title?.[reportLocale];

        const tableHeaderData: IFinancialTableData = {
            rowType: RowTypes.TABLE_HEAD,
            t: title ?? EMPTY_STRING,
            pV: generalInformation.noPreviousData ? undefined : previousDate,
            cV: currentDate,
        };
        itemData.push(tableHeaderData);

        const tableRowData: IFinancialTableData = {
            rowType: RowTypes.ROW,
            t: selectData.options.text[translationLocale] || selectData.options.text[reportLocale],
            pV: generalInformation.noPreviousData ? undefined : selectData.previousYearValue,
            cV: selectData.currentYearValue,
        };

        itemData.push(tableRowData);

        // Footnotes
        if (hasFootnotes && selectData.footnotes && selectData.footnotes.length > 0 && selectData.footnotes[0]) {
            const fliFootnoteData: IFinancialTableData = {
                rowType: RowTypes.FOOTNOTE,
                t: selectData.footnotes[0],
            };
            itemData.push(fliFootnoteData);
        }

        const subSectionItem: IItem = {
            itemType: NotesItemsTypes.SELECT,
            data: itemData,
            componentTitle: component.componentTitle
                ? `${component.componentTitle[translationLocale]}`.trim() ||
                `${component.componentTitle[reportLocale]}`.trim()
                : EMPTY_STRING,
        };

        return subSectionItem;
    }
    private mapEmsMovementsTotalRowToTableData(
        totalRow: IEquityMovementSchedulePrintPreviewTableRow,
        rowType: RowTypes,
        t: string,
        printDisplayOptions: IDisplayOptions,
    ): IEquityMovementScheduleFinancialTableData {
        const { numberFormatValues, valueDivisor } = this.getNumberFormatAndDecimalPlaces(
            printDisplayOptions,
            printDisplayOptions.finStatementHideDecimalPlacesValues,
            printDisplayOptions.finStatementHideDecimalPlacesRatios,
            printDisplayOptions.finStatementRoundToThousands,
        );

        const movementsTableTotalRow: IEquityMovementScheduleFinancialTableData = {
            rowType: rowType,
            t,
        };

        let colCount = ONE;

        for (const fliName in totalRow.flis) {
            if (totalRow.flis.hasOwnProperty(fliName)) {
                // EMS only: an empty number cell prints as 0.00 rather than blank (REV-21541).
                movementsTableTotalRow[`col${colCount}`] = this.aumFinancialNumberPipe.transform(
                    PdfTronMapperHelper.scaleValue(totalRow.flis[fliName] ?? 0, valueDivisor),
                    numberFormatValues,
                    printDisplayOptions.nonBreakingHyphen,
                );
                colCount++;
            }
        }

        return movementsTableTotalRow;
    }

    private mapFasToSectionData(
        annualReport: IAnnualReport,
        finStatements: IFinStatements[],
        predifinedLabels: Map<string, string>,
        printOptions: IPrintOptions | undefined,
        translationLocale: string,
    ): ISectionData {
        if (printOptions == null || annualReport.generalInformation == null) {
            throw new Error('Print options or general information is null or undefined');
        }

        const noPrevious = PreviousPeriodHelper.hasNoPreviousPeriod(annualReport.generalInformation);
        const tableData = this.fixedAssetsScheduleMapper.mapFixedAssetsScheduleToFixedAssetsScheduleTableDataForPrint(
            annualReport.fixedAssetsSchedule,
            finStatements[templateConstants.balanceTemplate].templateAndAccountsMapping.template,
            annualReport.generalInformation.balancePreviousYearDate,
            annualReport.generalInformation.balanceSheetDate,

            printOptions.displayOptions.finStatementHideDecimalPlacesValues,
            printOptions.displayOptions.finStatementRoundToThousands,
            printOptions.displayOptions.hideEmptyLines,
            annualReport.generalInformation.currency.name,
            predifinedLabels,
            printOptions.displayOptions.nonBreakingHyphen,
            noPrevious,
            annualReport.generalInformation.incomeCurrentYearFromDate,
        );

        return {
            sectionId: PDFSectionIds.FAS,
            title:
                printOptions.reportSectionsTitles != null &&
                    printOptions.reportSectionsTitles[SectionIds.FAS] != null &&
                    printOptions.reportSectionsTitles[SectionIds.FAS][translationLocale] != null
                    ? printOptions.reportSectionsTitles[SectionIds.FAS][translationLocale]
                    : this.fasSectionTitle,
            // The column also follows the print option; the dates above do not.
            noPrevious: PreviousPeriodHelper.hidesPreviousPeriod(
                annualReport.generalInformation,
                printOptions.displayOptions,
            ),
            data: tableData.rows,
        };
    }

    // Complementary Reports
    public mapComplementaryReportToSectionData(
        complementaryReport: IComplementaryReport,
        complementaryReportIndex: string,
    ): ISectionData {
        return {
            sectionId: (PDFSectionIds.COMPLEMENTARY_REPORT + complementaryReportIndex) as PDFSectionIds,
            title: complementaryReport.title,
            data: HtmlContentMapper.replaceCKTablesWithTablesForDocxTemplater(complementaryReport.content),
            isLast: false,
            printLocation: complementaryReport.printLocation,
            sortOrderId: complementaryReport.sortOrderId,
            showInTOC: complementaryReport.showInTOC,
            fileId: complementaryReport.fileId,
            fileName: complementaryReport.fileName,
            showPageNumbers: complementaryReport.showPageNumbers,
            showTitleInReport: complementaryReport.showTitleInReport ?? true,
        };
    }

    private getNumberFormatAndDecimalPlaces(
        printDisplayOptions: IDisplayOptions,
        hideDecimalPlacesValues: boolean,
        hideDecimalPlacesRatios: boolean,
        roundToThousands: boolean,
        customDecimalPlaces?: number,
    ): {
        numberOfDecimalPlacesValues: number;
        numberFormatValues: string;
        numberOfDecimalPlacesRatios: number;
        numberFormatRatios: string;
        valueDivisor: number;
    } {
        let numberOfDecimalPlacesValues = customDecimalPlaces != null ? customDecimalPlaces : TWO_DECIMAL_PLACES;
        let numberOfDecimalPlacesRatios = customDecimalPlaces != null ? customDecimalPlaces : TWO_DECIMAL_PLACES;
        let numberFormatValues = '1.2-2';
        let numberFormatRatios = '1.2-2';
        if (hideDecimalPlacesValues) {
            numberOfDecimalPlacesValues = ZERO_DECIMAL_PLACES;
            numberFormatValues = '1.0-0';
        }
        if (hideDecimalPlacesRatios) {
            numberOfDecimalPlacesRatios = ZERO_DECIMAL_PLACES;
            numberFormatRatios = '1.0-0';
        }

        return {
            numberOfDecimalPlacesValues,
            numberFormatValues,
            numberOfDecimalPlacesRatios,
            numberFormatRatios,
            valueDivisor: PdfTronMapperHelper.getValueDivisor(roundToThousands),
        };
    }

    private isItemHasNonZeroValue(currentValue: number, previousValue: number, hidePreviousYear = false): boolean {
        const isCurrentValueNonZeroAndFiniteNumber = currentValue != null && !NumberUtils.isZero(currentValue);
        const isPreviousValueNonZeroAndFiniteNumber = previousValue != null && !NumberUtils.isZero(previousValue);
        return isCurrentValueNonZeroAndFiniteNumber || (isPreviousValueNonZeroAndFiniteNumber && !hidePreviousYear);
    }

    private isGroup(
        obj: IAccountMappingTemplateGroup | AccountMappingTemplateGroupItem,
    ): obj is IAccountMappingTemplateGroup {
        return 'groups' in obj;
    }

    private extractNumber(id: string): number | null {
        const match = id.match(/\d+$/);
        return match ? parseInt(match[0], 10) : null;
    }

    private getNotesDetailsForToc(notesSection: ISectionData): INotesDetails[] {
        const notesDetails: INotesDetails[] = [];

        notesSection.subsections.forEach((subsection, index) => {
            const notesComponents = subsection.items
                .map((item) => {
                    const title = item.componentTitle;
                    if (title == null || title.trim().length === 0) return null;

                    const trimmedTitle = title.trim();

                    // Regex seacrches for examle "1.1 Text", "2.3.4 Subtest"
                    const match = trimmedTitle.match(/^(\d+(?:\.\d+)+)\s+(.*)/);

                    if (match) {
                        return {
                            disableNumbering: false,
                            number: match[1],
                            title: match[2],
                        };
                    } else {
                        return {
                            disableNumbering: true,
                            number: null,
                            title: trimmedTitle,
                        };
                    }
                })
                .filter((comp) => comp !== null);

            const title = {
                title: subsection.title ?? '',
                disableNumbering: false,
                number: index + 1 + '.',
            };
            notesDetails.push({
                title: title,
                components: notesComponents,
            });
        });

        return notesDetails;
    }

    private hasNonZeroSubFli(item: IAccountMappingTemplateGroupItemFLI, printOptions: IPrintOptions): boolean {
        if (item.accounts == null || item.accounts.length === 0) {
            return false;
        }

        for (const account of item.accounts) {
            if (account.type === ItemType.SUB_FLI) {
                const subFli = account as IAccountMappingTemplateGroupItemFLI;
                const subFliValues = this.accountMappingDisplayHelper.getOpeningAndClosingValues(
                    subFli,
                    printOptions.displayOptions.showReClassInSums,
                    printOptions.displayOptions.showAdjustmentInSums,
                );
                if (this.isItemHasNonZeroValue(subFliValues.closingValue, subFliValues.openingValue, printOptions.displayOptions.hidePreviousYear)) {
                    return true;
                }
            }
        }

        return false;
    }

    private isFliHasNoChild(
        item: IAccountMappingTemplateGroupItemFLI | IAccountMappingTemplateGroupItemSubtotal,
    ): boolean {
        if (item.type === ItemType.FLI) {
            return item.accounts.length === 0;
        }
        return true;
    }

    private isEachFliAccountZeroValue(item: IAccountMappingTemplateGroupItemFLI, hidePreviousYear = false): boolean {
        for (const account of item.accounts) {
            const isCurrentValueZero = account.calculatedClosingBalance == null || NumberUtils.isZero(account.calculatedClosingBalance);
            const isPreviousValueZero = hidePreviousYear || account.calculatedOpeningBalance == null || NumberUtils.isZero(account.calculatedOpeningBalance);

            if (!isCurrentValueZero || !isPreviousValueZero) {
                return false;
            }
        }
        return true;
    }
}
